import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
  Autocomplete,
  AutocompleteItem,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Chip,
  Checkbox,
  Card,
  CardBody,
  Divider,
  Spinner,
  Tooltip,
  Switch,
  Tabs,
  Tab,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useDisclosure,
} from '@nextui-org/react';
import {
  Settlement,
  SettlementDetails,
  ShipTrip,
  Market,
  Ship,
  MarketComment,
  SettlementDetailRevenue,
  ExporterReceiver,
} from 'common/src/utils/types';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  updateSettlement,
  updateSettlementDetail,
  updateSettlementDetailFieldAndFetch,
  createSettlementDetail,
  deleteSettlementDetail,
  checkSettlementExists,
  deleteSettlement,
  deleteSettlementAudited,
  getClaimsBlockingSettlementDeletion,
  setSettlementReceiverForLegacyName,
} from 'common/src/api/settlements';
import { toast } from 'react-toastify';
import { hasSettlementKilosMismatch } from 'common/src/utils/settlement-detail-validation';
import {
  getShipTrips,
  findOrCreateAirShipTrip,
} from 'common/src/api/ship_trips';
import { getShips } from 'common/src/api/ships';
import { Icon } from '@iconify/react';
import { getPrimaryMarkets } from 'common/src/api/markets';
import {
  addHistoryComment,
  getSettlementOriginalDocument,
  getSettlementHistory,
  SettlementOriginalDocument,
} from '../../services/historyService';
import useUserStore from '../../../../common/src/stores/useUserStore';
import FullScreenLoading from '../common/FullScreenLoading';
import useDataStore from '../../../../common/src/stores/useDataStore';
import { SEASONS, VALID_FORMATS } from 'common/src/utils/definitions';
import { formatDate } from '../../utils/functions';
import {
  isSeasonDateAligned,
  getSeasonMismatchMessage,
} from '../../utils/seasonUtils';
import TransportTypeSelector from '../common/TransportTypeSelector';
import { Alert, LoadingState, useConfirm } from '../ui';
import { getCurrencies, Currency } from 'common/src/api/currencies';
import { supabase } from 'common/src/supabase';
import {
  listMarketComments,
  getMarketCommentAttachmentSignedUrl,
} from 'common/src/api/market_comments';
import useExporterLabelsStore from '../../store/useExporterLabelsStore';
import useShipmentsStore from '../../store/useShipmentsStore';
import useFruticStore from '../../store/useFruticStore';
import { getRevenuesBySettlementId } from 'common/src/api/settlement-detail-revenues';
import { getReceiversByExporter } from 'common/src/api/exporterReceivers';
import {
  ExtractedSettlementCostLine,
  getSettlementSourceCostEditor,
  OfficialCostCategory,
  ResolvedSettlementCostLine,
  updateStandardSettlementSourceCostLines,
} from 'common/src/api/receiver-costs';
import ReceiverCostClassificationTable from './receiver-cost-classification-table';
import ExporterCostSummary from './exporter-cost-summary';
import CostStandardToggle from './cost-standard-toggle';
import {
  CostStandard,
  useExporterCostView,
} from '../../hooks/useExporterCostView';
import { useReceiverExporterCosts } from '../../hooks/useReceiverExporterCosts';
import { isExporterCostViewSeason } from 'common/src/api/exporter-cost-categories';
import SettlementEditSupportPanel from './settlement-edit-support-panel';
import SettlementSourcePreview from './settlement-source-preview';
import SettlementQuadratureManager from './settlement-quadrature-manager';
import FormatMappingModal from '../frutic-box-mapping/FormatMappingModal';
import { ApplySettlementReconciliationResult } from 'common/src/api/settlement-reconciliation';
import {
  buildSettlementQuadratureProductKey,
  buildShipmentQuadratureProductKey,
  normalizeQuadratureReference,
} from 'common/src/utils/quadrature';

type CopyableTextField =
  | 'caliber'
  | 'color'
  | 'label'
  | 'variety'
  | 'format'
  | 'category';
type CopyableNumericField = 'unit_weight' | 'quantity' | 'price_per_bulto';
type CopyableField = CopyableTextField | CopyableNumericField;
type EditableCostField =
  | 'commission'
  | 'tax'
  | 'freight'
  | 'otros'
  | 'repacking'
  | 'exchange_rate';

type EditSettlementTab =
  | 'general'
  | 'income_costs'
  | 'quadrature'
  | 'history'
  | 'documents';

const NUMERIC_COPY_FIELDS: CopyableNumericField[] = [
  'unit_weight',
  'quantity',
  'price_per_bulto',
];
const ALL_COPY_FIELDS: CopyableField[] = [
  'caliber',
  'color',
  'label',
  'variety',
  'format',
  'category',
  'unit_weight',
  'quantity',
  'price_per_bulto',
];

const COPY_FIELD_LABELS: Record<CopyableField, string> = {
  caliber: 'CALIBRE',
  color: 'COLOR',
  label: 'ETIQUETA',
  variety: 'VARIEDAD',
  format: 'FORMATO',
  category: 'CATEGORIA',
  unit_weight: 'PESO UNITARIO',
  quantity: 'CANTIDAD',
  price_per_bulto: 'PRECIO POR BULTO',
};

import { REVENUE_TYPES, REVENUE_TYPE_LABELS } from '../../utils/revenue-types';
import { SINISTER_RESPONSIBLE_OPTIONS } from 'common/src/utils/sinister';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

interface RevenueEntry {
  revenue_type: string;
  description: string;
  amount_local: number;
  exchange_rate: number;
  distribution: 'byKg' | 'bySales';
  group_id?: string; // present for existing DB entries, absent for new ones
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  settlement: Settlement | null;
  onEdit: (settlement: Settlement) => void;
  onDelete?: () => void;
  onRevenueChange?: () => void;
}

const displayToken = (value: string | number | null | undefined) => {
  const trimmed = (value ?? '').toString().trim();
  return trimmed.length > 0 ? trimmed : 'N/A';
};

const displayUnitWeight = (value: string | number | null | undefined) => {
  const trimmed = (value ?? '').toString().trim();
  return trimmed.length > 0 ? `${trimmed} KG` : 'N/A';
};

const buildSettlementProductLabel = (detail: {
  variety?: string | null;
  caliber?: string | null;
  color?: string | null;
  format?: string | null;
  label?: string | null;
  unit_weight?: number | string | null;
  category?: string | null;
}) =>
  [
    displayToken(detail.variety),
    displayToken(detail.caliber),
    displayToken(detail.color),
    displayToken(detail.format),
    displayToken(detail.label),
    displayUnitWeight(detail.unit_weight),
    displayToken(detail.category),
  ].join('-');

const buildShipmentProductLabel = (box: {
  labeled_variety?: string | null;
  variety?: string | null;
  caliber?: string | null;
  color?: string | null;
  box_format?: string | null;
  box_label?: string | null;
  unit_weight?: number | string | null;
  box_weight?: number | string | null;
  category?: string | null;
}) =>
  [
    displayToken(box.labeled_variety ?? box.variety),
    displayToken(box.caliber),
    displayToken(box.color),
    displayToken(box.box_format),
    displayToken(box.box_label),
    displayUnitWeight(box.unit_weight ?? box.box_weight),
    displayToken(box.category),
  ].join('-');

type SourceCostTotals = Record<OfficialCostCategory, number>;

const normalizeSourceCostCurrency = (
  value: string | null | undefined
): string => {
  const normalized = String(value || '')
    .trim()
    .toUpperCase();
  return normalized === 'CNY' ? 'RMB' : normalized;
};

const calculateSourceCostTotals = (
  lines: ResolvedSettlementCostLine[],
  exchangeRate: number,
  localCurrency: string | null | undefined
): SourceCostTotals => {
  const normalizedLocalCurrency =
    normalizeSourceCostCurrency(localCurrency) || 'USD';
  const safeExchangeRate = exchangeRate > 0 ? exchangeRate : 1;

  return lines.reduce<SourceCostTotals>(
    (totals, line) => {
      const currency = normalizeSourceCostCurrency(line.currency);
      const localAmount =
        currency === 'USD' && normalizedLocalCurrency !== 'USD'
          ? line.amount * safeExchangeRate
          : line.amount;
      const usdAmount =
        currency === 'USD' ? line.amount : line.amount / safeExchangeRate;

      totals[line.officialCategory] +=
        line.officialCategory === 'freight' ? usdAmount : localAmount;
      return totals;
    },
    {
      commission: 0,
      tax: 0,
      freight: 0,
      repacking: 0,
      other_costs: 0,
    }
  );
};

const applySourceCostTotals = (
  currentFormData: Partial<Settlement>,
  fallbackDetails: Settlement['settlement_details'],
  totals: SourceCostTotals
): Partial<Settlement> => {
  const details = [
    ...((currentFormData.settlement_details ||
      fallbackDetails ||
      []) as Settlement['settlement_details']),
  ];
  const exchangeRate = Number(currentFormData.exchange_rate) || 1;
  const totalRevenue = details.reduce(
    (sum, detail) => sum + (detail.local_total_price || 0),
    0
  );
  const totalKilos = details.reduce(
    (sum, detail) => sum + (detail.kilos || 0),
    0
  );

  const updatedDetails = details.map((detail) => {
    const salesShare =
      totalRevenue > 0 ? (detail.local_total_price || 0) / totalRevenue : 0;
    const kilosShare = totalKilos > 0 ? (detail.kilos || 0) / totalKilos : 0;
    const localTotalCommission = totals.commission * salesShare;
    const localTotalTax = totals.tax * salesShare;
    const localOtherCosts = totals.other_costs * kilosShare;
    const localRepackingCosts = totals.repacking * kilosShare;
    const usdFreight = totals.freight * kilosShare;

    return {
      ...detail,
      local_total_commision: localTotalCommission,
      local_total_tax: localTotalTax,
      local_other_costs: localOtherCosts,
      local_repacking_costs: localRepackingCosts,
      usd_freight: usdFreight,
      usd_total_fob:
        ((detail.local_total_price || 0) -
          localTotalTax -
          localTotalCommission -
          localOtherCosts -
          localRepackingCosts) /
          exchangeRate -
        usdFreight,
    };
  });

  const correctRemainder = (
    field:
      | 'local_total_commision'
      | 'local_total_tax'
      | 'local_other_costs'
      | 'local_repacking_costs'
      | 'usd_freight',
    expectedTotal: number
  ) => {
    if (updatedDetails.length === 0) return;
    const currentTotal = updatedDetails.reduce(
      (sum, detail) => sum + (Number(detail[field]) || 0),
      0
    );
    const lastDetail = updatedDetails[updatedDetails.length - 1];
    lastDetail[field] =
      (Number(lastDetail[field]) || 0) + expectedTotal - currentTotal;
  };

  correctRemainder('local_total_commision', totals.commission);
  correctRemainder('local_total_tax', totals.tax);
  correctRemainder('local_other_costs', totals.other_costs);
  correctRemainder('local_repacking_costs', totals.repacking);
  correctRemainder('usd_freight', totals.freight);

  if (updatedDetails.length > 0) {
    const lastDetail = updatedDetails[updatedDetails.length - 1];
    lastDetail.usd_total_fob =
      ((lastDetail.local_total_price || 0) -
        (lastDetail.local_total_tax || 0) -
        (lastDetail.local_total_commision || 0) -
        (lastDetail.local_other_costs || 0) -
        (lastDetail.local_repacking_costs || 0)) /
        exchangeRate -
      (lastDetail.usd_freight || 0);
  }

  return {
    ...currentFormData,
    total_commission_local: totals.commission,
    total_tax_local: totals.tax,
    total_other_costs_local: totals.other_costs,
    total_repacking_costs_local: totals.repacking,
    total_freight_usd: totals.freight,
    settlement_details: updatedDetails,
  };
};

const EditSettlementDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  settlement,
  onEdit,
  onDelete,
  onRevenueChange,
}) => {
  const { userProfile, session } = useUserStore();
  const { species } = useDataStore();
  const { shipments } = useShipmentsStore();
  const { mappedFormats, fetchedMappedAndUnmappedFormats, fetchMappings } =
    useFruticStore();
  const { t } = useTranslation('common');

  const [activeTab, setActiveTab] = useState<EditSettlementTab>('general');
  const confirm = useConfirm();
  const {
    isOpen: isFormatMappingModalOpen,
    onOpen: onFormatMappingModalOpen,
    onClose: onFormatMappingModalClose,
  } = useDisclosure();
  const [formatMappingLoading, setFormatMappingLoading] = useState(false);
  const [formatMappingError, setFormatMappingError] = useState('');
  const formatMappingRequestVersion = useRef(0);

  useEffect(() => {
    onFormatMappingModalClose();
    setFormatMappingLoading(false);
    setFormatMappingError('');
    return () => {
      formatMappingRequestVersion.current += 1;
    };
  }, [
    isOpen,
    settlement?.id,
    settlement?.exporter_id,
    settlement?.season,
    settlement?.species_id,
    onFormatMappingModalClose,
  ]);

  const handleOpenFormatMapping = useCallback(async () => {
    const { userProfile: latestProfile, session: latestSession } =
      useUserStore.getState();
    if (!settlement || !latestProfile || !latestSession) return;
    const requestVersion = ++formatMappingRequestVersion.current;
    onFormatMappingModalOpen();
    setFormatMappingLoading(true);
    setFormatMappingError('');
    try {
      const mappings = await fetchMappings(
        settlement.species_id,
        settlement.season,
        latestSession,
        settlement.reference_number || '',
        { ...latestProfile, exporter_id: settlement.exporter_id }
      );
      if (requestVersion !== formatMappingRequestVersion.current) return;
      if (mappings === undefined)
        throw new Error('No se pudieron cargar los formatos.');
    } catch (error) {
      if (requestVersion !== formatMappingRequestVersion.current) return;
      setFormatMappingError(
        error instanceof Error
          ? error.message
          : 'No se pudieron cargar los formatos.'
      );
    } finally {
      if (requestVersion === formatMappingRequestVersion.current)
        setFormatMappingLoading(false);
    }
  }, [fetchMappings, onFormatMappingModalOpen, settlement]);

  const handleCloseFormatMapping = useCallback(() => {
    formatMappingRequestVersion.current += 1;
    onFormatMappingModalClose();
    setFormatMappingLoading(false);
    setFormatMappingError('');
  }, [onFormatMappingModalClose]);
  const [originalDocument, setOriginalDocument] =
    useState<SettlementOriginalDocument | null>(null);
  const [originalDocumentLoading, setOriginalDocumentLoading] = useState(false);
  const [originalDocumentError, setOriginalDocumentError] = useState('');
  const [isOriginalPreviewOpen, setIsOriginalPreviewOpen] = useState(false);
  const [isSinisterPopoverOpen, setIsSinisterPopoverOpen] = useState(false);
  const [supportTabCounts, setSupportTabCounts] = useState({
    history: 0,
    documents: 0,
  });
  const [formData, setFormData] = useState<Partial<Settlement>>({});
  const [loading, setLoading] = useState(false);
  const [loadingFullScreen, setLoadingFullScreen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [ships, setShips] = useState<Ship[]>([]);
  const [shipTrips, setShipTrips] = useState<ShipTrip[]>([]);
  const [exporterReceivers, setExporterReceivers] = useState<
    ExporterReceiver[]
  >([]);
  const [selectedShipId, setSelectedShipId] = useState<string>('');
  const [selectedShipTripId, setSelectedShipTripId] = useState<string>(
    settlement?.ship_trip_id?.toString() || ''
  );
  const [editingCell, setEditingCell] = useState<{
    detailId: number;
    field: string;
    value: string;
  } | null>(null);

  // Varieties are validated through allowedVarieties + unknownVarieties.
  const [editingCostField, setEditingCostField] = useState<{
    field: EditableCostField;
    value: string;
  } | null>(null);
  const [savingCostField, setSavingCostField] = useState(false);
  const [transportType, setTransportType] = useState<string>('M');
  const [airShipmentData, setAirShipmentData] = useState({
    etd: new Date().toISOString().split('T')[0],
    eta: new Date().toISOString().split('T')[0],
    pod: '',
  });
  const [isAddingNewDetail, setIsAddingNewDetail] = useState(false);
  const [duplicateSourceDetailId, setDuplicateSourceDetailId] = useState<
    number | null
  >(null);
  const [newDetailData, setNewDetailData] = useState<
    Partial<Settlement['settlement_details'][0] & { category: string }>
  >({});
  const [validationMessage, setValidationMessage] = useState<string>('');
  const [canSave, setCanSave] = useState(true);
  const [transportMessage, setTransportMessage] = useState<string>('');
  const [marketComments, setMarketComments] = useState<MarketComment[]>([]);
  const [isLoadingMarketComments, setIsLoadingMarketComments] =
    useState<boolean>(false);
  const [marketCommentsError, setMarketCommentsError] = useState<string>('');
  const [showMarketComments, setShowMarketComments] = useState<boolean>(false);
  const [marketCommentImageUrls, setMarketCommentImageUrls] = useState<
    Record<number, string>
  >({});
  const [unknownLabels, setUnknownLabels] = useState<string[]>([]);
  const [unknownVarieties, setUnknownVarieties] = useState<string[]>([]);
  const [otherRevenues, setOtherRevenues] = useState<RevenueEntry[]>([]);
  const [showWithRevenues, setShowWithRevenues] = useState(false);
  const [showCostColumns, setShowCostColumns] = useState(false);
  const [sourceCostLines, setSourceCostLines] = useState<
    ExtractedSettlementCostLine[]
  >([]);
  const [resolvedSourceCostLines, setResolvedSourceCostLines] = useState<
    ResolvedSettlementCostLine[]
  >([]);
  const [sourceCostLinesLoading, setSourceCostLinesLoading] = useState(false);
  const [sourceCostLinesError, setSourceCostLinesError] = useState('');
  const [sourceCostMappingsReady, setSourceCostMappingsReady] = useState(false);
  const [canEditSourceCostLines, setCanEditSourceCostLines] = useState(false);
  // Multi-shipment cost lines are shared with other settlements: the server
  // re-splits them and recalculates the FM columns on save.
  const [sharedSourceCosts, setSharedSourceCosts] = useState(false);
  const [sourceCostLinesDirty, setSourceCostLinesDirty] = useState(false);
  const [sourceCostEditorCategory, setSourceCostEditorCategory] = useState<
    OfficialCostCategory | 'all'
  >('all');
  const [isSourceCostEditorOpen, setIsSourceCostEditorOpen] = useState(false);
  const canEditCurrentSourceCosts =
    canEditSourceCostLines && !settlement?.is_blocked;
  const [costStandard, setCostStandard] = useState<CostStandard>('fm');
  const receiverExporterCosts = useReceiverExporterCosts(
    settlement?.exporter_id,
    formData.exporter_receiver_id
  );
  // Shared (multi-shipment) costs: show the saved server split.
  const savedExporterCosts = useExporterCostView(
    sharedSourceCosts ? settlement?.exporter_id : null,
    settlement?.id ? [settlement.id] : []
  );
  const exporterCostsAvailable =
    receiverExporterCosts.available &&
    isExporterCostViewSeason(formData.season || settlement?.season);
  const showExporterCosts =
    exporterCostsAvailable && costStandard === 'exporter';
  // Cost-line editor narrowed to one exporter category (null = Sin clasificar).
  const [exporterCostEditorFilter, setExporterCostEditorFilter] = useState<
    number | null | undefined
  >(undefined);
  useEffect(() => {
    if (!isSourceCostEditorOpen) setExporterCostEditorFilter(undefined);
  }, [isSourceCostEditorOpen]);
  const [newRevenue, setNewRevenue] = useState<RevenueEntry | null>(null);
  const [savingRevenue, setSavingRevenue] = useState(false);
  const {
    isOpen: isRevenueModalOpen,
    onOpen: onRevenueModalOpen,
    onClose: onRevenueModalClose,
  } = useDisclosure();
  const marketCommentsCountRef = useRef<number>(0);

  /** Adjust the last detail so that sum(details[field]) === headerTotal */
  const applyRemainderCorrection = (
    details: any[],
    field: string,
    headerTotal: number
  ) => {
    if (details.length === 0) return;
    const sum = details.reduce(
      (acc: number, d: any) => acc + (d[field] || 0),
      0
    );
    const remainder = headerTotal - sum;
    if (Math.abs(remainder) > 0.000001) {
      details[details.length - 1][field] =
        (details[details.length - 1][field] || 0) + remainder;
    }
  };

  /** Recalculate usd_total_fob on a detail after cost corrections */
  const recalcFob = (detail: any, exchangeRate: number) => {
    detail.usd_total_fob =
      ((detail.local_total_price || 0) -
        (detail.local_total_tax || 0) -
        (detail.local_total_commision || 0) -
        (detail.local_other_costs || 0) -
        (detail.local_repacking_costs || 0)) /
        (exchangeRate || 1) -
      (detail.usd_freight || 0);
  };

  const buildCostAdjustedFormData = (
    currentFormData: Partial<Settlement>,
    field: EditableCostField,
    value: number
  ): Partial<Settlement> | null => {
    const details = [
      ...((currentFormData.settlement_details ||
        settlement?.settlement_details ||
        []) as Settlement['settlement_details']),
    ];

    if (details.length === 0) return currentFormData;

    const totalRevenue = details.reduce(
      (acc, detail) => acc + (detail.local_total_price || 0),
      0
    );
    const totalKilos = details.reduce(
      (acc, detail) => acc + (detail.kilos || 0),
      0
    );
    const exchangeRate =
      field === 'exchange_rate' ? value : currentFormData.exchange_rate || 1;

    if (field === 'exchange_rate' && value <= 0) return null;

    const commissionRate =
      field === 'commission' && totalRevenue > 0 ? value / totalRevenue : 0;
    const taxRate =
      field === 'tax' && totalRevenue > 0 ? value / totalRevenue : 0;
    const otherCostsPerKilo =
      field === 'otros' && totalKilos > 0 ? value / totalKilos : 0;
    const repackingCostsPerKilo =
      field === 'repacking' && totalKilos > 0 ? value / totalKilos : 0;
    const freightPerKilo =
      field === 'freight' && totalKilos > 0 ? value / totalKilos : 0;

    const updatedDetails = details.map((detail) => {
      const localTotalCommision =
        field === 'commission'
          ? (detail.local_total_price || 0) * commissionRate
          : detail.local_total_commision || 0;
      const localTotalTax =
        field === 'tax'
          ? (detail.local_total_price || 0) * taxRate
          : detail.local_total_tax || 0;
      const localOtherCosts =
        field === 'otros'
          ? (detail.kilos || 0) * otherCostsPerKilo
          : detail.local_other_costs || 0;
      const localRepackingCosts =
        field === 'repacking'
          ? (detail.kilos || 0) * repackingCostsPerKilo
          : detail.local_repacking_costs || 0;
      const usdFreight =
        field === 'freight'
          ? (detail.kilos || 0) * freightPerKilo
          : detail.usd_freight || 0;

      return {
        ...detail,
        local_total_commision: localTotalCommision,
        local_total_tax: localTotalTax,
        local_other_costs: localOtherCosts,
        local_repacking_costs: localRepackingCosts,
        usd_freight: usdFreight,
        exchange_rate: exchangeRate,
        usd_total_fob:
          ((detail.local_total_price || 0) -
            localTotalTax -
            localTotalCommision -
            localOtherCosts -
            localRepackingCosts) /
            (exchangeRate || 1) -
          usdFreight,
      };
    });

    const detailFieldByCostField: Partial<Record<EditableCostField, string>> = {
      commission: 'local_total_commision',
      tax: 'local_total_tax',
      otros: 'local_other_costs',
      repacking: 'local_repacking_costs',
      freight: 'usd_freight',
    };
    const detailField = detailFieldByCostField[field];
    if (detailField) {
      applyRemainderCorrection(updatedDetails, detailField, value);
      recalcFob(updatedDetails[updatedDetails.length - 1], exchangeRate || 1);
    }

    const headerFieldByCostField: Record<EditableCostField, keyof Settlement> =
      {
        commission: 'total_commission_local',
        tax: 'total_tax_local',
        otros: 'total_other_costs_local',
        repacking: 'total_repacking_costs_local',
        freight: 'total_freight_usd',
        exchange_rate: 'exchange_rate',
      };

    return {
      ...currentFormData,
      [headerFieldByCostField[field]]: value,
      settlement_details: updatedDetails,
    };
  };

  const revenueAdjustmentByDetail = useMemo(() => {
    if (otherRevenues.length === 0) return new Map<number, number>();
    const details = (formData.settlement_details ||
      []) as Settlement['settlement_details'];
    const totalKilos = details.reduce((s, d) => s + (d.kilos || 0), 0);
    const totalSales = details.reduce(
      (s, d) => s + (d.local_total_price || 0),
      0
    );
    const map = new Map<number, number>();
    for (const detail of details) {
      let adj = 0;
      for (const rev of otherRevenues) {
        const proportion =
          rev.distribution === 'byKg'
            ? totalKilos > 0
              ? (detail.kilos || 0) / totalKilos
              : 0
            : totalSales > 0
              ? (detail.local_total_price || 0) / totalSales
              : 0;
        adj += rev.amount_local * proportion;
      }
      if (detail.id != null) map.set(detail.id, adj);
    }
    return map;
  }, [otherRevenues, formData.settlement_details]);

  const totalOtherRevenue = useMemo(
    () => otherRevenues.reduce((s, r) => s + r.amount_local, 0),
    [otherRevenues]
  );

  const detailTableTotals = useMemo(
    () =>
      (formData.settlement_details || []).reduce(
        (totals, detail) => ({
          quantity: totals.quantity + (detail.quantity || 0),
          kilos: totals.kilos + (detail.kilos || 0),
          revenue: totals.revenue + (detail.local_total_price || 0),
        }),
        { quantity: 0, kilos: 0, revenue: 0 }
      ),
    [formData.settlement_details]
  );

  const displayedDetailRevenueTotal =
    detailTableTotals.revenue + (showWithRevenues ? totalOtherRevenue : 0);

  const totalOtherRevenueUsd = useMemo(
    () =>
      otherRevenues.reduce(
        (s, r) =>
          s + (r.exchange_rate > 0 ? r.amount_local / r.exchange_rate : 0),
        0
      ),
    [otherRevenues]
  );

  const baseSubtotalLocal = useMemo(
    () =>
      (formData.total_revenue_local || 0) -
      (formData.total_commission_local || 0) -
      (formData.total_tax_local || 0) -
      (formData.total_other_costs_local || 0) -
      (formData.total_repacking_costs_local || 0),
    [
      formData.total_commission_local,
      formData.total_other_costs_local,
      formData.total_repacking_costs_local,
      formData.total_revenue_local,
      formData.total_tax_local,
    ]
  );

  const effectiveExchangeRate = useMemo(() => {
    if (
      !showWithRevenues ||
      totalOtherRevenue === 0 ||
      totalOtherRevenueUsd === 0
    ) {
      return null;
    }

    const baseExchangeRate = formData.exchange_rate || 1;
    const adjustedSubtotalLocal = baseSubtotalLocal + totalOtherRevenue;
    const adjustedSubtotalUsd =
      baseSubtotalLocal / baseExchangeRate + totalOtherRevenueUsd;

    if (adjustedSubtotalUsd === 0) return null;

    const effectiveRate = adjustedSubtotalLocal / adjustedSubtotalUsd;
    return Math.abs(effectiveRate - baseExchangeRate) > 0.0001
      ? effectiveRate
      : null;
  }, [
    baseSubtotalLocal,
    formData.exchange_rate,
    showWithRevenues,
    totalOtherRevenue,
    totalOtherRevenueUsd,
  ]);

  const baseFobUsd =
    baseSubtotalLocal / (formData.exchange_rate || 1) -
    (formData.total_freight_usd || 0);
  const displayedFobUsd =
    baseFobUsd +
    (showWithRevenues && totalOtherRevenueUsd !== 0 ? totalOtherRevenueUsd : 0);
  const hasSinisterChanges = Boolean(
    settlement &&
      (!!settlement.is_sinister !== !!formData.is_sinister ||
        (settlement.sinister_responsible || '') !==
          (formData.sinister_responsible || '') ||
        (settlement.sinister_delay_days ?? null) !==
          (formData.sinister_delay_days ?? null) ||
        (settlement.sinister_case_number || '') !==
          (formData.sinister_case_number || ''))
  );
  const sinisterSummary = formData.is_sinister
    ? [
        formData.sinister_responsible || 'Responsable pendiente',
        formData.sinister_delay_days != null
          ? `${formData.sinister_delay_days} días de retraso`
          : 'Sin retraso informado',
        formData.sinister_case_number
          ? `Caso ${formData.sinister_case_number}`
          : 'Sin ID de caso',
      ].join(' · ')
    : 'Esta liquidación no está marcada como siniestro';

  const loadRevenues = useCallback(async () => {
    if (!settlement?.id) return;
    const revenues = await getRevenuesBySettlementId(settlement.id);
    if (revenues && revenues.length > 0) {
      const map = new Map<string, RevenueEntry & { _amount_usd: number }>();
      for (const rev of revenues) {
        const key =
          rev.group_id || `${rev.revenue_type}||${rev.description || ''}`;
        const existing = map.get(key);
        if (existing) {
          existing.amount_local += rev.amount_local;
          existing._amount_usd += rev.amount_usd || 0;
        } else {
          map.set(key, {
            revenue_type: rev.revenue_type,
            description: rev.description || '',
            amount_local: rev.amount_local,
            _amount_usd: rev.amount_usd || 0,
            exchange_rate: 1,
            distribution: (rev.distribution as 'byKg' | 'bySales') || 'bySales',
            group_id: rev.group_id || undefined,
          });
        }
      }
      // Derive exchange_rate from the totals
      for (const entry of map.values()) {
        entry.exchange_rate =
          entry._amount_usd > 0
            ? entry.amount_local / entry._amount_usd
            : formData.exchange_rate || 1;
      }
      setOtherRevenues(
        Array.from(map.values()).map(({ _amount_usd, ...rest }) => rest)
      );
      setShowWithRevenues(true);
    } else {
      setOtherRevenues([]);
      setShowWithRevenues(false);
    }
  }, [settlement?.id]);

  const updateSettlementTotal = useCallback(
    async (revenues: RevenueEntry[]) => {
      if (!settlement?.id) return;
      const total = revenues.reduce((s, r) => s + r.amount_local, 0);
      await supabase
        .from('settlements')
        .update({ total_other_revenue_local: total })
        .eq('id', settlement.id);
    },
    [settlement?.id]
  );

  const handleConfirmNewRevenue = useCallback(async () => {
    if (!newRevenue || !settlement?.id || newRevenue.amount_local === 0) return;
    setSavingRevenue(true);
    try {
      const details =
        (formData.settlement_details as Settlement['settlement_details']) || [];
      if (details.length === 0) return;

      const totalKilos = details.reduce((sum, d) => sum + (d.kilos || 0), 0);
      const totalSales = details.reduce(
        (sum, d) => sum + (d.local_total_price || 0),
        0
      );
      const groupId = crypto.randomUUID();
      const revenueInserts: Omit<
        SettlementDetailRevenue,
        'id' | 'created_at'
      >[] = [];

      for (const detail of details) {
        const proportion =
          newRevenue.distribution === 'byKg'
            ? totalKilos > 0
              ? (detail.kilos || 0) / totalKilos
              : 0
            : totalSales > 0
              ? (detail.local_total_price || 0) / totalSales
              : 0;
        revenueInserts.push({
          settlement_detail_id: detail.id,
          revenue_type: newRevenue.revenue_type,
          description: newRevenue.description || undefined,
          amount_local: newRevenue.amount_local * proportion,
          amount_usd:
            (newRevenue.exchange_rate || 0) > 0
              ? (newRevenue.amount_local * proportion) /
                newRevenue.exchange_rate
              : 0,
          group_id: groupId,
          distribution: newRevenue.distribution,
          created_by: userProfile?.id,
        });
      }

      await supabase.from('settlement_detail_revenues').insert(revenueInserts);
      await loadRevenues();
      const updatedRevenues = [
        ...otherRevenues,
        { ...newRevenue, group_id: groupId },
      ];
      await updateSettlementTotal(updatedRevenues);

      // Track in history
      const revenueTypeLabel =
        REVENUE_TYPE_LABELS[newRevenue.revenue_type] || newRevenue.revenue_type;
      const actionMessage = `${userProfile?.first_name} ${userProfile?.last_name} ha agregado un ingreso adicional: ${revenueTypeLabel}${newRevenue.description ? ` (${newRevenue.description})` : ''} por ${newRevenue.amount_local} ${formData.local_currency || ''} (distribución: ${newRevenue.distribution === 'byKg' ? 'por Kg' : 'por Ventas'})`;
      addHistoryComment(
        settlement.id,
        actionMessage,
        userProfile?.id,
        'action'
      );

      setNewRevenue(null);
      toast.success('Ingreso adicional guardado');
      onRevenueChange?.();
    } catch (error) {
      console.error('Error saving revenue:', error);
      toast.error('Error al guardar ingreso adicional');
    } finally {
      setSavingRevenue(false);
    }
  }, [
    newRevenue,
    settlement?.id,
    formData.settlement_details,
    formData.exchange_rate,
    formData.local_currency,
    otherRevenues,
    userProfile,
    loadRevenues,
    updateSettlementTotal,
  ]);

  const handleDeleteRevenue = useCallback(
    async (groupId: string) => {
      if (!settlement?.id) return;
      setSavingRevenue(true);
      try {
        // Capture revenue info before deleting for history
        const deletedRevenue = otherRevenues.find(
          (r) => r.group_id === groupId
        );

        await supabase
          .from('settlement_detail_revenues')
          .delete()
          .eq('group_id', groupId);
        await loadRevenues();
        const updatedRevenues = otherRevenues.filter(
          (r) => r.group_id !== groupId
        );
        await updateSettlementTotal(updatedRevenues);

        // Track in history
        if (deletedRevenue) {
          const revenueTypeLabel =
            REVENUE_TYPE_LABELS[deletedRevenue.revenue_type] ||
            deletedRevenue.revenue_type;
          const actionMessage = `${userProfile?.first_name} ${userProfile?.last_name} ha eliminado un ingreso adicional: ${revenueTypeLabel}${deletedRevenue.description ? ` (${deletedRevenue.description})` : ''} por ${deletedRevenue.amount_local} ${formData.local_currency || ''}`;
          addHistoryComment(
            settlement.id,
            actionMessage,
            userProfile?.id,
            'action'
          );
        }

        toast.success('Ingreso adicional eliminado');
        onRevenueChange?.();
      } catch (error) {
        console.error('Error deleting revenue:', error);
        toast.error('Error al eliminar ingreso adicional');
      } finally {
        setSavingRevenue(false);
      }
    },
    [
      settlement?.id,
      otherRevenues,
      loadRevenues,
      updateSettlementTotal,
      userProfile,
      formData.local_currency,
    ]
  );

  const mappedShipments = useMemo(() => {
    if (!shipments.length) return [];

    if (!fetchedMappedAndUnmappedFormats) {
      return shipments.map((box) => ({
        ...box,
        unit_weight: box.unit_weight ?? box.box_weight,
      }));
    }

    return shipments.map((box) => {
      const mapping = mappedFormats.find(
        (m) =>
          Number(m.exporter_id) === Number(box.exporter_id) &&
          m.frutic_code.trim().toUpperCase() ===
            box.box_type?.trim().toUpperCase()
      );
      const box_format = mapping?.cherry_box_format || box.box_format || 'NA';
      const unit_weight = box.unit_weight ?? box.box_weight;

      return {
        ...box,
        box_format,
        unit_weight,
      };
    });
  }, [shipments, mappedFormats, fetchedMappedAndUnmappedFormats]);

  // Create a stable key for settlement details to trigger recalculation
  // This ensures the cuadratura updates whenever any detail quantity changes
  const settlementDetailsKey = useMemo(() => {
    const details = formData.settlement_details || [];
    return details
      .map(
        (d) =>
          `${d.id}:${d.quantity}:${d.variety}:${d.caliber}:${d.color}:${d.format}:${d.label}:${d.unit_weight}:${d.category}`
      )
      .join('|');
  }, [formData.settlement_details]);

  // Use formData.settlement_details to ensure cuadratura updates when editing
  const quadratureRows = useMemo(() => {
    if (!settlement || !settlement.reference_number) return [];

    // Use formData.settlement_details (edited values) instead of settlement.settlement_details (original)
    const settlementDetails = formData.settlement_details || [];
    if (!settlementDetails.length) return [];

    const reference = normalizeQuadratureReference(settlement.reference_number);
    const quadratureContext = {
      season: settlement.season,
      speciesId: settlement.species_id,
    };
    const shipmentBoxes = mappedShipments.filter(
      (box) => normalizeQuadratureReference(box.reference_number) === reference
    );

    const settlementTotals = new Map<
      string,
      { product: string; quantity: number }
    >();
    settlementDetails.forEach((detail) => {
      const key = buildSettlementQuadratureProductKey(
        detail,
        quadratureContext
      );
      const current = settlementTotals.get(key);
      const quantity = (detail.quantity || 0) + (current?.quantity || 0);
      settlementTotals.set(key, {
        product: buildSettlementProductLabel(detail),
        quantity,
      });
    });

    const shipmentTotals = new Map<
      string,
      { product: string; quantity: number }
    >();
    shipmentBoxes.forEach((box) => {
      const key = buildShipmentQuadratureProductKey(box, quadratureContext);
      const current = shipmentTotals.get(key);
      const quantity = (box.quantity || 0) + (current?.quantity || 0);
      shipmentTotals.set(key, {
        product: buildShipmentProductLabel(box),
        quantity,
      });
    });

    const keys = new Set([
      ...Array.from(settlementTotals.keys()),
      ...Array.from(shipmentTotals.keys()),
    ]);

    const areQuantitiesEqual = (a: number, b: number) =>
      Math.abs(a - b) < 0.0001;

    const rows = Array.from(keys).map((key) => {
      const settled = settlementTotals.get(key);
      const shipped = shipmentTotals.get(key);
      const settledQuantity = settled?.quantity || 0;
      const shippedQuantity = shipped?.quantity || 0;

      return {
        key,
        product: settled?.product || shipped?.product || key,
        settled: settledQuantity,
        shipped: shippedQuantity,
        isMatched: areQuantitiesEqual(settledQuantity, shippedQuantity),
      };
    });

    rows.sort((a, b) => {
      if (a.isMatched !== b.isMatched) {
        return a.isMatched ? -1 : 1;
      }
      return a.product.localeCompare(b.product);
    });

    return rows;
  }, [
    settlement,
    mappedShipments,
    settlementDetailsKey,
    formData.settlement_details,
  ]);

  const [hasPendingQuadratureChanges, setHasPendingQuadratureChanges] =
    useState(false);
  const hasQuadratureData = quadratureRows.length > 0;
  const isQuadratureMatched =
    hasQuadratureData && quadratureRows.every((row) => row.isMatched);
  const canFetchMarketComments = Boolean(
    (formData.reference_number || formData.container_number) &&
      formData.species_id &&
      formData.season
  );
  const {
    labels: officialLabels,
    fetchLabels,
    loading: isLoadingLabels,
  } = useExporterLabelsStore();

  const normalizeMatchValue = (value: string | null | undefined) =>
    (value || '')
      .trim()
      .toLowerCase()
      .replace(/[-/\s]+/g, '');

  const normalizeReceiverName = (value: string | null | undefined) =>
    (value || '').trim().toUpperCase();

  const normalizeLabel = (value: string | null | undefined) =>
    (value || '').trim().toUpperCase();

  const normalizeVariety = (value: string | null | undefined) =>
    (value || '').trim().toUpperCase();

  const buildContainerSearchValue = (value: string | null | undefined) => {
    if (!value) return undefined;
    return value.trim().replace(/[-/\s]+/g, '%');
  };

  const formatMarketValue = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
  };

  const refreshOriginalDocument = useCallback(async () => {
    if (!settlement?.id) return;
    setOriginalDocumentLoading(true);
    setOriginalDocumentError('');
    try {
      const document = await getSettlementOriginalDocument(settlement.id);
      setOriginalDocument(document);
      setIsOriginalPreviewOpen(false);
    } catch (error) {
      console.error('Error loading original settlement document:', error);
      setOriginalDocument(null);
      setIsOriginalPreviewOpen(false);
      setOriginalDocumentError('No se pudo cargar el documento original.');
    } finally {
      setOriginalDocumentLoading(false);
    }
  }, [settlement?.id]);

  const refreshSupportTabCounts = useCallback(async () => {
    if (!settlement?.id) {
      setSupportTabCounts({ history: 0, documents: 0 });
      return;
    }

    try {
      const historyItems = await getSettlementHistory(settlement.id);
      setSupportTabCounts({
        history: historyItems.filter(
          (item) => item.type === 'comment' || item.type === 'action'
        ).length,
        documents: historyItems.filter((item) => item.type === 'document')
          .length,
      });
    } catch (error) {
      console.error('Error loading settlement tab counts:', error);
    }
  }, [settlement?.id]);

  const handleQuadratureApplied = useCallback(
    async (result: ApplySettlementReconciliationResult) => {
      if (!settlement) return;
      const updatedSettlement: Settlement = {
        ...settlement,
        settlement_details: result.details,
      };
      setFormData((current) => ({
        ...current,
        settlement_details: result.details,
      }));
      onEdit(updatedSettlement);
      await loadRevenues();
      void refreshSupportTabCounts();
    },
    [loadRevenues, onEdit, refreshSupportTabCounts, settlement]
  );

  useEffect(() => {
    if (!isOpen || !settlement?.id) {
      setOriginalDocument(null);
      setIsOriginalPreviewOpen(false);
      setIsSinisterPopoverOpen(false);
      setOriginalDocumentError('');
      return;
    }

    setActiveTab('general');
    setIsSinisterPopoverOpen(false);
    void refreshOriginalDocument();
  }, [isOpen, refreshOriginalDocument, settlement?.id]);

  useEffect(() => {
    if (!isOpen || !settlement?.id) {
      setSupportTabCounts({ history: 0, documents: 0 });
      return;
    }

    void refreshSupportTabCounts();
  }, [isOpen, refreshSupportTabCounts, settlement?.id]);

  useEffect(() => {
    if (
      !isOpen ||
      !userProfile?.exporter_id ||
      !formData.season ||
      !formData.species_id
    ) {
      return;
    }

    fetchLabels(
      userProfile.exporter_id,
      formData.species_id,
      formData.season
    ).catch(() => undefined);
  }, [
    fetchLabels,
    formData.season,
    formData.species_id,
    isOpen,
    userProfile?.exporter_id,
  ]);

  const allowedLabels = useMemo(() => {
    if (!userProfile?.exporter_id || !formData.season || !formData.species_id) {
      return [];
    }
    const normalized = Array.from(
      new Set(
        officialLabels
          .map((item) => normalizeLabel(item.label))
          .filter((label) => label.length > 0)
      )
    );
    return normalized.sort((a, b) => a.localeCompare(b));
  }, [
    formData.season,
    formData.species_id,
    officialLabels,
    userProfile?.exporter_id,
  ]);

  const allowedVarieties = useMemo(() => {
    const selectedSpecies = species.find(
      (item) => item.id === formData.species_id
    );
    const normalized = Array.from(
      new Set(
        (selectedSpecies?.all_varieties || [])
          .map((item) => normalizeVariety(item))
          .filter((variety) => variety.length > 0)
      )
    );
    return normalized.sort((a, b) => a.localeCompare(b));
  }, [formData.species_id, species]);

  const selectedExporterReceiver = useMemo(
    () =>
      exporterReceivers.find(
        (receiver) => receiver.id === formData.exporter_receiver_id
      ) || null,
    [exporterReceivers, formData.exporter_receiver_id]
  );

  useEffect(() => {
    if (
      formData.exporter_receiver_id ||
      !formData.receiver ||
      exporterReceivers.length === 0
    ) {
      return;
    }

    const receiver = exporterReceivers.find(
      (item) =>
        normalizeReceiverName(item.name) ===
        normalizeReceiverName(formData.receiver)
    );

    if (!receiver) return;

    setFormData((prev) => ({
      ...prev,
      exporter_receiver_id: receiver.id,
      receiver: receiver.name,
      legacy_receiver: prev.legacy_receiver || prev.receiver,
    }));
  }, [exporterReceivers, formData.exporter_receiver_id, formData.receiver]);

  useEffect(() => {
    if (allowedLabels.length === 0) {
      setUnknownLabels([]);
      return;
    }

    const allowedSet = new Set(allowedLabels);
    const unknown = Array.from(
      new Set(
        (formData.settlement_details || [])
          .map((detail) => normalizeLabel(detail.label))
          .filter((label) => label.length > 0 && label !== 'N/A')
          .filter((label) => !allowedSet.has(label))
      )
    ).sort((a, b) => a.localeCompare(b));

    setUnknownLabels(unknown);
  }, [allowedLabels, formData.settlement_details]);

  useEffect(() => {
    if (allowedVarieties.length === 0) {
      setUnknownVarieties([]);
      return;
    }

    const allowedSet = new Set(allowedVarieties);
    const unknown = Array.from(
      new Set(
        (formData.settlement_details || [])
          .map((detail) => normalizeVariety(detail.variety))
          .filter((variety) => variety.length > 0 && variety !== 'N/A')
          .filter((variety) => !allowedSet.has(variety))
      )
    ).sort((a, b) => a.localeCompare(b));

    setUnknownVarieties(unknown);
  }, [allowedVarieties, formData.settlement_details]);

  // Copy functionality state
  const [copyMode, setCopyMode] = useState<{
    field: CopyableField;
    value: string;
  } | null>(null);
  const [selectedRowsForCopy, setSelectedRowsForCopy] = useState<Set<number>>(
    new Set()
  );

  // Track original values to detect changes that require market comparison recalculation
  const [originalDateSold, setOriginalDateSold] = useState<string>('');
  const [originalComparableMarketId, setOriginalComparableMarketId] =
    useState<string>('');
  const [originalLocalCurrency, setOriginalLocalCurrency] =
    useState<string>('');

  // Track original transport info
  const [originalShipTripId, setOriginalShipTripId] = useState<string>('');
  const [originalTransportType, setOriginalTransportType] =
    useState<string>('M');

  // Business type options (previously used by this exporter)
  const [businessTypeOptions, setBusinessTypeOptions] = useState<string[]>([]);

  useEffect(() => {
    const fetchBusinessTypes = async () => {
      try {
        if (!userProfile?.exporter_id) return;
        const { data, error } = await supabase
          .from('settlements')
          .select('business_type')
          .eq('exporter_id', userProfile.exporter_id)
          .not('business_type', 'is', null)
          .limit(1000);

        if (error) {
          console.error('Error fetching business types:', error);
          return;
        }

        const unique = Array.from(
          new Set(
            (data || [])
              .map(
                (r: { business_type: string | null }) => r.business_type || ''
              )
              .filter((v) => v && v.trim().length > 0)
              .map((v) => v.toUpperCase())
          )
        ).sort();
        setBusinessTypeOptions(unique);
      } catch (e) {
        console.error('Unexpected error fetching business types:', e);
      }
    };

    if (isOpen) fetchBusinessTypes();
  }, [isOpen, userProfile?.exporter_id]);

  // Helper functions for season validation
  const isSeasonDateAlignedLocal = (): boolean => {
    if (!formData.date_sold || !formData.season) return true;
    return isSeasonDateAligned(formData.date_sold, formData.season);
  };

  const getSeasonMismatchMessageLocal = (): string => {
    if (!formData.date_sold || !formData.season) return '';
    return getSeasonMismatchMessage(formData.date_sold, formData.season);
  };

  const labelStatus = () => {
    if (isLoadingLabels) {
      return {
        text: 'Etiquetas: verificando...',
        className: 'bg-default-100 text-default-500',
      };
    }
    if (allowedLabels.length === 0) {
      return {
        text: 'Etiquetas: sin base para validar',
        className: 'bg-default-100 text-default-500',
      };
    }
    if (unknownLabels.length > 0) {
      return {
        text: `Etiquetas: ${unknownLabels.length} no oficiales`,
        className: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
      };
    }
    return {
      text: 'Etiquetas: cumplen con etiquetas oficiales',
      className:
        'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
    };
  };

  const labelStatusInfo = labelStatus();
  const isLabelUnknown = (value: string | null | undefined) => {
    const normalized = normalizeLabel(value);
    if (!normalized || normalized === 'N/A') return false;
    return unknownLabels.includes(normalized);
  };

  const varietyStatus = () => {
    if (allowedVarieties.length === 0) {
      return {
        text: 'Variedades: sin base para validar',
        className: 'bg-default-100 text-default-500',
      };
    }
    if (unknownVarieties.length > 0) {
      return {
        text: `Variedades: ${unknownVarieties.length} no oficiales`,
        className: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
      };
    }
    return {
      text: 'Variedades: cumplen con variedades oficiales',
      className:
        'bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300',
    };
  };

  const varietyStatusInfo = varietyStatus();
  const isVarietyUnknown = (value: string | null | undefined) => {
    const normalized = normalizeVariety(value);
    if (!normalized || normalized === 'N/A') return false;
    return unknownVarieties.includes(normalized);
  };

  const detailValuesStatus = () => {
    const details = (formData.settlement_details ||
      settlement?.settlement_details ||
      []) as any[];
    const zeroQuantity = details.filter(
      (d) => !d.quantity || d.quantity == 0
    ).length;
    const zeroWeight = details.filter(
      (d) => !d.unit_weight || d.unit_weight == 0
    ).length;
    const kilosMismatch = details.filter(hasSettlementKilosMismatch).length;
    const total = zeroQuantity + zeroWeight + kilosMismatch;
    if (total > 0) {
      const parts: string[] = [];
      if (zeroQuantity > 0) parts.push(`${zeroQuantity} con cantidad 0`);
      if (zeroWeight > 0) parts.push(`${zeroWeight} con peso bulto 0`);
      if (kilosMismatch > 0) {
        parts.push(
          `${kilosMismatch} con kilos distintos a peso bulto × bultos`
        );
      }
      return {
        text: `Detalles: ${parts.join(', ')}`,
        className: 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300',
      };
    }
    return null;
  };

  const detailValuesStatusInfo = detailValuesStatus();

  useEffect(() => {
    if (settlement) {
      setFormData({
        reference_number: settlement.reference_number,
        exporter_receiver_id: settlement.exporter_receiver_id || null,
        receiver: settlement.receiver,
        legacy_receiver: settlement.legacy_receiver || settlement.receiver,
        business_type: settlement.business_type || '',
        sale_order: settlement.sale_order || '',
        container_number: settlement.container_number,
        date_sold: settlement.date_sold,
        market_id: settlement.market_id,
        comparable_market_id: settlement.comparable_market_id,
        region: settlement.region,
        total_revenue_local: settlement.total_revenue_local,
        total_tax_local: settlement.total_tax_local,
        total_commission_local: settlement.total_commission_local,
        total_other_costs_local: settlement.total_other_costs_local,
        total_repacking_costs_local:
          settlement.total_repacking_costs_local || 0,
        total_freight_usd: settlement.total_freight_usd,
        exchange_rate: settlement.exchange_rate,
        ship_trip_id: settlement.ship_trip_id,
        local_currency: settlement.local_currency,
        settlement_details: [...settlement.settlement_details],
        original_incoterm: settlement.original_incoterm,
        species_id: settlement.species_id,
        season: settlement.season,
        is_sinister: settlement.is_sinister,
        sinister_responsible: settlement.sinister_responsible || null,
        sinister_delay_days: settlement.sinister_delay_days || null,
        sinister_case_number: settlement.sinister_case_number || null,
      });

      // Store original values for comparison
      setOriginalDateSold(settlement.date_sold || '');
      setOriginalComparableMarketId(settlement.comparable_market_id || '');
      setOriginalLocalCurrency(settlement.local_currency || '');
      setOriginalShipTripId(settlement.ship_trip_id?.toString() || '');

      if (settlement.ship_trip_id) {
        setSelectedShipTripId(settlement.ship_trip_id.toString());
      }

      // Set default POD for air shipment
      setAirShipmentData((prev) => ({
        ...prev,
        pod: settlement.market_id?.toString() || '',
      }));
    }
  }, [settlement]);

  useEffect(() => {
    let cancelled = false;
    setSourceCostLines([]);
    setResolvedSourceCostLines([]);
    setSourceCostLinesError('');
    setSourceCostMappingsReady(false);
    setCanEditSourceCostLines(false);
    setSharedSourceCosts(false);
    setSourceCostLinesDirty(false);
    setSourceCostEditorCategory('all');
    setIsSourceCostEditorOpen(false);

    if (!isOpen || !settlement?.id) return;

    setSourceCostLinesLoading(true);
    getSettlementSourceCostEditor(Number(settlement.id))
      .then((editor) => {
        if (!cancelled) {
          setSourceCostLines(editor.costLines);
          setCanEditSourceCostLines(editor.canEditOriginalCosts);
          setSharedSourceCosts(editor.sourceFlow === 'multi_shipment');
        }
      })
      .catch((sourceCostError) => {
        if (!cancelled) {
          console.error(
            'Error loading settlement source costs:',
            sourceCostError
          );
          setSourceCostLinesError(
            'No se pudieron cargar los costos originales de esta liquidación.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setSourceCostLinesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, settlement?.id]);

  const handleSourceCostLinesChange = useCallback(
    (lines: ExtractedSettlementCostLine[]) => {
      setSourceCostLines(lines);
      setSourceCostLinesDirty(true);
    },
    []
  );

  const handleSourceCostResolution = useCallback(
    (lines: ResolvedSettlementCostLine[]) => {
      setResolvedSourceCostLines(lines);
    },
    []
  );

  useEffect(() => {
    if (!canEditCurrentSourceCosts || !sourceCostLinesDirty) return;
    if (resolvedSourceCostLines.length !== sourceCostLines.length) return;

    setFormData((current) =>
      applySourceCostTotals(
        current,
        settlement?.settlement_details || [],
        calculateSourceCostTotals(
          resolvedSourceCostLines,
          Number(current.exchange_rate) || 1,
          current.local_currency
        )
      )
    );
  }, [
    canEditCurrentSourceCosts,
    formData.exchange_rate,
    formData.local_currency,
    resolvedSourceCostLines,
    settlement?.settlement_details,
    sourceCostLines.length,
    sourceCostLinesDirty,
  ]);

  // Load existing other revenues when settlement loads
  useEffect(() => {
    if (!settlement?.id) {
      setOtherRevenues([]);
      setShowWithRevenues(false);
      return;
    }

    setOtherRevenues([]);
    setShowWithRevenues(false);
    void loadRevenues();
  }, [settlement?.id, loadRevenues]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!canFetchMarketComments) {
        setMarketComments([]);
        setMarketCommentsError('');
        return;
      }
      setIsLoadingMarketComments(true);
      setMarketCommentsError('');
      try {
        const baseParams = {
          species_id: formData.species_id as number,
          season: formData.season as string,
          limit: 50,
        };
        const reference = normalizeMatchValue(
          formData.reference_number as string
        );
        const container = normalizeMatchValue(
          formData.container_number as string
        );

        let data: MarketComment[] = [];
        if (reference && container) {
          const [byRef, byContainer] = await Promise.all([
            listMarketComments({
              ...baseParams,
              exporter_ref: formData.reference_number || undefined,
            }),
            listMarketComments({
              ...baseParams,
              container_number:
                buildContainerSearchValue(formData.container_number) ||
                undefined,
            }),
          ]);
          const merged = new Map<number, MarketComment>();
          [...byRef, ...byContainer].forEach((comment) => {
            merged.set(comment.id, comment);
          });
          data = Array.from(merged.values());
        } else {
          data = await listMarketComments({
            ...baseParams,
            exporter_ref: formData.reference_number || undefined,
            container_number:
              buildContainerSearchValue(formData.container_number) || undefined,
          });
        }
        if (!cancelled) setMarketComments(data || []);
      } catch {
        if (!cancelled) {
          setMarketCommentsError(
            'No se pudieron cargar los comentarios de mercado.'
          );
        }
      } finally {
        if (!cancelled) setIsLoadingMarketComments(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    canFetchMarketComments,
    formData.reference_number,
    formData.container_number,
    formData.species_id,
    formData.season,
  ]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const attachments = marketComments.flatMap(
        (comment) => comment.attachments || []
      );
      const pending = attachments.filter(
        (attachment) =>
          attachment.is_image &&
          !attachment.deleted_at &&
          !marketCommentImageUrls[attachment.id]
      );

      if (pending.length === 0) return;

      try {
        const entries = await Promise.all(
          pending.map(async (attachment) => {
            const url = await getMarketCommentAttachmentSignedUrl(
              attachment.bucket,
              attachment.path,
              300
            );
            return [attachment.id, url] as const;
          })
        );
        if (!cancelled) {
          setMarketCommentImageUrls((prev) => {
            const next = { ...prev };
            entries.forEach(([id, url]) => {
              next[id] = url;
            });
            return next;
          });
        }
      } catch {
        // Non-blocking: attachments can still be opened on demand.
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [marketComments, marketCommentImageUrls]);

  useEffect(() => {
    if (marketComments.length === 0) {
      setShowMarketComments(false);
    } else if (marketCommentsCountRef.current === 0) {
      setShowMarketComments(true);
    }
    marketCommentsCountRef.current = marketComments.length;
  }, [marketComments.length]);

  useEffect(() => {
    const fetchAndSetData = async () => {
      const [trips, shipsData, marketsData, currenciesData, receiversData] =
        await Promise.all([
          getShipTrips(),
          getShips(),
          getPrimaryMarkets(),
          getCurrencies(),
          userProfile?.exporter_id
            ? getReceiversByExporter(userProfile.exporter_id)
            : Promise.resolve([]),
        ]);

      setShipTrips(trips || []);
      setShips(shipsData || []);
      setMarkets(marketsData || []);
      setCurrencies(currenciesData || []);
      setExporterReceivers(receiversData || []);

      if (settlement && settlement.ship_trip_id) {
        const trip = trips?.find((t) => t.id === settlement.ship_trip_id);
        if (trip) {
          const shipType = trip.ship.type || 'M';
          setTransportType(shipType);
          setOriginalTransportType(shipType);
          if (shipType === 'A' || shipType === 'T') {
            setAirShipmentData({
              etd: trip.etd.split('T')[0],
              eta: trip.eta.split('T')[0],
              pod: trip.market_id?.toString() || '',
            });
          } else {
            setSelectedShipId(trip.ship.id.toString());
          }
        }
      } else if (settlement) {
        setTransportType('M');
        setOriginalTransportType('M');
      }
    };
    fetchAndSetData();
  }, [settlement, userProfile?.exporter_id]);

  // Check settlement existence when season, species, or reference changes
  useEffect(() => {
    if (
      formData.reference_number &&
      formData.season &&
      formData.species_id &&
      settlement
    ) {
      checkSettlementExists(
        formData.reference_number.trim(),
        formData.season,
        formData.species_id,
        userProfile?.exporter_id || 0
      ).then((result) => {
        if (result.exists) {
          // Check if the found settlement is the current one being edited
          if (Number(result.settlementId) === Number(settlement.id)) {
            // It's the same settlement, so no duplicate issue
            setValidationMessage('');
            setCanSave(true);
          } else {
            // It's a different settlement - this is a duplicate
            if (!result.isApproved) {
              setValidationMessage(
                `La referencia ${formData.reference_number} ya existe para esta temporada y especie (en aprobación).No puedes duplicar referencias.`
              );
            } else {
              setValidationMessage(
                `La referencia ${formData.reference_number} ya existe para esta temporada y especie. No puedes duplicar referencias.`
              );
            }

            setCanSave(false);
          }
        } else {
          // No settlement found with these criteria
          setValidationMessage('');
          setCanSave(true);
        }
      });
    } else {
      // Clear message if required fields are empty
      setValidationMessage('');
      setCanSave(true);
    }
  }, [
    formData.reference_number,
    formData.season,
    formData.species_id,
    userProfile?.exporter_id,
    settlement,
  ]);

  const handleSubmit = async () => {
    if (!settlement || !canSave) return;
    setLoading(true);
    try {
      let effectiveFormData: Partial<Settlement> = formData;
      let shipTripId = undefined;

      if (transportType === 'M') {
        if (!selectedShipId) {
          toast.error('Debe seleccionar una nave');
          return;
        }
        if (!selectedShipTripId) {
          toast.error('Debe seleccionar un viaje');
          return;
        }
        shipTripId = selectedShipTripId;
      }

      if (transportType === 'A' || transportType === 'T') {
        const shipTrip = await handleCreateAirShipment();
        if (shipTrip) {
          shipTripId = shipTrip.id;
        }
      }

      if (unknownVarieties.length > 0) {
        toast.error(
          'Hay detalles con variedades fuera del catálogo oficial de la especie. Corrígelas antes de guardar.'
        );
        return;
      }

      if (!effectiveFormData.exporter_receiver_id) {
        toast.error('Selecciona un cliente oficial antes de guardar.');
        return;
      }

      if (sourceCostLinesDirty && canEditCurrentSourceCosts) {
        if (
          !sourceCostMappingsReady ||
          resolvedSourceCostLines.length !== sourceCostLines.length
        ) {
          toast.error(
            'Espera a que terminen de cargar los mapeos de costos originales.'
          );
          return;
        }
        if (
          resolvedSourceCostLines.some(
            (line) =>
              !line.originalName.trim() ||
              !Number.isFinite(line.amount) ||
              line.amount < 0
          )
        ) {
          toast.error('Revisa los nombres y montos de los costos originales.');
          return;
        }

        const localCurrency = normalizeSourceCostCurrency(
          effectiveFormData.local_currency
        );
        const hasUnsupportedCurrency = resolvedSourceCostLines.some((line) => {
          const currency = normalizeSourceCostCurrency(line.currency);
          return currency !== 'USD' && currency !== localCurrency;
        });
        if (hasUnsupportedCurrency) {
          toast.error(
            'Cada costo original debe estar en USD o en la moneda local de la liquidación.'
          );
          return;
        }
        if (
          localCurrency !== 'USD' &&
          resolvedSourceCostLines.length > 0 &&
          (Number(effectiveFormData.exchange_rate) || 0) <= 0
        ) {
          toast.error(
            'Ingresa un tipo de cambio válido para recalcular los costos originales.'
          );
          return;
        }

        effectiveFormData = applySourceCostTotals(
          effectiveFormData,
          settlement.settlement_details || [],
          calculateSourceCostTotals(
            resolvedSourceCostLines,
            Number(effectiveFormData.exchange_rate) || 1,
            effectiveFormData.local_currency
          )
        );
      }

      if (editingCostField) {
        const numericValue = parseFloat(editingCostField.value);
        if (isNaN(numericValue) || numericValue < 0) {
          toast.error(
            'Por favor ingrese un valor numérico válido mayor o igual a 0'
          );
          return;
        }

        const adjustedFormData = buildCostAdjustedFormData(
          effectiveFormData,
          editingCostField.field,
          numericValue
        );
        if (!adjustedFormData) {
          toast.error('Por favor ingrese un tipo de cambio válido mayor a 0');
          return;
        }

        effectiveFormData = adjustedFormData;
        await Promise.all(
          (
            (effectiveFormData.settlement_details ||
              []) as Settlement['settlement_details']
          ).map((detail) => updateSettlementDetail(detail))
        );
        setFormData(effectiveFormData);
        setEditingCostField(null);
      }

      const details = (effectiveFormData.settlement_details ||
        settlement.settlement_details ||
        []) as Settlement['settlement_details'];

      if (
        details.some((detail) => !detail.unit_weight || detail.unit_weight == 0)
      ) {
        toast.error(
          'Hay algunos detalles que tienen peso bulto 0. Asigna los pesos antes de guardar.'
        );
        return;
      }

      if (details.some((detail) => !detail.quantity || detail.quantity == 0)) {
        toast.error(
          'Hay algunos detalles que tienen cantidad 0. Asigna las cantidades antes de guardar.'
        );
        return;
      }

      if (sourceCostLinesDirty && canEditCurrentSourceCosts) {
        await updateStandardSettlementSourceCostLines(
          settlement.id,
          resolvedSourceCostLines
        );
        const detailUpdates = await Promise.all(
          details.map((detail) => updateSettlementDetail(detail))
        );
        if (detailUpdates.some((result) => result === null)) {
          throw new Error(
            'No se pudo distribuir uno de los costos originales en el detalle.'
          );
        }
      }

      // Check if date_sold, comparable_market_id, currency, or ship_trip has changed
      const dateChanged =
        originalDateSold !== (effectiveFormData.date_sold || '');
      const comparableMarketChanged =
        originalComparableMarketId !== effectiveFormData.comparable_market_id;
      const currencyChanged =
        originalLocalCurrency !== (effectiveFormData.local_currency || '');
      const shipTripChanged =
        (originalShipTripId || '') !==
        (shipTripId ? shipTripId.toString() : '');
      const shouldRecalculateComparison =
        dateChanged ||
        comparableMarketChanged ||
        shipTripChanged ||
        currencyChanged;

      // Reconcile header totals from detail sums before saving
      const reconciledTotals = {
        total_revenue_local: details.reduce(
          (acc, d) => acc + (d.local_total_price || 0),
          0
        ),
        total_commission_local: details.reduce(
          (acc, d) => acc + (d.local_total_commision || 0),
          0
        ),
        total_tax_local: details.reduce(
          (acc, d) => acc + (d.local_total_tax || 0),
          0
        ),
        total_other_costs_local: details.reduce(
          (acc, d) => acc + (d.local_other_costs || 0),
          0
        ),
        total_repacking_costs_local: details.reduce(
          (acc, d) => acc + (d.local_repacking_costs || 0),
          0
        ),
        total_freight_usd: details.reduce(
          (acc, d) => acc + (d.usd_freight || 0),
          0
        ),
      };
      const legacyReceiverName =
        effectiveFormData.legacy_receiver ||
        settlement.legacy_receiver ||
        settlement.receiver;
      const officialReceiverName =
        selectedExporterReceiver?.name || effectiveFormData.receiver || '';

      // Update the settlement with new freight and commission values
      const updatedSettlement = await updateSettlement(settlement.id, {
        ...effectiveFormData,
        ...reconciledTotals,
        exporter_receiver_id: effectiveFormData.exporter_receiver_id,
        receiver: officialReceiverName,
        legacy_receiver: legacyReceiverName,
        reference_number: effectiveFormData.reference_number?.trim(),
        container_number: effectiveFormData.container_number?.trim(),
        comparable_market_id: effectiveFormData.comparable_market_id || null,
        ship_trip_id: shipTripId,
        // settlement_details: formData.settlement_details?.map((detail) => ({
        //   id: detail.id,
        //   usd_freight: detail.usd_freight,
        //   usd_total_fob: detail.usd_total_fob,
        //   local_currency: detail.local_currency,
        //   local_total_commision: detail.local_total_commision,
        //   local_total_tax: detail.local_total_tax,
        // })),
      });

      if (updatedSettlement) {
        if (
          userProfile?.exporter_id &&
          effectiveFormData.exporter_receiver_id
        ) {
          const result = await setSettlementReceiverForLegacyName({
            exporterId: userProfile.exporter_id,
            legacyReceiverName,
            exporterReceiverId: effectiveFormData.exporter_receiver_id,
          });

          if (result && result.updated_settlements > 0) {
            toast.success(
              `Cliente actualizado en ${result.updated_settlements} liquidaciones adicionales con el mismo nombre original.`
            );
          }
        }

        // Call market comparison calculation if relevant fields changed
        if (shouldRecalculateComparison) {
          try {
            toast.info(
              'Cambios detectados: recalculando comparación de precios...'
            );

            const response = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calculate-settlement-market-comparison`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${session?.access_token}`,
                },
                body: JSON.stringify({
                  settlement_id: settlement.id,
                  modifyMarketDateSold:
                    dateChanged || comparableMarketChanged || currencyChanged,
                  modifyMarketEta:
                    shipTripChanged ||
                    comparableMarketChanged ||
                    currencyChanged,
                }),
              }
            );

            if (response.ok) {
              await response.json();
            } else {
              console.warn(
                'Error calculating market comparison:',
                await response.text()
              );
              // Don't show error toast since main update was successful
            }
          } catch (error) {
            console.warn('Error calculating market comparison:', error);
            // Don't show error toast since main update was successful
          }
        }
        toast.success('Liquidación actualizada correctamente');

        // Track header changes in history
        const userName = `${userProfile?.first_name} ${userProfile?.last_name}`;
        const changes: string[] = [];

        if (dateChanged) {
          changes.push(
            `Fecha de Venta: ${originalDateSold || 'vacío'} → ${effectiveFormData.date_sold || 'vacío'}`
          );
        }
        if (
          (settlement.market_id || '').toString() !==
          (effectiveFormData.market_id || '').toString()
        ) {
          const oldMarket =
            markets.find(
              (m) => m.id.toString() === (settlement.market_id || '').toString()
            )?.name ||
            settlement.market_id ||
            'ninguno';
          const newMarket =
            markets.find(
              (m) =>
                m.id.toString() ===
                (effectiveFormData.market_id || '').toString()
            )?.name ||
            effectiveFormData.market_id ||
            'ninguno';
          changes.push(`Mercado de Venta: ${oldMarket} → ${newMarket}`);
        }
        if (comparableMarketChanged) {
          const oldMarket =
            markets.find((m) => m.id.toString() === originalComparableMarketId)
              ?.name ||
            originalComparableMarketId ||
            'ninguno';
          const newMarket =
            markets.find(
              (m) => m.id.toString() === effectiveFormData.comparable_market_id
            )?.name ||
            effectiveFormData.comparable_market_id ||
            'ninguno';
          changes.push(`Mercado Comparable: ${oldMarket} → ${newMarket}`);
        }
        if ((settlement.region || '') !== (effectiveFormData.region || '')) {
          changes.push(
            `Región: ${settlement.region || 'vacío'} → ${effectiveFormData.region || 'vacío'}`
          );
        }
        if (currencyChanged) {
          changes.push(
            `Moneda Local: ${originalLocalCurrency || 'vacío'} → ${effectiveFormData.local_currency || 'vacío'}`
          );
        }
        if (shipTripChanged) {
          const transportLabel = (t: string) =>
            t === 'M'
              ? 'Marítimo'
              : t === 'A'
                ? 'Aéreo'
                : t === 'T'
                  ? 'Terrestre'
                  : t;
          const describeTripById = (tripId: string) => {
            const trip = shipTrips.find((t) => t.id.toString() === tripId);
            if (!trip) return tripId || 'ninguno';
            const parts = [trip.ship?.name];
            if (trip.etd)
              parts.push(`ETD: ${formatDate(trip.etd, 'dd/MM/yyyy')}`);
            if (trip.eta)
              parts.push(`ETA: ${formatDate(trip.eta, 'dd/MM/yyyy')}`);
            const podName =
              trip.market?.name ||
              markets.find(
                (m) => m.id.toString() === trip.market_id?.toString()
              )?.name;
            if (podName) parts.push(`POD: ${podName}`);
            return parts.filter(Boolean).join(', ');
          };
          const oldDesc = originalShipTripId
            ? `${transportLabel(originalTransportType)} - ${describeTripById(originalShipTripId)}`
            : 'ninguno';
          const newTripId = shipTripId ? shipTripId.toString() : '';
          const newDesc = newTripId
            ? `${transportLabel(transportType)} - ${describeTripById(newTripId)}`
            : 'ninguno';
          changes.push(`Viaje/Transporte: ${oldDesc} → ${newDesc}`);
        }
        if (
          settlement.reference_number !==
          effectiveFormData.reference_number?.trim()
        ) {
          changes.push(
            `Número de Referencia: ${settlement.reference_number || 'vacío'} → ${effectiveFormData.reference_number?.trim() || 'vacío'}`
          );
        }
        if (
          settlement.container_number !==
          effectiveFormData.container_number?.trim()
        ) {
          changes.push(
            `Número de Contenedor: ${settlement.container_number || 'vacío'} → ${effectiveFormData.container_number?.trim() || 'vacío'}`
          );
        }
        if (settlement.receiver !== effectiveFormData.receiver) {
          changes.push(
            `Receptor: ${settlement.receiver || 'vacío'} → ${effectiveFormData.receiver || 'vacío'}`
          );
        }
        if (
          settlement.original_incoterm !== effectiveFormData.original_incoterm
        ) {
          changes.push(
            `Incoterm Original: ${settlement.original_incoterm || 'vacío'} → ${effectiveFormData.original_incoterm || 'vacío'}`
          );
        }
        if (
          (settlement.business_type || '') !==
          (effectiveFormData.business_type || '')
        ) {
          changes.push(
            `Tipo de Negocio: ${settlement.business_type || 'vacío'} → ${effectiveFormData.business_type || 'vacío'}`
          );
        }
        if (
          (settlement.sale_order || '') !== (effectiveFormData.sale_order || '')
        ) {
          changes.push(
            `Orden de Venta: ${settlement.sale_order || 'vacío'} → ${effectiveFormData.sale_order || 'vacío'}`
          );
        }
        if (!!settlement.is_sinister !== !!formData.is_sinister) {
          changes.push(
            `Siniestro: ${settlement.is_sinister ? 'sí' : 'no'} → ${formData.is_sinister ? 'sí' : 'no'}`
          );
        }
        if (
          (settlement.sinister_responsible || '') !==
          (formData.sinister_responsible || '')
        ) {
          changes.push(
            `Responsable Siniestro: ${settlement.sinister_responsible || 'vacío'} → ${formData.sinister_responsible || 'vacío'}`
          );
        }
        if (
          (settlement.sinister_delay_days || 0) !==
          (formData.sinister_delay_days || 0)
        ) {
          changes.push(
            `Días de retraso siniestro: ${settlement.sinister_delay_days || 0} → ${formData.sinister_delay_days || 0}`
          );
        }
        if (
          (settlement.sinister_case_number || '') !==
          (formData.sinister_case_number || '')
        ) {
          changes.push(
            `Número de Caso Siniestro: ${settlement.sinister_case_number || 'vacío'} → ${formData.sinister_case_number || 'vacío'}`
          );
        }
        if ((settlement.season || '') !== (formData.season || '')) {
          changes.push(
            `Temporada: ${settlement.season || 'vacío'} → ${formData.season || 'vacío'}`
          );
        }

        if (changes.length > 0) {
          const actionMessage = `${userName} ha actualizado la liquidación: ${changes.join('; ')}`;
          addHistoryComment(
            settlement.id,
            actionMessage,
            userProfile?.id,
            'action'
          );
        }

        setSourceCostLinesDirty(false);
        onEdit(updatedSettlement);
      }
    } catch (error) {
      console.error('Error updating settlement:', error);
      toast.error('Error al actualizar la liquidación');
    } finally {
      setLoading(false);
    }
  };

  const handleFreightChange = async (newFreightUsd: number) => {
    if (!settlement) return;
    // Calculate freight per kilo
    const totalKilos =
      formData.settlement_details?.reduce(
        (acc, detail) => acc + (detail.kilos || 0),
        0
      ) || 1;

    const freightPerKilo = newFreightUsd / totalKilos;

    // Update each detail's freight and recalculate FOB
    const updatedDetails = (formData.settlement_details || []).map((detail) => {
      const detailFreight = freightPerKilo * detail.kilos;
      const detailFob =
        (detail.local_total_price -
          detail.local_total_tax -
          detail.local_total_commision -
          detail.local_other_costs -
          (detail.local_repacking_costs || 0)) /
          (formData.exchange_rate || 1) -
        detailFreight;

      return {
        ...detail,
        usd_freight: detailFreight,
        usd_total_fob: detailFob,
      };
    });

    // Correct floating-point remainder on the last detail
    applyRemainderCorrection(updatedDetails, 'usd_freight', newFreightUsd);
    if (updatedDetails.length > 0) {
      recalcFob(
        updatedDetails[updatedDetails.length - 1],
        formData.exchange_rate || 1
      );
    }

    await Promise.all(updatedDetails.map((d) => updateSettlementDetail(d)));

    await updateSettlement(settlement.id, {
      total_freight_usd: newFreightUsd,
    });

    const actionMessage = `${userProfile?.first_name} ${
      userProfile?.last_name
    } ha actualizado el Flete (USD) a ${newFreightUsd}`;

    addHistoryComment(settlement.id, actionMessage, userProfile?.id, 'action');

    setFormData({
      ...formData,
      total_freight_usd: newFreightUsd,
      settlement_details: updatedDetails,
    });
  };

  const handleCommissionChange = async (newCommissionLocal: number) => {
    if (!settlement) return;
    // Calculate total revenue to determine commission rate
    const totalRevenue = formData.total_revenue_local || 1;
    const commissionRate = newCommissionLocal / totalRevenue;

    // Update each detail's commission and recalculate FOB
    const updatedDetails = (formData.settlement_details || []).map((detail) => {
      const detailCommission = detail.local_total_price * commissionRate;
      const detailFob =
        (detail.local_total_price -
          detail.local_total_tax -
          detailCommission -
          detail.local_other_costs -
          (detail.local_repacking_costs || 0)) /
          (formData.exchange_rate || 1) -
        (detail.usd_freight || 0);

      return {
        ...detail,
        local_total_commision: detailCommission,
        usd_total_fob: detailFob,
      };
    });

    // Correct floating-point remainder on the last detail
    applyRemainderCorrection(
      updatedDetails,
      'local_total_commision',
      newCommissionLocal
    );
    if (updatedDetails.length > 0) {
      recalcFob(
        updatedDetails[updatedDetails.length - 1],
        formData.exchange_rate || 1
      );
    }

    await Promise.all(updatedDetails.map((d) => updateSettlementDetail(d)));

    await updateSettlement(settlement.id, {
      total_commission_local: newCommissionLocal,
    });

    const actionMessage = `${userProfile?.first_name} ${
      userProfile?.last_name
    } ha actualizado la Comisión a ${newCommissionLocal}`;

    addHistoryComment(settlement.id, actionMessage, userProfile?.id, 'action');

    // Update local state
    setFormData({
      ...formData,
      total_commission_local: newCommissionLocal,
      settlement_details: updatedDetails,
    });
  };

  const handleTaxChange = async (newTaxLocal: number) => {
    if (!settlement) return;
    // Calculate total revenue to determine tax rate
    const totalRevenue = formData.total_revenue_local || 1;
    const taxRate = newTaxLocal / totalRevenue;

    // Update each detail's tax and recalculate FOB
    const updatedDetails = (formData.settlement_details || []).map((detail) => {
      const detailTax = detail.local_total_price * taxRate;
      const detailFob =
        (detail.local_total_price -
          detailTax -
          detail.local_total_commision -
          detail.local_other_costs -
          (detail.local_repacking_costs || 0)) /
          (formData.exchange_rate || 1) -
        (detail.usd_freight || 0);

      return {
        ...detail,
        local_total_tax: detailTax,
        usd_total_fob: detailFob,
      };
    });

    // Correct floating-point remainder on the last detail
    applyRemainderCorrection(updatedDetails, 'local_total_tax', newTaxLocal);
    if (updatedDetails.length > 0) {
      recalcFob(
        updatedDetails[updatedDetails.length - 1],
        formData.exchange_rate || 1
      );
    }

    await Promise.all(updatedDetails.map((d) => updateSettlementDetail(d)));

    await updateSettlement(settlement.id, {
      total_tax_local: newTaxLocal,
    });

    // Update local state
    setFormData({
      ...formData,
      total_tax_local: newTaxLocal,
      settlement_details: updatedDetails,
    });

    const actionMessage = `${userProfile?.first_name} ${
      userProfile?.last_name
    } ha actualizado los Impuestos a ${newTaxLocal}`;

    addHistoryComment(settlement.id, actionMessage, userProfile?.id, 'action');
  };

  const handleOtherCostsChange = async (newOtherCostsLocal: number) => {
    if (!settlement) return;
    // Calculate total kilos to determine cost per kilo
    const totalKilos =
      formData.settlement_details?.reduce(
        (acc, detail) => acc + detail.kilos,
        0
      ) || 1;
    const otherCostsPerKilo = newOtherCostsLocal / totalKilos;

    // Update each detail's other costs and recalculate FOB
    const updatedDetails = (formData.settlement_details || []).map((detail) => {
      const detailOtherCosts = otherCostsPerKilo * detail.kilos;
      const detailFob =
        (detail.local_total_price -
          detail.local_total_tax -
          detail.local_total_commision -
          detailOtherCosts -
          (detail.local_repacking_costs || 0)) /
          (formData.exchange_rate || 1) -
        (detail.usd_freight || 0);

      return {
        ...detail,
        local_other_costs: detailOtherCosts,
        usd_total_fob: detailFob,
      };
    });

    // Correct floating-point remainder on the last detail
    applyRemainderCorrection(
      updatedDetails,
      'local_other_costs',
      newOtherCostsLocal
    );
    if (updatedDetails.length > 0) {
      recalcFob(
        updatedDetails[updatedDetails.length - 1],
        formData.exchange_rate || 1
      );
    }

    await Promise.all(updatedDetails.map((d) => updateSettlementDetail(d)));

    await updateSettlement(settlement.id, {
      total_other_costs_local: newOtherCostsLocal,
    });

    // Update local state
    setFormData({
      ...formData,
      total_other_costs_local: newOtherCostsLocal,
      settlement_details: updatedDetails,
    });

    const actionMessage = `${userProfile?.first_name} ${
      userProfile?.last_name
    } ha actualizado los Otros Costos a ${newOtherCostsLocal}`;

    addHistoryComment(settlement.id, actionMessage, userProfile?.id, 'action');
  };

  const handleRepackingCostsChange = async (newRepackingCostsLocal: number) => {
    if (!settlement) return;
    const totalKilos =
      formData.settlement_details?.reduce(
        (acc, detail) => acc + detail.kilos,
        0
      ) || 1;
    const repackingCostsPerKilo = newRepackingCostsLocal / totalKilos;

    const updatedDetails = (formData.settlement_details || []).map((detail) => {
      const detailRepackingCosts = repackingCostsPerKilo * detail.kilos;
      const detailFob =
        (detail.local_total_price -
          detail.local_total_tax -
          detail.local_total_commision -
          (detail.local_other_costs || 0) -
          detailRepackingCosts) /
          (formData.exchange_rate || 1) -
        (detail.usd_freight || 0);

      return {
        ...detail,
        local_repacking_costs: detailRepackingCosts,
        usd_total_fob: detailFob,
      };
    });

    applyRemainderCorrection(
      updatedDetails,
      'local_repacking_costs',
      newRepackingCostsLocal
    );
    if (updatedDetails.length > 0) {
      recalcFob(
        updatedDetails[updatedDetails.length - 1],
        formData.exchange_rate || 1
      );
    }

    await Promise.all(updatedDetails.map((d) => updateSettlementDetail(d)));

    await updateSettlement(settlement.id, {
      total_repacking_costs_local: newRepackingCostsLocal,
    });

    setFormData({
      ...formData,
      total_repacking_costs_local: newRepackingCostsLocal,
      settlement_details: updatedDetails,
    });

    const actionMessage = `${userProfile?.first_name} ${
      userProfile?.last_name
    } ha actualizado los costos de Repacking a ${newRepackingCostsLocal}`;

    addHistoryComment(settlement.id, actionMessage, userProfile?.id, 'action');
  };

  const handleExchangeRateChange = async (newExchangeRate: number) => {
    if (!settlement || newExchangeRate <= 0) return;

    try {
      // Update each detail's USD FOB with the new exchange rate
      const detailUpdatePromises: Promise<any>[] = [];
      const updatedDetails = formData.settlement_details?.map((detail) => {
        const detailFob =
          (detail.local_total_price -
            (detail.local_total_tax || 0) -
            (detail.local_total_commision || 0) -
            (detail.local_other_costs || 0) -
            (detail.local_repacking_costs || 0)) /
            newExchangeRate -
          (detail.usd_freight || 0);

        const updatedDetail = {
          ...detail,
          usd_total_fob: detailFob,
          exchange_rate: newExchangeRate,
        };

        detailUpdatePromises.push(updateSettlementDetail(updatedDetail));

        return updatedDetail;
      });

      await Promise.all(detailUpdatePromises);

      // Update the settlement's exchange rate in the database
      await updateSettlement(settlement.id, {
        exchange_rate: newExchangeRate,
      });

      // Update local state
      setFormData({
        ...formData,
        exchange_rate: newExchangeRate,
        settlement_details: updatedDetails,
      });

      const actionMessage = `${userProfile?.first_name} ${
        userProfile?.last_name
      } ha actualizado el Tipo de Cambio a ${newExchangeRate}`;

      addHistoryComment(
        settlement.id,
        actionMessage,
        userProfile?.id,
        'action'
      );
    } catch (error) {
      console.error('Error updating exchange rate:', error);
      toast.error('Error al actualizar el tipo de cambio');
    }
  };

  const handleCellEdit = (detailId: number, field: string, value: string) => {
    setEditingCell({ detailId, field, value });
  };

  const handleCellSave = async () => {
    if (!editingCell || !settlement) return;

    setLoadingFullScreen(true);

    const userName = `${userProfile?.first_name} ${userProfile?.last_name}`;

    try {
      const { detailId, field, value } = editingCell;

      // Handle unit_weight field with specific calculations
      if (field === 'unit_weight') {
        const numericValue = parseFloat(value);
        if (isNaN(numericValue) || numericValue <= 0) {
          toast.error('Por favor ingrese un valor numérico válido mayor a 0');
          return;
        }

        // Find the current detail
        const currentDetail = formData.settlement_details?.find(
          (d) => d.id === detailId
        );
        if (!currentDetail) return;

        // STEP 1: Calculate new kilos for this detail
        const newUnitWeight = numericValue;
        const newKilos = newUnitWeight * currentDetail.quantity;

        // STEP 2: Calculate new total kilos for the settlement
        const otherDetailsKilos =
          formData.settlement_details
            ?.filter((d) => d.id !== detailId)
            .reduce((acc, d) => acc + d.kilos, 0) || 0;
        const newTotalKilos = otherDetailsKilos + newKilos;

        // STEP 3: Recalculate per-kg cost rates for the settlement
        const newCostPerKgExCommExTaxLiqLocal =
          (formData.total_other_costs_local || 0) / newTotalKilos;
        const newRepackingCostsPerKilo =
          (formData.total_repacking_costs_local || 0) / newTotalKilos;

        // STEP 4: Calculate freight per kilo for redistribution
        const freightPerKilo =
          (formData.total_freight_usd || 0) / newTotalKilos;

        // STEP 5: Update all settlement details with new proportional costs and freight
        const updatedDetails = (formData.settlement_details || []).map(
          (detail) => {
            if (detail.id == detailId) {
              // For the current detail being edited
              const newLocalOtherCosts =
                newCostPerKgExCommExTaxLiqLocal * newKilos;
              const newLocalRepackingCosts =
                newRepackingCostsPerKilo * newKilos;
              const newUsdFreight = freightPerKilo * newKilos;
              const newUsdTotalFob =
                (detail.local_total_price -
                  (detail.local_total_tax || 0) -
                  (detail.local_total_commision || 0) -
                  newLocalOtherCosts -
                  newLocalRepackingCosts) /
                  (formData.exchange_rate || 1) -
                newUsdFreight;

              return {
                ...detail,
                unit_weight: newUnitWeight,
                kilos: newKilos,
                local_other_costs: newLocalOtherCosts,
                local_repacking_costs: newLocalRepackingCosts,
                usd_freight: newUsdFreight,
                usd_total_fob: newUsdTotalFob,
              };
            } else {
              // For other details, recalculate costs and freight based on new proportions
              const newLocalOtherCosts =
                newCostPerKgExCommExTaxLiqLocal * detail.kilos;
              const newLocalRepackingCosts =
                newRepackingCostsPerKilo * detail.kilos;
              const newUsdFreight = freightPerKilo * detail.kilos;
              const newUsdTotalFob =
                (detail.local_total_price -
                  (detail.local_total_tax || 0) -
                  (detail.local_total_commision || 0) -
                  newLocalOtherCosts -
                  newLocalRepackingCosts) /
                  (formData.exchange_rate || 1) -
                newUsdFreight;

              return {
                ...detail,
                local_other_costs: newLocalOtherCosts,
                local_repacking_costs: newLocalRepackingCosts,
                usd_freight: newUsdFreight,
                usd_total_fob: newUsdTotalFob,
              };
            }
          }
        );

        // Correct floating-point remainder on the last detail
        applyRemainderCorrection(
          updatedDetails,
          'local_other_costs',
          formData.total_other_costs_local || 0
        );
        applyRemainderCorrection(
          updatedDetails,
          'local_repacking_costs',
          formData.total_repacking_costs_local || 0
        );
        applyRemainderCorrection(
          updatedDetails,
          'usd_freight',
          formData.total_freight_usd || 0
        );
        if (updatedDetails.length > 0) {
          recalcFob(
            updatedDetails[updatedDetails.length - 1],
            formData.exchange_rate || 1
          );
        }

        await Promise.all(updatedDetails.map((d) => updateSettlementDetail(d)));

        const updatedFormData = {
          ...formData,
          settlement_details: updatedDetails,
        };

        const actionMessage = `${userName} ha actualizado el peso unitario de la línea de detalle ${detailId} desde ${currentDetail.unit_weight} kg a ${newUnitWeight} kg`;

        addHistoryComment(
          settlement.id,
          actionMessage,
          userProfile?.id,
          'action'
        );

        // STEP 6: Update local state with all calculated values
        setFormData(updatedFormData);
        onEdit({ ...settlement, settlement_details: updatedDetails });
        toast.success('Peso unitario actualizado correctamente');
      } else if (field === 'quantity') {
        const numericValue = parseFloat(value);
        if (isNaN(numericValue) || numericValue <= 0) {
          toast.error('Por favor ingrese un valor numérico válido mayor a 0');
          return;
        }

        // Find the current detail
        const currentDetail = formData.settlement_details?.find(
          (d) => d.id === detailId
        );
        if (!currentDetail) return;

        // STEP 1: Calculate new values for this detail
        const newQuantity = numericValue;
        const newKilos = currentDetail.unit_weight * newQuantity;
        const pricePerBulto =
          currentDetail.local_total_price / currentDetail.quantity;
        const newLocalTotalPrice = pricePerBulto * newQuantity;

        // STEP 2: Calculate new totals for the settlement
        const otherDetailsKilos =
          formData.settlement_details
            ?.filter((d) => d.id !== detailId)
            .reduce((acc, d) => acc + d.kilos, 0) || 0;
        const newTotalKilos = otherDetailsKilos + newKilos;

        const otherDetailsRevenue =
          formData.settlement_details
            ?.filter((d) => d.id !== detailId)
            .reduce((acc, d) => acc + d.local_total_price, 0) || 0;
        const newTotalRevenueLocal = otherDetailsRevenue + newLocalTotalPrice;

        // STEP 3: Recalculate settlement-level costs per kg
        const newCostPerKgExCommExTaxLiqLocal =
          (formData.total_other_costs_local || 0) / newTotalKilos;
        const newRepackingCostsPerKilo =
          (formData.total_repacking_costs_local || 0) / newTotalKilos;

        // STEP 4: Calculate commission and tax rates for proportional distribution
        const commissionRate =
          (formData.total_commission_local || 0) / newTotalRevenueLocal;
        const taxRate = (formData.total_tax_local || 0) / newTotalRevenueLocal;

        // STEP 5: Calculate freight per kilo for redistribution
        const freightPerKilo =
          (formData.total_freight_usd || 0) / newTotalKilos;

        // STEP 6: Update all settlement details with costs and freight redistribution
        const updatedDetails = (formData.settlement_details || []).map(
          (detail) => {
            if (detail.id == detailId) {
              // For the current detail being edited
              const newLocalOtherCosts =
                newCostPerKgExCommExTaxLiqLocal * newKilos;
              const newLocalRepackingCosts =
                newRepackingCostsPerKilo * newKilos;
              const newLocalCommission = newLocalTotalPrice * commissionRate;
              const newLocalTax = newLocalTotalPrice * taxRate;
              const newUsdFreight = freightPerKilo * newKilos;
              const newUsdTotalFob =
                (newLocalTotalPrice -
                  newLocalTax -
                  newLocalCommission -
                  newLocalOtherCosts -
                  newLocalRepackingCosts) /
                  (formData.exchange_rate || 1) -
                newUsdFreight;

              return {
                ...detail,
                quantity: newQuantity,
                kilos: newKilos,
                local_total_price: newLocalTotalPrice,
                local_other_costs: newLocalOtherCosts,
                local_repacking_costs: newLocalRepackingCosts,
                local_total_commision: newLocalCommission,
                local_total_tax: newLocalTax,
                usd_freight: newUsdFreight,
                usd_total_fob: newUsdTotalFob,
              };
            } else {
              // For other details, recalculate costs and freight based on new proportions
              const newLocalOtherCosts =
                newCostPerKgExCommExTaxLiqLocal * detail.kilos;
              const newLocalRepackingCosts =
                newRepackingCostsPerKilo * detail.kilos;
              const newLocalCommission =
                detail.local_total_price * commissionRate;
              const newLocalTax = detail.local_total_price * taxRate;
              const newUsdFreight = freightPerKilo * detail.kilos;
              const newUsdTotalFob =
                (detail.local_total_price -
                  newLocalTax -
                  newLocalCommission -
                  newLocalOtherCosts -
                  newLocalRepackingCosts) /
                  (formData.exchange_rate || 1) -
                newUsdFreight;

              return {
                ...detail,
                local_other_costs: newLocalOtherCosts,
                local_repacking_costs: newLocalRepackingCosts,
                local_total_commision: newLocalCommission,
                local_total_tax: newLocalTax,
                usd_freight: newUsdFreight,
                usd_total_fob: newUsdTotalFob,
              };
            }
          }
        );

        // Correct floating-point remainder on the last detail
        applyRemainderCorrection(
          updatedDetails,
          'local_total_commision',
          formData.total_commission_local || 0
        );
        applyRemainderCorrection(
          updatedDetails,
          'local_total_tax',
          formData.total_tax_local || 0
        );
        applyRemainderCorrection(
          updatedDetails,
          'local_other_costs',
          formData.total_other_costs_local || 0
        );
        applyRemainderCorrection(
          updatedDetails,
          'local_repacking_costs',
          formData.total_repacking_costs_local || 0
        );
        applyRemainderCorrection(
          updatedDetails,
          'usd_freight',
          formData.total_freight_usd || 0
        );
        if (updatedDetails.length > 0) {
          recalcFob(
            updatedDetails[updatedDetails.length - 1],
            formData.exchange_rate || 1
          );
        }

        await Promise.all(updatedDetails.map((d) => updateSettlementDetail(d)));

        // STEP 7: Update the settlement with new totals
        const updatedFormData = {
          ...formData,
          settlement_details: updatedDetails,
          total_revenue_local: newTotalRevenueLocal,
        };

        // STEP 8: Update the database
        const updatedSettlement = await updateSettlement(settlement.id, {
          total_revenue_local: newTotalRevenueLocal,
        });

        const actionMessage = `${userName} ha actualizado la cantidad de bultos de la línea de detalle ${detailId} desde ${currentDetail.quantity} a ${newQuantity} unidades`;

        addHistoryComment(
          settlement.id,
          actionMessage,
          userProfile?.id,
          'action'
        );

        if (updatedSettlement) {
          // STEP 9: Update local state with all calculated values
          setFormData(updatedFormData);
          onEdit(updatedSettlement);
          toast.success('Cantidad de bultos actualizada correctamente');
        } else {
          toast.error('Error al actualizar la liquidación');
        }
      } else if (field === 'price_per_bulto') {
        const numericValue = parseFloat(value);
        if (isNaN(numericValue) || numericValue < 0) {
          toast.error(
            'Por favor ingrese un valor numérico válido mayor o igual a 0'
          );
          return;
        }

        // Find the current detail
        const currentDetail = formData.settlement_details?.find(
          (d) => d.id === detailId
        );
        if (!currentDetail) return;

        // STEP 1: Calculate new local_total_price for this detail
        const newPricePerBulto = numericValue;
        const newLocalTotalPrice = newPricePerBulto * currentDetail.quantity;

        // STEP 2: Calculate new total_revenue_local for the settlement
        const otherDetailsRevenue =
          formData.settlement_details
            ?.filter((d) => d.id !== detailId)
            .reduce((acc, d) => acc + d.local_total_price, 0) || 0;
        const newTotalRevenueLocal = otherDetailsRevenue + newLocalTotalPrice;

        // STEP 3: Calculate commission and tax rates for proportional distribution
        const commissionRate =
          (formData.total_commission_local || 0) / newTotalRevenueLocal;
        const taxRate = (formData.total_tax_local || 0) / newTotalRevenueLocal;

        // STEP 4: Update all settlement details (commission and tax redistribution)
        const updatedDetails = (formData.settlement_details || []).map(
          (detail) => {
            if (detail.id == detailId) {
              // For the current detail being edited
              const newLocalCommission = newLocalTotalPrice * commissionRate;
              const newLocalTax = newLocalTotalPrice * taxRate;
              const newUsdTotalFob =
                (newLocalTotalPrice -
                  newLocalTax -
                  newLocalCommission -
                  (detail.local_other_costs || 0) -
                  (detail.local_repacking_costs || 0)) /
                  (formData.exchange_rate || 1) -
                (detail.usd_freight || 0);

              return {
                ...detail,
                local_total_price: newLocalTotalPrice,
                local_total_commision: newLocalCommission,
                local_total_tax: newLocalTax,
                usd_total_fob: newUsdTotalFob,
              };
            } else {
              // For other details, recalculate commission and tax based on new rates
              const newLocalCommission =
                detail.local_total_price * commissionRate;
              const newLocalTax = detail.local_total_price * taxRate;
              const newUsdTotalFob =
                (detail.local_total_price -
                  newLocalTax -
                  newLocalCommission -
                  (detail.local_other_costs || 0) -
                  (detail.local_repacking_costs || 0)) /
                  (formData.exchange_rate || 1) -
                (detail.usd_freight || 0);

              return {
                ...detail,
                local_total_commision: newLocalCommission,
                local_total_tax: newLocalTax,
                usd_total_fob: newUsdTotalFob,
              };
            }
          }
        );

        // Correct floating-point remainder on the last detail
        applyRemainderCorrection(
          updatedDetails,
          'local_total_commision',
          formData.total_commission_local || 0
        );
        applyRemainderCorrection(
          updatedDetails,
          'local_total_tax',
          formData.total_tax_local || 0
        );
        if (updatedDetails.length > 0) {
          recalcFob(
            updatedDetails[updatedDetails.length - 1],
            formData.exchange_rate || 1
          );
        }

        await Promise.all(updatedDetails.map((d) => updateSettlementDetail(d)));

        // STEP 5: Update the settlement with new total_revenue_local
        const updatedFormData = {
          ...formData,
          settlement_details: updatedDetails,
          total_revenue_local: newTotalRevenueLocal,
        };

        // STEP 6: Update the database
        const updatedSettlement = await updateSettlement(settlement.id, {
          total_revenue_local: newTotalRevenueLocal,
        });

        const actionMessage = `${userName} ha actualizado el precio por bulto de la línea de detalle ${detailId} desde ${currentDetail.local_total_price / currentDetail.quantity} a ${newPricePerBulto} ${formData.local_currency}`;

        addHistoryComment(
          settlement.id,
          actionMessage,
          userProfile?.id,
          'action'
        );

        if (updatedSettlement) {
          // STEP 7: Update local state with all calculated values
          setFormData(updatedFormData);
          onEdit(updatedSettlement);
          toast.success(
            'Precio por bulto actualizado correctamente y recalculada la comparación de precios'
          );

          // STEP 8: Recalculate market comparison for this specific detail
          try {
            const response = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calculate-settlement-detail-market-comparison`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${session?.access_token}`,
                },
                body: JSON.stringify({
                  settlement_detail_id: detailId,
                  // Recompute both date_sold and ETA metrics after price/weight edits
                  modifyMarketDateSold: true,
                  modifyMarketEta: true,
                }),
              }
            );

            if (response.ok) {
              const result = await response.json();
              console.log('Market comparison recalculated for detail:', result);
            } else {
              console.warn(
                'Error calculating market comparison for detail:',
                await response.text()
              );
            }
          } catch (error) {
            console.warn(
              'Error calculating market comparison for detail:',
              error
            );
          }
        } else {
          toast.error('Error al actualizar la liquidación');
        }
      } else {
        // Handle non-numeric fields (existing logic)
        const formattedValue = value.toUpperCase().trim();

        // Update the local state immediately for real-time feedback
        const updatedDetails =
          formData.settlement_details?.map((detail) => {
            if (detail.id === detailId) {
              return {
                ...detail,
                [field]: formattedValue,
              };
            }
            return detail;
          }) || [];

        // Update the form data immediately for real-time UI update
        setFormData({
          ...formData,
          settlement_details: updatedDetails,
        });

        // Call the API to update the database
        const updatedSettlement = await updateSettlementDetailFieldAndFetch(
          settlement.id,
          detailId,
          field,
          formattedValue
        );

        const currentDetail = formData.settlement_details?.find(
          (d) => d.id === detailId
        );

        const actionMessage = `${userName} ha actualizado el campo ${field} de la línea de detalle ${detailId} desde ${currentDetail?.[field] || ''} a ${formattedValue}`;

        addHistoryComment(
          settlement.id,
          actionMessage,
          userProfile?.id,
          'action'
        );

        if (updatedSettlement) {
          // Update the parent component with the updated settlement
          onEdit(updatedSettlement);
          toast.success('Detalle actualizado correctamente');
        } else {
          toast.error('Error al actualizar el detalle');
        }
      }
    } catch (error) {
      console.error('Error updating settlement detail:', error);
      toast.error('Error al actualizar el detalle');
    } finally {
      setEditingCell(null);
      setLoadingFullScreen(false);
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
  };

  const handleCostFieldEdit = (
    field:
      | 'commission'
      | 'tax'
      | 'freight'
      | 'otros'
      | 'repacking'
      | 'exchange_rate',
    value: string
  ) => {
    setEditingCostField({ field, value });
  };

  const handleCostFieldSave = async () => {
    if (!editingCostField) return;

    try {
      const { field, value } = editingCostField;
      const numericValue = parseFloat(value);

      if (isNaN(numericValue) || numericValue < 0) {
        toast.error(
          'Por favor ingrese un valor numérico válido mayor o igual a 0'
        );
        return;
      }

      setSavingCostField(true);

      if (field === 'commission') {
        await handleCommissionChange(numericValue);
        toast.success('Comisión actualizada correctamente');
      } else if (field === 'tax') {
        await handleTaxChange(numericValue);
        toast.success('Impuestos actualizados correctamente');
      } else if (field === 'freight') {
        await handleFreightChange(numericValue);
        toast.success('Flete actualizado correctamente');
      } else if (field === 'otros') {
        await handleOtherCostsChange(numericValue);
        toast.success('Otros costos actualizados correctamente');
      } else if (field === 'repacking') {
        await handleRepackingCostsChange(numericValue);
        toast.success('Repacking actualizado correctamente');
      } else if (field === 'exchange_rate') {
        await handleExchangeRateChange(numericValue);
        toast.success('Tipo de cambio actualizado correctamente');
      }
    } catch (error) {
      console.error('Error updating cost field:', error);
      toast.error('Error al actualizar el campo');
    } finally {
      setSavingCostField(false);
      setEditingCostField(null);
    }
  };

  const handleCostFieldCancel = () => {
    setEditingCostField(null);
  };

  // Copy functionality handlers
  const handleCellCopy = (
    detailId: number,
    field: CopyableField,
    value: string
  ) => {
    setCopyMode({ field, value });
    setSelectedRowsForCopy(new Set());
    setEditingCell(null); // Exit edit mode
  };

  const handleRowSelectionForCopy = (detailId: number, isSelected: boolean) => {
    setSelectedRowsForCopy((prev) => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(detailId);
      } else {
        newSet.delete(detailId);
      }
      return newSet;
    });
  };

  const isRowAlreadyHasValue = (
    detail: Settlement['settlement_details'][0],
    field: CopyableField,
    value: string
  ): boolean => {
    if (NUMERIC_COPY_FIELDS.includes(field as CopyableNumericField)) {
      const numValue = parseFloat(value);
      if (isNaN(numValue)) return false;
      if (field === 'unit_weight') return detail.unit_weight === numValue;
      if (field === 'quantity') return detail.quantity === numValue;
      if (field === 'price_per_bulto') {
        const currentPricePerBulto =
          detail.local_total_price / (detail.quantity || 1);
        return Math.abs(currentPricePerBulto - numValue) < 0.01;
      }
    }
    return (detail as any)[field] === value;
  };

  const handleConfirmCopyToRows = async () => {
    if (!copyMode || !settlement) return;

    setLoadingFullScreen(true);
    const rowCount = selectedRowsForCopy.size;

    try {
      const { field, value } = copyMode;

      if (NUMERIC_COPY_FIELDS.includes(field as CopyableNumericField)) {
        // ── Numeric path: bulk recalculation with cost redistribution ──
        const numericValue = parseFloat(value);

        // ── Step A: Validate and build updated details with new primary values ──
        if (field === 'unit_weight' || field === 'quantity') {
          if (isNaN(numericValue) || numericValue <= 0) {
            toast.error('Por favor ingrese un valor numérico válido mayor a 0');
            return;
          }
        } else if (field === 'price_per_bulto') {
          if (isNaN(numericValue) || numericValue < 0) {
            toast.error(
              'Por favor ingrese un valor numérico válido mayor o igual a 0'
            );
            return;
          }
        }

        const updatedDetails = (formData.settlement_details || []).map(
          (detail) => {
            if (!selectedRowsForCopy.has(detail.id)) return { ...detail };

            if (field === 'unit_weight') {
              const newKilos = numericValue * detail.quantity;
              return { ...detail, unit_weight: numericValue, kilos: newKilos };
            } else if (field === 'quantity') {
              const pricePerBulto =
                detail.local_total_price / (detail.quantity || 1);
              const newKilos = detail.unit_weight * numericValue;
              const newLocalTotalPrice = pricePerBulto * numericValue;
              return {
                ...detail,
                quantity: numericValue,
                kilos: newKilos,
                local_total_price: newLocalTotalPrice,
              };
            } else {
              // price_per_bulto
              const newLocalTotalPrice = numericValue * detail.quantity;
              return { ...detail, local_total_price: newLocalTotalPrice };
            }
          }
        );

        // ── Step B: Compute new totals AFTER all primary values are updated ──
        const newTotalKilos = updatedDetails.reduce(
          (acc, d) => acc + d.kilos,
          0
        );
        const newTotalRevenueLocal = updatedDetails.reduce(
          (acc, d) => acc + d.local_total_price,
          0
        );
        if (newTotalKilos <= 0) {
          toast.error('Total de kilos no puede ser 0');
          return;
        }
        if (newTotalRevenueLocal <= 0) {
          toast.error('Ingreso total no puede ser 0');
          return;
        }

        // ── Step C: Compute ALL redistribution rates from new totals ──
        // Commission & tax → proportional by sales
        const commissionRate =
          (formData.total_commission_local || 0) / newTotalRevenueLocal;
        const taxRate = (formData.total_tax_local || 0) / newTotalRevenueLocal;
        // Freight & other costs → proportional by kg
        const otherCostsPerKilo =
          (formData.total_other_costs_local || 0) / newTotalKilos;
        const repackingCostsPerKilo =
          (formData.total_repacking_costs_local || 0) / newTotalKilos;
        const freightPerKilo =
          (formData.total_freight_usd || 0) / newTotalKilos;

        // ── Step D: Redistribute ALL cost fields across ALL details ──
        const detailPromises: Promise<any>[] = [];
        const finalDetails = updatedDetails.map((detail) => {
          const newLocalCommission = detail.local_total_price * commissionRate;
          const newLocalTax = detail.local_total_price * taxRate;
          const newLocalOtherCosts = otherCostsPerKilo * detail.kilos;
          const newLocalRepackingCosts = repackingCostsPerKilo * detail.kilos;
          const newUsdFreight = freightPerKilo * detail.kilos;
          const newUsdTotalFob =
            (detail.local_total_price -
              newLocalTax -
              newLocalCommission -
              newLocalOtherCosts -
              newLocalRepackingCosts) /
              (formData.exchange_rate || 1) -
            newUsdFreight;

          const finalDetail = {
            ...detail,
            local_total_commision: newLocalCommission,
            local_total_tax: newLocalTax,
            local_other_costs: newLocalOtherCosts,
            local_repacking_costs: newLocalRepackingCosts,
            usd_freight: newUsdFreight,
            usd_total_fob: newUsdTotalFob,
          };
          detailPromises.push(updateSettlementDetail(finalDetail));
          return finalDetail;
        });

        await Promise.all(detailPromises);

        // ── Step E: Update settlement-level totals ──
        const revenueChanged =
          field === 'quantity' || field === 'price_per_bulto';
        const updatedSettlement = revenueChanged
          ? await updateSettlement(settlement.id, {
              total_revenue_local: newTotalRevenueLocal,
            })
          : { ...settlement, settlement_details: finalDetails };

        setFormData({
          ...formData,
          settlement_details: finalDetails,
          ...(revenueChanged
            ? { total_revenue_local: newTotalRevenueLocal }
            : {}),
        });
        if (updatedSettlement) onEdit(updatedSettlement);

        // ── Step F: Recalculate market comparison for price changes ──
        if (field === 'price_per_bulto') {
          const marketComparisonPromises = Array.from(selectedRowsForCopy).map(
            (detailId) =>
              fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calculate-settlement-detail-market-comparison`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${session?.access_token}`,
                  },
                  body: JSON.stringify({
                    settlement_detail_id: detailId,
                    modifyMarketDateSold: true,
                    modifyMarketEta: true,
                  }),
                }
              ).catch((err) =>
                console.warn(
                  'Error calculating market comparison for detail:',
                  err
                )
              )
          );
          await Promise.allSettled(marketComparisonPromises);
        }

        const fieldLabel = COPY_FIELD_LABELS[field] || field;
        const actionMessage = `${userProfile?.first_name} ${userProfile?.last_name} ha copiado el valor "${value}" en el campo ${fieldLabel} a ${rowCount} líneas de detalle`;
        addHistoryComment(
          settlement.id,
          actionMessage,
          userProfile?.id,
          'action'
        );
      } else {
        // ── Text path (existing logic) ──
        const updatePromises = Array.from(selectedRowsForCopy).map(
          async (detailId) => {
            try {
              const updatedSettlement =
                await updateSettlementDetailFieldAndFetch(
                  settlement.id,
                  detailId,
                  field,
                  value
                );
              return updatedSettlement;
            } catch (error) {
              console.error(`Error updating detail ${detailId}:`, error);
              return null;
            }
          }
        );

        await Promise.all(updatePromises);

        // Update local state
        setFormData((prev) => ({
          ...prev,
          settlement_details: prev.settlement_details?.map((detail) => {
            if (selectedRowsForCopy.has(detail.id)) {
              return {
                ...detail,
                [field]: value,
              };
            }
            return detail;
          }),
        }));

        // Add history comment
        const actionMessage = `${userProfile?.first_name} ${userProfile?.last_name} ha copiado el valor "${value}" en el campo ${field} a ${rowCount} líneas de detalle`;
        addHistoryComment(
          settlement.id,
          actionMessage,
          userProfile?.id,
          'action'
        );
      }

      // Exit copy mode
      setCopyMode(null);
      setSelectedRowsForCopy(new Set());
      toast.success(`Valor copiado a ${rowCount} filas`);
    } catch (error) {
      console.error('Error copying to rows:', error);
      toast.error('Error al copiar el valor a las filas seleccionadas');
    } finally {
      setLoadingFullScreen(false);
    }
  };

  const handleCancelCopyMode = () => {
    setCopyMode(null);
    setSelectedRowsForCopy(new Set());
  };

  const handleAddNewDetail = () => {
    const templateDetail = formData.settlement_details?.[0];
    const template = templateDetail
      ? {
          pallet: '',
          variety: templateDetail.variety,
          format: templateDetail.format,
          caliber: templateDetail.caliber,
          color: templateDetail.color,
          label: templateDetail.label,
          category: templateDetail.category || '',
          unit_weight: templateDetail.unit_weight,
          quantity: 0,
          kilos: 0,
          local_total_price: 0,
          local_total_tax: 0,
          local_total_commision: 0,
          local_other_costs: 0,
          local_repacking_costs: 0,
          usd_freight: 0,
          usd_total_fob: 0,
          local_currency: formData.local_currency || 'CLP',
        }
      : {
          pallet: '',
          variety: '',
          format: 'CAJA',
          caliber: '',
          color: '',
          label: '',
          category: '',
          unit_weight: 0,
          quantity: 0,
          kilos: 0,
          local_total_price: 0,
          local_total_tax: 0,
          local_total_commision: 0,
          local_other_costs: 0,
          local_repacking_costs: 0,
          usd_freight: 0,
          usd_total_fob: 0,
          local_currency: formData.local_currency || 'CLP',
        };

    setNewDetailData(template);
    setDuplicateSourceDetailId(null);
    setIsAddingNewDetail(true);

    window.setTimeout(() => {
      const newRow = document.querySelector('[data-key="new-detail"]');
      newRow?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleConfirmNewDetail = async () => {
    if (
      !settlement ||
      !newDetailData.unit_weight ||
      newDetailData.unit_weight <= 0 ||
      !newDetailData.quantity ||
      newDetailData.quantity <= 0
    ) {
      toast.error(
        'Por favor complete peso unitario y cantidad con valores mayores a 0'
      );
      return;
    }

    if (
      newDetailData.local_total_price === undefined ||
      newDetailData.local_total_price === null ||
      newDetailData.local_total_price < 0
    ) {
      toast.error(
        'Por favor ingrese un precio total válido (mayor o igual a 0)'
      );
      return;
    }

    setLoadingFullScreen(true);

    try {
      // Calculate kilos for new detail
      const newKilos = newDetailData.unit_weight * newDetailData.quantity;

      // Calculate new totals including the new detail
      const currentTotalKilos =
        formData.settlement_details?.reduce(
          (acc, detail) => acc + detail.kilos,
          0
        ) || 0;
      const newTotalKilos = currentTotalKilos + newKilos;

      const currentTotalRevenue = formData.total_revenue_local || 0;
      const newTotalRevenue =
        currentTotalRevenue + (newDetailData.local_total_price || 0);

      if (newTotalRevenue === 0 || newTotalKilos === 0) {
        toast.error(
          'No se puede agregar un detalle que resulte en ingresos o kilos totales en 0'
        );
        setLoadingFullScreen(false);
        return;
      }

      // Calculate rates for proportional distribution
      const commissionRate =
        (formData.total_commission_local || 0) / newTotalRevenue;
      const taxRate = (formData.total_tax_local || 0) / newTotalRevenue;
      const otherCostsPerKilo =
        (formData.total_other_costs_local || 0) / newTotalKilos;
      const repackingCostsPerKilo =
        (formData.total_repacking_costs_local || 0) / newTotalKilos;
      const freightPerKilo = (formData.total_freight_usd || 0) / newTotalKilos;

      // Calculate costs for the new detail
      const newDetailCommission =
        (newDetailData.local_total_price || 0) * commissionRate;
      const newDetailTax = (newDetailData.local_total_price || 0) * taxRate;
      const newDetailOtherCosts = otherCostsPerKilo * newKilos;
      const newDetailRepackingCosts = repackingCostsPerKilo * newKilos;
      const newDetailFreight = freightPerKilo * newKilos;
      const newDetailFob =
        ((newDetailData.local_total_price || 0) -
          newDetailTax -
          newDetailCommission -
          newDetailOtherCosts -
          newDetailRepackingCosts) /
          (formData.exchange_rate || 1) -
        newDetailFreight;

      // Create the complete new detail
      const completeNewDetail = {
        pallet: newDetailData.pallet,
        variety: newDetailData.variety,
        format: newDetailData.format,
        caliber: newDetailData.caliber,
        color: newDetailData.color,
        label: newDetailData.label,
        category: newDetailData.category,
        unit_weight: newDetailData.unit_weight,
        quantity: newDetailData.quantity,
        kilos: newKilos,
        local_total_price: newDetailData.local_total_price,
        local_total_commision: newDetailCommission,
        local_total_tax: newDetailTax,
        local_other_costs: newDetailOtherCosts,
        local_repacking_costs: newDetailRepackingCosts,
        usd_freight: newDetailFreight,
        usd_total_fob: newDetailFob,
        settlement_id: settlement.id,
        date_sold: formData.date_sold!,
        local_currency: formData.local_currency!,
        exchange_rate: formData.exchange_rate!,
        incoterm: formData.original_incoterm!,
        quartil_vs_market: null,
        difference_vs_market: null,
        difference_vs_market_eta: null,
        quartil_vs_market_eta: null,
      };

      // Update existing details with recalculated costs
      const updatedExistingDetails = (formData.settlement_details || []).map(
        (detail) => {
          const updatedCommission = detail.local_total_price * commissionRate;
          const updatedTax = detail.local_total_price * taxRate;
          const updatedOtherCosts = otherCostsPerKilo * detail.kilos;
          const updatedRepackingCosts = repackingCostsPerKilo * detail.kilos;
          const updatedFreight = freightPerKilo * detail.kilos;
          const updatedFob =
            (detail.local_total_price -
              updatedTax -
              updatedCommission -
              updatedOtherCosts -
              updatedRepackingCosts) /
              (formData.exchange_rate || 1) -
            updatedFreight;
          return {
            ...detail,
            local_total_commision: updatedCommission,
            local_total_tax: updatedTax,
            local_other_costs: updatedOtherCosts,
            local_repacking_costs: updatedRepackingCosts,
            usd_freight: updatedFreight,
            usd_total_fob: updatedFob,
          };
        }
      );

      // Correct floating-point remainder across all details (existing + new)
      const allDetailsForCorrection = [
        ...updatedExistingDetails,
        completeNewDetail,
      ];
      applyRemainderCorrection(
        allDetailsForCorrection,
        'local_total_commision',
        formData.total_commission_local || 0
      );
      applyRemainderCorrection(
        allDetailsForCorrection,
        'local_total_tax',
        formData.total_tax_local || 0
      );
      applyRemainderCorrection(
        allDetailsForCorrection,
        'local_other_costs',
        formData.total_other_costs_local || 0
      );
      applyRemainderCorrection(
        allDetailsForCorrection,
        'local_repacking_costs',
        formData.total_repacking_costs_local || 0
      );
      applyRemainderCorrection(
        allDetailsForCorrection,
        'usd_freight',
        formData.total_freight_usd || 0
      );
      if (allDetailsForCorrection.length > 0) {
        recalcFob(
          allDetailsForCorrection[allDetailsForCorrection.length - 1],
          formData.exchange_rate || 1
        );
      }

      await Promise.all(
        updatedExistingDetails.map((d) => updateSettlementDetail(d))
      );

      // Add the new detail to the database
      const createdDetail = await createSettlementDetail(completeNewDetail);

      if (!createdDetail) {
        throw new Error('Error creating settlement detail');
      }

      // Update settlement totals
      await updateSettlement(settlement.id, {
        total_revenue_local: newTotalRevenue,
      });

      // Update local state
      setFormData({
        ...formData,
        total_revenue_local: newTotalRevenue,
        settlement_details: duplicateSourceDetailId
          ? updatedExistingDetails.reduce<Settlement['settlement_details']>(
              (acc, d) => {
                acc.push(d);
                if (d.id === duplicateSourceDetailId) acc.push(createdDetail);
                return acc;
              },
              []
            )
          : [createdDetail, ...updatedExistingDetails],
      });

      if (settlement) {
        const actionMessage = `${userProfile?.first_name} ${
          userProfile?.last_name
        } ha agregado una nueva línea de detalle con ${newDetailData.quantity} unidades de Variedad: ${newDetailData.variety} Formato: ${newDetailData.format} Calibre: ${newDetailData.caliber} Color: ${newDetailData.color} Etiqueta: ${newDetailData.label} Categoría: ${newDetailData.category} Peso unitario: ${newDetailData.unit_weight} kg, por un valor total de ${newDetailData.local_total_price} ${formData.local_currency}`;

        addHistoryComment(
          settlement.id,
          actionMessage,
          userProfile?.id,
          'action'
        );
      }

      // Reset state
      setIsAddingNewDetail(false);
      setNewDetailData({});
      setDuplicateSourceDetailId(null);

      // Calculate market comparison for the newly created detail
      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calculate-settlement-detail-market-comparison`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
              settlement_detail_id: createdDetail.id,
              // On create, compute both date_sold and ETA metrics
              modifyMarketDateSold: true,
              modifyMarketEta: true,
            }),
          }
        );

        if (response.ok) {
          const result = await response.json();
          console.log('Market comparison calculated for new detail:', result);
        } else {
          console.warn(
            'Error calculating market comparison for new detail:',
            await response.text()
          );
        }
      } catch (error) {
        console.warn(
          'Error calculating market comparison for new detail:',
          error
        );
      }

      toast.success('Detalle agregado correctamente');
    } catch (error) {
      console.error('Error adding new detail:', error);
      toast.error('Error al agregar el detalle');
    } finally {
      setLoadingFullScreen(false);
    }
  };

  const handleCancelNewDetail = () => {
    setIsAddingNewDetail(false);
    setNewDetailData({});
    setDuplicateSourceDetailId(null);
  };

  const handleNewDetailFieldChange = (field: string, value: string) => {
    const numericFields = ['unit_weight', 'quantity', 'local_total_price'];
    const newValue = numericFields.includes(field)
      ? parseFloat(value) || 0
      : value;

    const updatedData = {
      ...newDetailData,
      [field]: newValue,
    };

    // Calculate kilos and price per bulto if needed
    if (field === 'unit_weight' || field === 'quantity') {
      updatedData.kilos =
        (updatedData.unit_weight || 0) * (updatedData.quantity || 0);
    }

    setNewDetailData(updatedData);
  };

  const handleDeleteDetail = async (detailId: number) => {
    if (!settlement) return;

    const confirmed = await confirm({
      title: t('¿Eliminar este detalle?'),
      message: t('Esta acción no se puede deshacer.'),
      isDestructive: true,
    });
    if (!confirmed) return;

    try {
      setLoadingFullScreen(true);

      // Find the detail to be deleted
      const detailToDelete = formData.settlement_details?.find(
        (d) => d.id === detailId
      );
      if (!detailToDelete) return;

      // Calculate new totals excluding the deleted detail
      const remainingDetails =
        formData.settlement_details?.filter((d) => d.id !== detailId) || [];
      const newTotalKilos = remainingDetails.reduce(
        (acc, detail) => acc + detail.kilos,
        0
      );
      const newTotalRevenue = remainingDetails.reduce(
        (acc, detail) => acc + detail.local_total_price,
        0
      );

      if (newTotalKilos === 0 || newTotalRevenue === 0) {
        toast.error('No se puede eliminar el único detalle de la liquidación');
        return;
      }

      // Calculate new rates for redistribution
      const commissionRate =
        (formData.total_commission_local || 0) / newTotalRevenue;
      const taxRate = (formData.total_tax_local || 0) / newTotalRevenue;
      const otherCostsPerKilo =
        (formData.total_other_costs_local || 0) / newTotalKilos;
      const repackingCostsPerKilo =
        (formData.total_repacking_costs_local || 0) / newTotalKilos;
      const freightPerKilo = (formData.total_freight_usd || 0) / newTotalKilos;

      // Update all remaining details with new proportional costs
      const updatedDetails = remainingDetails.map((detail) => {
        const newLocalCommission = detail.local_total_price * commissionRate;
        const newLocalTax = detail.local_total_price * taxRate;
        const newLocalOtherCosts = otherCostsPerKilo * detail.kilos;
        const newLocalRepackingCosts = repackingCostsPerKilo * detail.kilos;
        const newUsdFreight = freightPerKilo * detail.kilos;
        const newUsdTotalFob =
          (detail.local_total_price -
            newLocalTax -
            newLocalCommission -
            newLocalOtherCosts -
            newLocalRepackingCosts) /
            (formData.exchange_rate || 1) -
          newUsdFreight;

        return {
          ...detail,
          local_total_commision: newLocalCommission,
          local_total_tax: newLocalTax,
          local_other_costs: newLocalOtherCosts,
          local_repacking_costs: newLocalRepackingCosts,
          usd_freight: newUsdFreight,
          usd_total_fob: newUsdTotalFob,
        };
      });

      // Correct floating-point remainder on the last detail
      applyRemainderCorrection(
        updatedDetails,
        'local_total_commision',
        formData.total_commission_local || 0
      );
      applyRemainderCorrection(
        updatedDetails,
        'local_total_tax',
        formData.total_tax_local || 0
      );
      applyRemainderCorrection(
        updatedDetails,
        'local_other_costs',
        formData.total_other_costs_local || 0
      );
      applyRemainderCorrection(
        updatedDetails,
        'local_repacking_costs',
        formData.total_repacking_costs_local || 0
      );
      applyRemainderCorrection(
        updatedDetails,
        'usd_freight',
        formData.total_freight_usd || 0
      );
      if (updatedDetails.length > 0) {
        recalcFob(
          updatedDetails[updatedDetails.length - 1],
          formData.exchange_rate || 1
        );
      }

      await Promise.all(updatedDetails.map((d) => updateSettlementDetail(d)));

      // Delete the detail from database
      await deleteSettlementDetail(detailId);

      // Update settlement totals
      await updateSettlement(settlement.id, {
        total_revenue_local: newTotalRevenue,
      });

      if (settlement) {
        const actionMessage = `${userProfile?.first_name} ${
          userProfile?.last_name
        } ha eliminado la línea de detalle ${detailId}, que tenía ${detailToDelete.quantity} unidades de Variedad: ${detailToDelete.variety} Formato: ${detailToDelete.format} Calibre: ${detailToDelete.caliber} Color: ${detailToDelete.color} Etiqueta: ${detailToDelete.label} Categoría: ${detailToDelete.category} Peso unitario: ${detailToDelete.unit_weight} kg, por un valor total de ${detailToDelete.local_total_price} ${formData.local_currency}`;

        addHistoryComment(
          settlement.id,
          actionMessage,
          userProfile?.id,
          'action'
        );
      }

      // Update local state
      setFormData({
        ...formData,
        total_revenue_local: newTotalRevenue,
        settlement_details: updatedDetails,
      });

      toast.success('Detalle eliminado correctamente');
    } catch (error) {
      console.error('Error deleting settlement detail:', error);
      toast.error('Error al eliminar el detalle');
    } finally {
      setLoadingFullScreen(false);
    }
  };

  const handleDuplicateDetail = (detailId: number) => {
    // Find the detail to duplicate
    const detailToDuplicate = formData.settlement_details?.find(
      (d) => d.id === detailId
    );
    if (!detailToDuplicate) return;

    // Create a copy of the detail data for the new detail
    const duplicatedData = {
      id: '',
      pallet: detailToDuplicate.pallet || '',
      variety: detailToDuplicate.variety,
      format: detailToDuplicate.format,
      caliber: detailToDuplicate.caliber,
      color: detailToDuplicate.color,
      label: detailToDuplicate.label,
      category: detailToDuplicate.category || '',
      unit_weight: detailToDuplicate.unit_weight,
      quantity: detailToDuplicate.quantity,
      kilos: detailToDuplicate.kilos,
      local_total_price: detailToDuplicate.local_total_price,
      incoterm: detailToDuplicate.incoterm,
      local_currency: detailToDuplicate.local_currency,
      exchange_rate: detailToDuplicate.exchange_rate,
    };

    // Set the duplicated data and start adding mode
    setNewDetailData(duplicatedData);
    setDuplicateSourceDetailId(detailId);
    setIsAddingNewDetail(true);

    // Scroll to the duplicated row so the new detail row below it is visible
    setTimeout(() => {
      const newRow = document.querySelector('[data-key="new-detail"]');
      if (newRow) {
        newRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    toast.success(
      'Detalle duplicado. Complete la cantidad y precio para agregar.'
    );
  };

  const renderEditableCell = (
    detailId: number,
    field: string,
    value: string
  ) => {
    const isEditing =
      editingCell?.detailId === detailId && editingCell?.field === field;
    const isBlocked = settlement?.is_blocked || copyMode !== null;
    const isNumericField = (
      ['unit_weight', 'quantity', 'price_per_bulto'] as string[]
    ).includes(field);
    const isZeroValue = isNumericField && Number(value) === 0;

    if (isEditing && !isBlocked) {
      if (field === 'format') {
        return (
          <div className='flex items-center'>
            <Select
              size='sm'
              selectedKeys={[editingCell.value]}
              onChange={(e) =>
                setEditingCell({ ...editingCell, value: e.target.value })
              }
              className='w-[104px] min-w-[104px]'
            >
              {VALID_FORMATS.map((format) => (
                <SelectItem key={format} value={format}>
                  {format}
                </SelectItem>
              ))}
            </Select>
            <div className='flex ml-2'>
              <Button
                isIconOnly
                size='sm'
                color='success'
                onClick={handleCellSave}
              >
                <Icon icon='tabler:check' />
              </Button>
              <Button
                isIconOnly
                size='sm'
                color='danger'
                onClick={handleCellCancel}
                className='ml-1'
              >
                <Icon icon='tabler:x' />
              </Button>
              {ALL_COPY_FIELDS.includes(field as CopyableField) && (
                <Button
                  isIconOnly
                  size='sm'
                  color='primary'
                  onClick={() =>
                    handleCellCopy(
                      detailId,
                      field as CopyableField,
                      editingCell.value
                    )
                  }
                  className='ml-1'
                >
                  <Icon icon='tabler:copy' />
                </Button>
              )}
            </div>
          </div>
        );
      } else if (field === 'color' && settlement?.species_id == 2) {
        return (
          <div className='flex items-center'>
            <Select
              size='sm'
              selectedKeys={[editingCell.value]}
              onChange={(e) =>
                setEditingCell({ ...editingCell, value: e.target.value })
              }
              className='min-w-[80px]'
            >
              <SelectItem key='L' value='L'>
                L
              </SelectItem>
              <SelectItem key='D' value='D'>
                D
              </SelectItem>
              <SelectItem key='DD' value='DD'>
                DD
              </SelectItem>
            </Select>
            <div className='flex ml-2'>
              <Button
                isIconOnly
                size='sm'
                color='success'
                onClick={handleCellSave}
              >
                <Icon icon='tabler:check' />
              </Button>
              <Button
                isIconOnly
                size='sm'
                color='danger'
                onClick={handleCellCancel}
                className='ml-1'
              >
                <Icon icon='tabler:x' />
              </Button>
              {ALL_COPY_FIELDS.includes(field as CopyableField) && (
                <Button
                  isIconOnly
                  size='sm'
                  color='primary'
                  onClick={() =>
                    handleCellCopy(
                      detailId,
                      field as CopyableField,
                      editingCell.value
                    )
                  }
                  className='ml-1'
                >
                  <Icon icon='tabler:copy' />
                </Button>
              )}
            </div>
          </div>
        );
      } else {
        return (
          <div className='flex items-center'>
            <Input
              size='sm'
              type={
                field === 'unit_weight' ||
                field === 'quantity' ||
                field === 'price_per_bulto'
                  ? 'number'
                  : 'text'
              }
              value={editingCell.value}
              onChange={(e) =>
                setEditingCell({
                  ...editingCell,
                  value: e.target.value.toUpperCase(),
                })
              }
              className='min-w-[120px]'
              classNames={{
                input: isNumericField ? 'text-right tabular-nums' : '',
              }}
              autoFocus
            />
            <div className='flex ml-2'>
              <Button
                isIconOnly
                size='sm'
                color='success'
                onClick={handleCellSave}
              >
                <Icon icon='tabler:check' />
              </Button>
              <Button
                isIconOnly
                size='sm'
                color='danger'
                onClick={handleCellCancel}
                className='ml-1'
              >
                <Icon icon='tabler:x' />
              </Button>
              {ALL_COPY_FIELDS.includes(field as CopyableField) && (
                <Button
                  isIconOnly
                  size='sm'
                  color='primary'
                  onClick={() =>
                    handleCellCopy(
                      detailId,
                      field as CopyableField,
                      editingCell.value
                    )
                  }
                  className='ml-1'
                >
                  <Icon icon='tabler:copy' />
                </Button>
              )}
            </div>
          </div>
        );
      }
    }

    return (
      <div
        className={`${isBlocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-default-200'} ${isNumericField ? 'text-right tabular-nums' : ''} ${isZeroValue ? 'text-default-300' : ''} rounded p-1`}
        onClick={() =>
          !isBlocked && handleCellEdit(detailId, field, value || '')
        }
        title={copyMode ? 'Edición no disponible en modo copia' : undefined}
      >
        {value || 'N/A'}
      </div>
    );
  };

  const renderEditableCostField = (
    field:
      | 'commission'
      | 'tax'
      | 'freight'
      | 'otros'
      | 'repacking'
      | 'exchange_rate',
    value: number | undefined,
    currency: string
  ) => {
    const isEditing = editingCostField?.field === field;
    const isBlocked = settlement?.is_blocked || copyMode !== null;
    const categoryByField: Partial<
      Record<EditableCostField, OfficialCostCategory>
    > = {
      commission: 'commission',
      tax: 'tax',
      freight: 'freight',
      otros: 'other_costs',
      repacking: 'repacking',
    };
    const sourceCategory = categoryByField[field];
    // Shared multi-shipment costs open the editor view-only: they are never
    // typed in directly.
    const opensSourceCostEditor =
      (canEditCurrentSourceCosts || sharedSourceCosts) &&
      sourceCategory !== undefined;

    if (opensSourceCostEditor) {
      return (
        <div
          className={`${isBlocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-default-200'} w-full rounded p-1`}
          onClick={() => {
            if (isBlocked) return;
            setSourceCostEditorCategory(sourceCategory);
            setIsSourceCostEditorOpen(true);
          }}
          title='Ver y editar las líneas originales de esta categoría'
        >
          <Input
            size='sm'
            type='number'
            value={value?.toString() || '0'}
            endContent={
              <span className='text-xs text-default-400'>{currency}</span>
            }
            className='pointer-events-none'
            readOnly
            disabled={isBlocked}
          />
        </div>
      );
    }

    if (isEditing && !isBlocked) {
      return (
        <div className='flex items-center justify-between w-full'>
          <Input
            size='sm'
            type='number'
            value={editingCostField.value}
            onChange={(e) =>
              setEditingCostField({
                ...editingCostField,
                value: e.target.value,
              })
            }
            endContent={
              <span className='text-xs text-default-400'>{currency}</span>
            }
            className='min-w-[120px]'
            autoFocus
            isDisabled={savingCostField}
          />
          <div className='flex ml-2'>
            <Button
              isIconOnly
              size='sm'
              color='success'
              onClick={handleCostFieldSave}
              isLoading={savingCostField}
              isDisabled={savingCostField}
            >
              {!savingCostField && <Icon icon='tabler:check' />}
            </Button>
            <Button
              isIconOnly
              size='sm'
              color='danger'
              onClick={handleCostFieldCancel}
              className='ml-1'
              isDisabled={savingCostField}
            >
              <Icon icon='tabler:x' />
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div
        className={`${isBlocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-default-200'} p-1 rounded flex items-center w-full`}
        onClick={() =>
          !isBlocked && handleCostFieldEdit(field, value?.toString() || '0')
        }
        title={copyMode ? 'Edición no disponible en modo copia' : undefined}
      >
        <Input
          size='sm'
          type='number'
          value={value?.toString() || '0'}
          endContent={
            <span className='text-xs text-default-400'>{currency}</span>
          }
          className='pointer-events-none'
          readOnly
          disabled={isBlocked}
        />
      </div>
    );
  };

  const handleCreateAirShipment = async (): Promise<ShipTrip | null> => {
    try {
      const shipTrip = await findOrCreateAirShipTrip(
        airShipmentData.etd,
        airShipmentData.eta,
        airShipmentData.pod,
        transportType
      );

      if (shipTrip) {
        setSelectedShipTripId(shipTrip.id.toString());
        setFormData({
          ...formData,
          ship_trip_id: shipTrip.id,
        });
        toast.success('Envío aéreo asociado correctamente');

        // Refresh ship trips list
        const trips = await getShipTrips();
        setShipTrips(trips || []);
        return shipTrip;
      }
    } catch (error) {
      console.error('Error creating air shipment:', error);
      toast.error('Error al crear envío aéreo');
      return null;
    }
    return null;
  };

  const showOriginalTransportData = () => {
    const originalTrip = shipTrips.find(
      (t) => t.id.toString() === originalShipTripId
    );
    let alertMessage = `Estás editando el transporte original:\n\n`;
    alertMessage += `Tipo: ${originalTransportType === 'M' ? 'Marítimo' : originalTransportType === 'A' ? 'Aéreo' : 'Terrestre'}\n`;
    if (originalTrip) {
      alertMessage += `Nave/Vuelo: ${originalTrip.ship.name}\n`;
      alertMessage += `ETD: ${formatDate(originalTrip.etd, 'dd MMMM yyyy')}\n`;
      alertMessage += `ETA: ${formatDate(originalTrip.eta, 'dd MMMM yyyy')}\n`;
      alertMessage += `POD: ${originalTrip.market?.name || 'No definido'}\n`;
    } else if (originalShipTripId) {
      alertMessage +=
        'No se encontró información completa del viaje original en la lista actual de viajes.';
    } else {
      alertMessage += 'No había ningún transporte original asociado.';
    }
    setTransportMessage(alertMessage);
  };

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();
  const [deleteBlockingClaims, setDeleteBlockingClaims] = useState<
    { id: number }[]
  >([]);
  const [isCheckingDeleteClaims, setIsCheckingDeleteClaims] = useState(false);

  const handleDeleteSettlement = async () => {
    if (!settlement) return;

    setIsCheckingDeleteClaims(true);
    try {
      const blockingClaims = await getClaimsBlockingSettlementDeletion(
        settlement.id
      );
      setDeleteBlockingClaims(blockingClaims);
    } catch (error) {
      console.error('Error checking claims for settlement:', error);
      setDeleteBlockingClaims([]);
    } finally {
      setIsCheckingDeleteClaims(false);
    }

    onDeleteModalOpen();
  };

  const handleConfirmDelete = async () => {
    if (!settlement) return;

    try {
      setDeleting(true);
      const success = session?.access_token
        ? await deleteSettlementAudited(settlement.id, session.access_token)
        : await deleteSettlement(settlement.id);

      if (success) {
        toast.success('Liquidación eliminada correctamente');
        onDeleteModalClose();
        if (onDelete) {
          onDelete();
        }
        onClose();
      } else {
        toast.error('Error al eliminar la liquidación');
      }
    } catch (error) {
      console.error('Error deleting settlement:', error);
      toast.error('Error al eliminar la liquidación');
    } finally {
      setDeleting(false);
    }
  };

  const stickyTotalsCellClass =
    'sticky bottom-0 z-30 border-t border-divider bg-default-100/95 shadow-[0_-4px_10px_-8px_rgba(0,0,0,0.45)] backdrop-blur-sm text-md font-bold';

  const renderDetailTotalsRow = () => (
    <TableRow key='detail-totals' className='border-0 font-semibold'>
      <TableCell className={stickyTotalsCellClass} />
      <TableCell className={stickyTotalsCellClass} />
      <TableCell
        className={`${stickyTotalsCellClass} ${copyMode ? '' : 'hidden'}`}
      />
      <TableCell className={stickyTotalsCellClass} />
      <TableCell className={stickyTotalsCellClass} />
      <TableCell className={stickyTotalsCellClass} />
      <TableCell className={stickyTotalsCellClass} />
      <TableCell className={stickyTotalsCellClass} />
      <TableCell className={stickyTotalsCellClass} />
      <TableCell
        className={`${stickyTotalsCellClass} whitespace-nowrap text-right text-default-500`}
      ></TableCell>
      <TableCell className={stickyTotalsCellClass} />
      <TableCell
        className={`${stickyTotalsCellClass} text-right tabular-nums ${!detailTableTotals.quantity ? 'text-default-300' : ''}`}
      >
        {detailTableTotals.quantity.toLocaleString()}
      </TableCell>
      <TableCell
        className={`${stickyTotalsCellClass} whitespace-nowrap text-right tabular-nums ${!detailTableTotals.kilos ? 'text-default-300' : ''}`}
      >
        {detailTableTotals.kilos.toLocaleString()} kg
      </TableCell>
      <TableCell className={stickyTotalsCellClass} />
      <TableCell
        className={`${stickyTotalsCellClass} whitespace-nowrap text-right tabular-nums ${!displayedDetailRevenueTotal ? 'text-default-300' : 'text-green-600 dark:text-green-400'}`}
      >
        {displayedDetailRevenueTotal.toLocaleString()} {formData.local_currency}
      </TableCell>
      <TableCell
        className={`${stickyTotalsCellClass} ${showCostColumns ? '' : 'hidden'}`}
      />
      <TableCell
        className={`${stickyTotalsCellClass} ${showCostColumns ? '' : 'hidden'}`}
      />
      <TableCell
        className={`${stickyTotalsCellClass} ${showCostColumns ? '' : 'hidden'}`}
      />
      <TableCell
        className={`${stickyTotalsCellClass} ${showCostColumns ? '' : 'hidden'}`}
      />
      <TableCell
        className={`${stickyTotalsCellClass} ${showCostColumns ? '' : 'hidden'}`}
      />
    </TableRow>
  );

  const renderNewDetailRow = () => (
    <TableRow key='new-detail' className='bg-blue-50 dark:bg-blue-950'>
      <TableCell>Nueva</TableCell>
      <TableCell>
        <div className='flex gap-1'>
          <Button
            isIconOnly
            size='sm'
            color='success'
            onClick={handleConfirmNewDetail}
          >
            <Icon icon='tabler:check' />
          </Button>
          <Button
            isIconOnly
            size='sm'
            color='danger'
            onClick={handleCancelNewDetail}
          >
            <Icon icon='tabler:x' />
          </Button>
        </div>
      </TableCell>
      <TableCell className={copyMode ? '' : 'hidden'}>
        {/* No checkbox for new detail row */}
      </TableCell>
      <TableCell>
        <Input
          size='sm'
          value={newDetailData.pallet || ''}
          onChange={(e) =>
            handleNewDetailFieldChange('pallet', e.target.value.toUpperCase())
          }
          className='min-w-[80px]'
        />
      </TableCell>
      <TableCell>
        <Input
          size='sm'
          value={newDetailData.variety || ''}
          onChange={(e) =>
            handleNewDetailFieldChange('variety', e.target.value.toUpperCase())
          }
          className='min-w-[120px]'
        />
      </TableCell>
      <TableCell>
        <Select
          size='sm'
          selectedKeys={[newDetailData.format || 'CAJA']}
          onChange={(e) => handleNewDetailFieldChange('format', e.target.value)}
          className='w-[104px] min-w-[104px]'
        >
          {VALID_FORMATS.map((format) => (
            <SelectItem key={format} value={format}>
              {format}
            </SelectItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <Input
          size='sm'
          value={newDetailData.caliber || ''}
          onChange={(e) =>
            handleNewDetailFieldChange('caliber', e.target.value.toUpperCase())
          }
          className='min-w-[80px]'
        />
      </TableCell>
      <TableCell>
        {settlement?.species_id == 2 ? (
          <Select
            size='sm'
            selectedKeys={[newDetailData.color || 'L']}
            onChange={(e) =>
              handleNewDetailFieldChange('color', e.target.value)
            }
            className='min-w-[80px]'
          >
            <SelectItem key='L' value='L'>
              L
            </SelectItem>
            <SelectItem key='D' value='D'>
              D
            </SelectItem>
            <SelectItem key='DD' value='DD'>
              DD
            </SelectItem>
          </Select>
        ) : (
          <Input
            size='sm'
            value={newDetailData.color || ''}
            onChange={(e) =>
              handleNewDetailFieldChange('color', e.target.value.toUpperCase())
            }
            className='min-w-[80px]'
          />
        )}
      </TableCell>
      <TableCell>
        <Input
          size='sm'
          value={newDetailData.label || ''}
          onChange={(e) =>
            handleNewDetailFieldChange('label', e.target.value.toUpperCase())
          }
          className='min-w-[120px]'
        />
      </TableCell>
      <TableCell>
        <Input
          size='sm'
          value={newDetailData.category || ''}
          onChange={(e) =>
            handleNewDetailFieldChange('category', e.target.value.toUpperCase())
          }
          className='min-w-[80px]'
        />
      </TableCell>
      <TableCell>
        <Input
          size='sm'
          type='number'
          value={newDetailData.unit_weight?.toString() || ''}
          onChange={(e) =>
            handleNewDetailFieldChange(
              'unit_weight',
              e.target.value.toUpperCase()
            )
          }
          className='min-w-[80px]'
          classNames={{ input: 'text-right tabular-nums' }}
        />
      </TableCell>
      <TableCell>
        <Input
          size='sm'
          type='number'
          value={newDetailData.quantity?.toString() || ''}
          onChange={(e) =>
            handleNewDetailFieldChange('quantity', e.target.value.toUpperCase())
          }
          className='min-w-[80px]'
          classNames={{ input: 'text-right tabular-nums' }}
        />
      </TableCell>
      <TableCell
        className={`text-right tabular-nums ${!newDetailData.kilos ? 'text-default-300' : ''}`}
      >
        {newDetailData.kilos || 0}
      </TableCell>
      <TableCell>
        <Input
          size='sm'
          type='number'
          value={
            (
              (newDetailData.local_total_price || 0) /
              (newDetailData.quantity || 1)
            )?.toString() || ''
          }
          onChange={(e) => {
            const pricePerBulto = parseFloat(e.target.value) || 0;
            const totalPrice = pricePerBulto * (newDetailData.quantity || 1);
            handleNewDetailFieldChange(
              'local_total_price',
              totalPrice.toString()
            );
          }}
          className='min-w-[100px]'
          classNames={{ input: 'text-right tabular-nums' }}
        />
      </TableCell>
      <TableCell
        className={`text-right tabular-nums ${!newDetailData.local_total_price ? 'text-default-300' : ''}`}
      >
        {newDetailData.local_total_price || 0}
      </TableCell>
      <TableCell
        className={`${showCostColumns ? '' : 'hidden'} text-right tabular-nums text-default-300`}
      >
        —
      </TableCell>
      <TableCell
        className={`${showCostColumns ? '' : 'hidden'} text-right tabular-nums text-default-300`}
      >
        —
      </TableCell>
      <TableCell
        className={`${showCostColumns ? '' : 'hidden'} text-right tabular-nums text-default-300`}
      >
        —
      </TableCell>
      <TableCell
        className={`${showCostColumns ? '' : 'hidden'} text-right tabular-nums text-default-300`}
      >
        —
      </TableCell>
      <TableCell
        className={`${showCostColumns ? '' : 'hidden'} text-right tabular-nums text-default-300`}
      >
        —
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <FullScreenLoading
        isOpen={loadingFullScreen}
        message='Guardando cambios...'
      />
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size='5xl'
        className='w-[95vw] max-w-[95vw] h-[90vh] mx-auto my-auto'
        scrollBehavior='inside'
      >
        <ModalContent className='mx-auto my-auto h-[90vh] overflow-hidden'>
          <ModalHeader className='flex min-h-[76px] flex-row items-center justify-between gap-4 px-6 py-4 pr-14'>
            <div className='flex min-w-0 flex-wrap items-center gap-3'>
              <span className='whitespace-nowrap text-xl font-semibold'>
                Editar Liquidación
              </span>
              {settlement?.reference_number && (
                <div className='flex items-center gap-2'>
                  <Chip size='sm' variant='flat' color='default'>
                    Ref {settlement.reference_number}
                  </Chip>
                  {settlement.container_number && (
                    <Chip size='sm' variant='flat' color='default'>
                      {settlement.container_number}
                    </Chip>
                  )}
                </div>
              )}
            </div>
            <div className='flex shrink-0 items-center gap-5'>
              {otherRevenues.length > 0 && (
                <Switch
                  size='sm'
                  isSelected={showWithRevenues}
                  onValueChange={setShowWithRevenues}
                  className='flex-shrink-0'
                >
                  <span className='whitespace-nowrap text-sm'>
                    Ver con ingresos adicionales
                  </span>
                </Switch>
              )}
              <div className='flex items-baseline gap-3 whitespace-nowrap'>
                <span className='text-sm font-medium text-default-400'>
                  FOB Total
                </span>
                <span className='text-lg font-bold text-foreground'>
                  {displayedFobUsd.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{' '}
                  USD
                </span>
              </div>
            </div>
          </ModalHeader>

          {/* Blocked Settlement Warning */}
          {settlement?.is_blocked && (
            <div className='mx-6 mb-4 bg-red-50 dark:bg-red-950 border-l-4 border-red-500 p-4 rounded-md'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <Icon icon='tabler:lock' className='h-5 w-5 text-red-500' />
                </div>
                <div className='ml-3'>
                  <p className='text-sm text-red-700 dark:text-red-300 font-medium'>
                    Liquidación Bloqueada
                  </p>
                  <p className='text-sm text-red-600 dark:text-red-400 mt-1'>
                    Esta liquidación está bloqueada para edición. No se pueden
                    realizar cambios.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Duplicate Settlement Warning */}
          {validationMessage && (
            <div className='mx-6 mb-4 bg-red-50 dark:bg-red-950 border-l-4 border-red-500 p-4 rounded-md'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <Icon icon='tabler:x' className='h-5 w-5 text-red-500' />
                </div>
                <div className='ml-3'>
                  <p className='text-sm text-red-700 dark:text-red-300 font-medium'>
                    Duplicado Detectado
                  </p>
                  <p className='text-sm text-red-600 dark:text-red-400 mt-1'>
                    {validationMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* {unknownVarieties.length > 0 && (
            <div className='mx-6 mb-4 bg-red-50 dark:bg-red-950 border-l-4 border-red-500 p-4 rounded-md'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <Icon icon='tabler:x' className='h-5 w-5 text-red-500' />
                </div>
                <div className='ml-3'>
                  <p className='text-sm text-red-700 dark:text-red-300 font-medium'>
                    Variedades fuera del catálogo oficial
                  </p>
                  <p className='text-sm text-red-600 dark:text-red-400 mt-1'>
                    {unknownVarieties.length} variedades no existen en el
                    catálogo oficial de la especie.
                  </p>
                </div>
              </div>
            </div>
          )} */}

          <div className='shrink-0 border-b border-divider px-6'>
            <div className='flex min-w-0 items-center justify-between gap-4'>
              <Tabs
                aria-label='Secciones de la liquidación'
                selectedKey={activeTab}
                onSelectionChange={(key) => {
                  const nextTab = key as EditSettlementTab;
                  setActiveTab(nextTab);
                  if (nextTab === 'history' || nextTab === 'documents') {
                    void refreshSupportTabCounts();
                  }
                }}
                variant='underlined'
                classNames={{
                  base: 'min-w-0 flex-1 overflow-x-auto',
                  tabList: 'gap-8 p-0',
                  tab: 'h-12 px-0 text-sm font-semibold',
                  cursor: 'bg-primary',
                }}
              >
                <Tab key='general' title='General' />
                <Tab key='income_costs' title='Ingresos y Costos' />
                <Tab
                  key='quadrature'
                  title={
                    <span className='flex items-center gap-2'>
                      <span>Cuadratura</span>
                      <span
                        className={`inline-flex h-5 items-center gap-1.5 rounded-full px-2 text-[11px] font-semibold ${
                          !hasQuadratureData
                            ? 'bg-default-100 text-default-500'
                            : isQuadratureMatched
                              ? 'bg-success-500/10 text-success-600 dark:text-success-400'
                              : hasPendingQuadratureChanges
                                ? 'bg-warning-500/10 text-warning-600 dark:text-warning-400'
                                : 'bg-danger-500/10 text-danger-600 dark:text-danger-400'
                        }`}
                      >
                        <span
                          className={`h-1.5 w-1.5 rounded-full ${
                            !hasQuadratureData
                              ? 'bg-default-400'
                              : isQuadratureMatched
                                ? 'bg-success-500'
                                : hasPendingQuadratureChanges
                                  ? 'bg-warning-500'
                                  : 'bg-danger-500'
                          }`}
                        />
                        {!hasQuadratureData
                          ? 'Sin datos'
                          : isQuadratureMatched
                            ? 'Cuadrada'
                            : hasPendingQuadratureChanges
                              ? 'Resuelta · falta aplicar'
                              : 'No cuadrada'}
                      </span>
                    </span>
                  }
                />
                <Tab
                  key='history'
                  title={
                    <span className='flex items-center gap-2'>
                      <span>Bitácora</span>
                      <span className='inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-default-100 px-1.5 text-[11px] font-semibold tabular-nums text-default-500'>
                        {supportTabCounts.history}
                      </span>
                    </span>
                  }
                />
                <Tab
                  key='documents'
                  title={
                    <span className='flex items-center gap-2'>
                      <span>Documentos</span>
                      <span className='inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-default-100 px-1.5 text-[11px] font-semibold tabular-nums text-default-500'>
                        {supportTabCounts.documents}
                      </span>
                    </span>
                  }
                />
              </Tabs>

              <Popover
                placement='bottom-end'
                isOpen={isSinisterPopoverOpen}
                onOpenChange={setIsSinisterPopoverOpen}
              >
                <PopoverTrigger>
                  <Button
                    size='sm'
                    variant='flat'
                    aria-label={`Siniestro: ${formData.is_sinister ? 'marcado' : 'no marcado'}`}
                    title={sinisterSummary}
                    className={`h-10 shrink-0 rounded-full border px-3 ${
                      formData.is_sinister
                        ? 'border-warning-500/50 bg-warning-500/10 text-warning-600 dark:text-warning-400'
                        : 'border-divider bg-transparent text-default-500'
                    }`}
                    startContent={<Icon icon='tabler:alert-triangle' />}
                  >
                    <span className='font-semibold'>Siniestro</span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                        formData.is_sinister
                          ? 'bg-warning-500/20 text-warning-700 dark:text-warning-300'
                          : 'bg-default-100 text-default-500'
                      }`}
                    >
                      {formData.is_sinister ? 'Marcado' : 'No marcado'}
                    </span>
                    {hasSinisterChanges && (
                      <span
                        aria-label='Cambios pendientes'
                        className='h-2 w-2 rounded-full bg-primary'
                      />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className='w-[390px] max-w-[calc(100vw-2rem)] p-0'>
                  <div className='w-full'>
                    <div className='flex items-start justify-between gap-4 border-b border-divider px-4 py-3'>
                      <div>
                        <p className='font-semibold'>Siniestro</p>
                        <p className='mt-0.5 text-xs text-default-500'>
                          Consulta o modifica los datos asociados.
                        </p>
                      </div>
                      <Switch
                        size='sm'
                        isSelected={!!formData.is_sinister}
                        onValueChange={(value) =>
                          setFormData({
                            ...formData,
                            is_sinister: value,
                            sinister_responsible: value
                              ? formData.sinister_responsible || null
                              : null,
                            sinister_delay_days: value
                              ? formData.sinister_delay_days || null
                              : null,
                            sinister_case_number: value
                              ? formData.sinister_case_number || null
                              : null,
                          })
                        }
                        isDisabled={settlement?.is_blocked}
                      >
                        <span className='text-xs font-medium'>
                          {formData.is_sinister ? 'Marcado' : 'No marcado'}
                        </span>
                      </Switch>
                    </div>

                    {formData.is_sinister ? (
                      <div className='space-y-3 px-4 py-4'>
                        <Select
                          size='sm'
                          label={t('Responsable del siniestro')}
                          placeholder={t('Selecciona un responsable')}
                          selectedKeys={
                            formData.sinister_responsible
                              ? [formData.sinister_responsible]
                              : []
                          }
                          onChange={(event) =>
                            setFormData({
                              ...formData,
                              sinister_responsible: event.target.value || null,
                            })
                          }
                          isRequired
                          isDisabled={settlement?.is_blocked}
                        >
                          {SINISTER_RESPONSIBLE_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option}>
                              {t(option)}
                            </SelectItem>
                          ))}
                        </Select>
                        <div className='grid grid-cols-2 gap-3'>
                          <Input
                            size='sm'
                            type='number'
                            min={0}
                            label='Días de retraso'
                            value={
                              formData.sinister_delay_days != null
                                ? String(formData.sinister_delay_days)
                                : ''
                            }
                            onChange={(event) => {
                              const value = event.target.value;
                              const parsedValue =
                                value === '' ? null : Number(value);
                              setFormData({
                                ...formData,
                                sinister_delay_days:
                                  parsedValue != null &&
                                  !isNaN(parsedValue) &&
                                  parsedValue >= 0
                                    ? parsedValue
                                    : null,
                              });
                            }}
                            isDisabled={settlement?.is_blocked}
                          />
                          <Input
                            size='sm'
                            label={t('ID del caso')}
                            placeholder={t('Opcional')}
                            value={formData.sinister_case_number || ''}
                            onChange={(event) =>
                              setFormData({
                                ...formData,
                                sinister_case_number:
                                  event.target.value.trim() || null,
                              })
                            }
                            isDisabled={settlement?.is_blocked}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className='px-4 py-5 text-sm text-default-500'>
                        Esta liquidación no está marcada como siniestro.
                      </div>
                    )}

                    <div className='flex items-center justify-between border-t border-divider px-4 py-3'>
                      <span className='text-xs text-default-400'>
                        {hasSinisterChanges
                          ? 'Cambios pendientes de guardar'
                          : 'Sin cambios pendientes'}
                      </span>
                      <Button
                        size='sm'
                        variant='flat'
                        onPress={() => setIsSinisterPopoverOpen(false)}
                      >
                        Listo
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <ModalBody className='min-h-0 overflow-hidden bg-default-50/30 px-6 py-5'>
            <div
              className={`grid h-full min-h-0 gap-4 ${
                activeTab === 'income_costs' &&
                originalDocument &&
                isOriginalPreviewOpen
                  ? 'grid-rows-[minmax(0,1fr)_minmax(260px,45%)] lg:grid-cols-[minmax(0,1fr)_minmax(420px,42%)] lg:grid-rows-1'
                  : 'grid-cols-1'
              }`}
            >
              <div className='min-h-0 min-w-0 space-y-5 overflow-y-auto pr-1'>
                {activeTab === 'quadrature' && settlement && (
                  <SettlementQuadratureManager
                    settlement={settlement}
                    details={
                      (formData.settlement_details || []) as SettlementDetails[]
                    }
                    shipmentBoxes={mappedShipments}
                    onApplied={handleQuadratureApplied}
                    onPendingChangesChange={setHasPendingQuadratureChanges}
                    onMapFormats={() => void handleOpenFormatMapping()}
                    formatsLoading={formatMappingLoading}
                  />
                )}
                {activeTab === 'general' && (
                  <>
                    <section className=''>
                      <div className='mb-5 flex min-h-10 items-center gap-3'>
                        <h2 className='text-sm font-semibold uppercase tracking-[0.12em] text-default-500'>
                          Info Comercial
                        </h2>
                        <Divider className='flex-1' />
                      </div>
                      <div className='grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2 xl:grid-cols-6'>
                        <Select
                          label='Especie'
                          isDisabled={settlement?.is_blocked}
                          selectedKeys={[formData.species_id?.toString() || '']}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              species_id: parseInt(e.target.value),
                            })
                          }
                          //   description="Especie de fruta seleccionada en la carga de Excel"
                        >
                          {species.map((species) => (
                            <SelectItem
                              key={species.id}
                              value={species.id.toString()}
                            >
                              {species.name}
                            </SelectItem>
                          ))}
                        </Select>
                        <Select
                          label='Temporada'
                          isDisabled={settlement?.is_blocked}
                          selectedKeys={
                            formData.season ? [formData.season] : []
                          }
                          onChange={(e) =>
                            setFormData({ ...formData, season: e.target.value })
                          }
                          description={getSeasonMismatchMessageLocal()}
                          color={
                            !isSeasonDateAlignedLocal() ? 'warning' : 'default'
                          }
                        >
                          {Object.values(SEASONS).map((season) => (
                            <SelectItem key={season.name} value={season.name}>
                              {season.name}
                            </SelectItem>
                          ))}
                        </Select>
                        <Autocomplete
                          label='Cliente'
                          selectedKey={
                            formData.exporter_receiver_id
                              ? formData.exporter_receiver_id.toString()
                              : null
                          }
                          onSelectionChange={(key) => {
                            const receiver = exporterReceivers.find(
                              (item) => item.id.toString() === key?.toString()
                            );
                            setFormData({
                              ...formData,
                              exporter_receiver_id: receiver?.id || null,
                              receiver: receiver?.name || '',
                              legacy_receiver:
                                formData.legacy_receiver || formData.receiver,
                            });
                          }}
                          isDisabled={settlement?.is_blocked}
                          // isRequired
                          description={
                            formData.legacy_receiver &&
                            normalizeReceiverName(formData.legacy_receiver) !==
                              normalizeReceiverName(formData.receiver)
                              ? `Nombre original: ${formData.legacy_receiver}`
                              : !formData.exporter_receiver_id
                                ? `Sin cliente oficial. Nombre actual: ${formData.receiver || '-'}`
                                : undefined
                          }
                          color={
                            !formData.exporter_receiver_id
                              ? 'warning'
                              : 'default'
                          }
                        >
                          {exporterReceivers.map((receiver) => (
                            <AutocompleteItem
                              key={receiver.id.toString()}
                              textValue={receiver.name}
                            >
                              {receiver.name}
                            </AutocompleteItem>
                          ))}
                        </Autocomplete>

                        <Input
                          label='Número de Referencia'
                          value={formData.reference_number}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              reference_number: e.target.value,
                            })
                          }
                          onBlur={() =>
                            setFormData({
                              ...formData,
                              reference_number:
                                formData.reference_number?.trim(),
                            })
                          }
                          isDisabled={settlement?.is_blocked}
                        />

                        <Input
                          label='Número de Contenedor'
                          value={formData.container_number}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              container_number: e.target.value,
                            })
                          }
                          onBlur={() =>
                            setFormData({
                              ...formData,
                              container_number:
                                formData.container_number?.trim(),
                            })
                          }
                          isDisabled={settlement?.is_blocked}
                        />
                        <Input
                          label='Fecha de Venta'
                          type='date'
                          isDisabled={settlement?.is_blocked}
                          value={formData.date_sold?.split('T')[0]}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              date_sold: e.target.value,
                            })
                          }
                          description={getSeasonMismatchMessageLocal()}
                          color={
                            !isSeasonDateAlignedLocal() ? 'warning' : 'default'
                          }
                        />

                        <Select
                          label='Mercado Venta'
                          selectedKeys={
                            formData.market_id ? [formData.market_id] : []
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              market_id: e.target.value,
                            })
                          }
                          isDisabled={settlement?.is_blocked}
                        >
                          {markets.map((market) => (
                            <SelectItem
                              key={market.id}
                              value={market.id}
                            >{`${market.name}, ${market.country}`}</SelectItem>
                          ))}
                        </Select>
                        <Select
                          label='Mercado Comparable'
                          selectedKeys={
                            formData.comparable_market_id
                              ? [formData.comparable_market_id]
                              : []
                          }
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              comparable_market_id: e.target.value,
                            })
                          }
                          isDisabled={settlement?.is_blocked}
                          description='Para hacer comparaciones de precios de venta.'
                        >
                          {[
                            {
                              name: 'SHANGHAI',
                              id: 'CNSHA',
                              country: 'CHINA',
                              country_code: 'CN',
                            },
                            {
                              name: 'GUANGZHOU',
                              id: 'CNCAN',
                              country: 'CHINA',
                              country_code: 'CN',
                            },
                          ].map((market) => (
                            <SelectItem
                              key={market.id}
                              value={market.id}
                            >{`${market.name}, CHINA`}</SelectItem>
                          ))}
                        </Select>

                        <Select
                          label='Moneda Local'
                          selectedKeys={
                            formData.local_currency
                              ? [formData.local_currency]
                              : []
                          }
                          onChange={(e) => {
                            const newCurrency = e.target.value;
                            setFormData({
                              ...formData,
                              local_currency: newCurrency,
                              settlement_details:
                                formData.settlement_details?.map((detail) => ({
                                  ...detail,
                                  local_currency: newCurrency,
                                })),
                            });
                          }}
                          isDisabled={settlement?.is_blocked}
                        >
                          {currencies.map((currency) => (
                            <SelectItem
                              key={currency.value}
                              value={currency.value}
                            >
                              {currency.name}
                            </SelectItem>
                          ))}
                        </Select>
                        <Input
                          label='Incoterm Original'
                          value={formData.original_incoterm}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              original_incoterm: e.target.value,
                            })
                          }
                          isDisabled={settlement?.is_blocked}
                        />
                        <Autocomplete
                          label='Tipo de Negocio'
                          inputValue={formData.business_type ?? ''}
                          onInputChange={(value) =>
                            setFormData({
                              ...formData,
                              business_type: (value || '').toUpperCase(),
                            })
                          }
                          onSelectionChange={(key) =>
                            setFormData({
                              ...formData,
                              business_type: (
                                key?.toString() || ''
                              ).toUpperCase(),
                            })
                          }
                          onBlur={() => {
                            const v = (formData.business_type || '')
                              .trim()
                              .toUpperCase();
                            if (v && !businessTypeOptions.includes(v)) {
                              setBusinessTypeOptions((prev) =>
                                [...prev, v].sort()
                              );
                            }
                          }}
                          allowsCustomValue
                          placeholder='Selecciona o escribe uno nuevo'
                          isDisabled={settlement?.is_blocked}
                          description='Por ejemplo: Mínimo Garantizado, Libre Consignación, En Firme, etc.'
                          defaultItems={businessTypeOptions.map((bt) => ({
                            key: bt,
                            name: bt,
                          }))}
                        >
                          {(item) => (
                            <AutocompleteItem key={item.key} value={item.key}>
                              {item.name}
                            </AutocompleteItem>
                          )}
                        </Autocomplete>
                        <Input
                          label='Orden de Venta'
                          value={formData.sale_order ?? ''}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              sale_order: e.target.value.toUpperCase(),
                            })
                          }
                          isDisabled={settlement?.is_blocked}
                          description='Útil si agrupas varias liquidaciones/contenedores en una sola orden de venta.'
                        />

                        {formData.local_currency &&
                          formData.local_currency.toUpperCase() !== 'RMB' && (
                            <Alert variant='warning' className='col-span-full'>
                              <span className='flex items-center gap-3'>
                                <Icon
                                  icon='tabler:alert-triangle'
                                  className='shrink-0'
                                />
                                <span>
                                  Al no ser RMB, no se calcularán comparaciones
                                  vs mercado para esta liquidación.
                                </span>
                              </span>
                            </Alert>
                          )}

                        {/* <div className="col-span-2">
                    <Select
                      label='Nave y Viaje'
                      selectedKeys={[selectedShipTripId]}
                      onSelectionChange={(keys) => {
                        const newId = Array.from(keys)[0]?.toString() || "";
                        setSelectedShipTripId(newId);
                        setFormData({
                          ...formData,
                          ship_trip_id: newId ? Number(newId) : undefined
                        });
                      }}
                      items={shipTrips.sort((a, b) => a.ship.name.localeCompare(b.ship.name))}
                      placeholder="Buscar por nave o fecha..."
                      classNames={{
                        trigger: "min-h-unit-12",
                        value: "text-small",
                      }}
                      isDisabled={settlement?.is_blocked}
                      description={<div className="flex justify-end items-center">
                        <Button
                          size="sm"
                          variant="flat"
                          onPress={() => setIsAirModalOpen(true)}
                          startContent={<Icon icon='tabler:plus' />}
                          isDisabled={settlement?.is_blocked}
                        >
                          Asociar AEREO O TERRESTRE
                        </Button>
                      </div>}
                    >
                      {(trip) => (
                        <SelectItem
                          key={trip.id}
                          textValue={`${trip.ship.name} - Arribo: ${formatDate(trip.eta, 'dd MMMM yyyy')} a ${trip.market?.name || 'No definido'}`}
                        >
                          <div className="flex flex-col">
                            <span className="text-small">{trip.ship.name}</span>
                            <span className="text-tiny text-default-400">
                              ETD: {formatDate(trip.etd, 'dd MMMM yyyy')} -
                              ETA: {formatDate(trip.eta, 'dd MMMM yyyy')} -
                              POD: {trip.market?.name || 'No definido'}
                            </span>
                          </div>
                        </SelectItem>
                      )}
                    </Select>
                  </div> */}
                      </div>
                    </section>

                    {/* Transporte Section */}
                    <section className=''>
                      <div className='mb-5 flex min-h-10 items-center gap-3'>
                        <h2 className='text-sm font-semibold uppercase tracking-[0.12em] text-default-500'>
                          Transporte
                        </h2>
                        <Divider className='flex-1' />
                      </div>
                      {transportMessage && (
                        <div
                          className='bg-yellow-100 dark:bg-yellow-950 border border-yellow-400 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300 px-4 py-3 rounded relative mb-4'
                          role='alert'
                        >
                          <span className='block sm:inline whitespace-pre-line'>
                            {transportMessage}
                          </span>
                        </div>
                      )}
                      <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
                        <div className='col-span-1'>
                          <TransportTypeSelector
                            value={transportType}
                            onChange={(type) => {
                              showOriginalTransportData();
                              setTransportType(type);
                              setSelectedShipId('');
                              setSelectedShipTripId('');
                              setFormData({
                                ...formData,
                                ship_trip_id: undefined,
                              });
                            }}
                            isDisabled={settlement?.is_blocked}
                          />
                        </div>

                        {transportType === 'M' && (
                          <>
                            <div className='flex flex-col gap-2'>
                              <label className='text-sm font-medium text-default-600'>
                                Nave
                              </label>
                              <Select
                                selectedKeys={
                                  selectedShipId ? [selectedShipId] : []
                                }
                                onSelectionChange={(keys) => {
                                  showOriginalTransportData();
                                  const newShipId =
                                    Array.from(keys)[0]?.toString() || '';
                                  setSelectedShipId(newShipId);
                                  setSelectedShipTripId(''); // Reset trip selection
                                  setFormData({
                                    ...formData,
                                    ship_trip_id: undefined,
                                  });
                                }}
                                items={ships.filter((s) => s.type === 'M')}
                                isDisabled={settlement?.is_blocked}
                              >
                                {(ship) => (
                                  <SelectItem
                                    key={ship.id}
                                    value={ship.id.toString()}
                                  >
                                    {ship.name}
                                  </SelectItem>
                                )}
                              </Select>
                            </div>
                            <div className='flex flex-col gap-2 col-span-2'>
                              <label className='text-sm font-medium text-default-600'>
                                Viaje
                              </label>
                              <Select
                                // label="Viaje"
                                selectedKeys={
                                  selectedShipTripId ? [selectedShipTripId] : []
                                }
                                onSelectionChange={(keys) => {
                                  showOriginalTransportData();
                                  const newTripId =
                                    Array.from(keys)[0]?.toString() || '';
                                  setSelectedShipTripId(newTripId);
                                  setFormData({
                                    ...formData,
                                    ship_trip_id: newTripId
                                      ? Number(newTripId)
                                      : undefined,
                                  });
                                }}
                                items={shipTrips.filter(
                                  (t) =>
                                    t.ship_id?.toString() === selectedShipId
                                )}
                                isDisabled={
                                  !selectedShipId || settlement?.is_blocked
                                }
                                placeholder='Seleccione un viaje'
                              >
                                {(trip) => (
                                  <SelectItem
                                    key={trip.id}
                                    textValue={`ETD: ${formatDate(trip.etd, 'dd MMMM yyyy')}, ETA: ${formatDate(trip.eta, 'dd MMMM yyyy')}, Destino: ${trip.market?.name || 'No definido'}`}
                                  >
                                    <div className='flex flex-col'>
                                      <span className='text-small'>
                                        {trip.ship.name}
                                      </span>
                                      <span className='text-tiny text-default-400'>
                                        ETD:{' '}
                                        {formatDate(trip.etd, 'dd MMMM yyyy')} -
                                        ETA:{' '}
                                        {formatDate(trip.eta, 'dd MMMM yyyy')} -
                                        Destino:{' '}
                                        {trip.market?.name || 'No definido'}
                                      </span>
                                    </div>
                                  </SelectItem>
                                )}
                              </Select>
                            </div>
                          </>
                        )}

                        {(transportType === 'A' || transportType === 'T') && (
                          <div className='col-span-3 grid grid-cols-3 gap-4 rounded-lg'>
                            <div className='flex flex-col gap-2'>
                              <label className='text-sm font-medium text-default-600'>
                                Fecha de Salida (ETD)
                              </label>
                              <Input
                                // label="Fecha de salida (ETD)"
                                type='date'
                                value={airShipmentData.etd}
                                onChange={(e) => {
                                  showOriginalTransportData();
                                  setAirShipmentData({
                                    ...airShipmentData,
                                    etd: e.target.value,
                                  });
                                }}
                                isDisabled={settlement?.is_blocked}
                              />
                            </div>
                            <div className='flex flex-col gap-2'>
                              <label className='text-sm font-medium text-default-600'>
                                Fecha de Llegada (ETA)
                              </label>
                              <Input
                                // label="Fecha de llegada (ETA)"
                                type='date'
                                value={airShipmentData.eta}
                                onChange={(e) => {
                                  showOriginalTransportData();
                                  setAirShipmentData({
                                    ...airShipmentData,
                                    eta: e.target.value,
                                  });
                                }}
                                isDisabled={settlement?.is_blocked}
                              />
                            </div>
                            <div className='flex flex-col gap-2'>
                              <label className='text-sm font-medium text-default-600'>
                                Lugar de Arribo
                              </label>
                              <Select
                                // label="Puerto de destino (POD)"
                                selectedKeys={[airShipmentData.pod]}
                                onChange={(e) => {
                                  showOriginalTransportData();
                                  setAirShipmentData({
                                    ...airShipmentData,
                                    pod: e.target.value,
                                  });
                                }}
                                isDisabled={settlement?.is_blocked}
                                description='Si falta algún puerto, escribe a CherryMarket para agregarlo.'
                              >
                                {markets.map((market) => (
                                  <SelectItem
                                    key={market.id}
                                    value={market.id.toString()}
                                  >{`${market.name}, ${market.country}`}</SelectItem>
                                ))}
                              </Select>
                            </div>
                            {/* <div className="col-span-3 flex justify-end">
                          <Button 
                            color="primary" 
                            onPress={handleCreateAirShipment} 
                            isLoading={loading}
                            isDisabled={settlement?.is_blocked}
                            startContent={<Icon icon='tabler:check' />}
                          >
                            Guardar y Asociar {transportType === 'A' ? 'Aéreo' : 'Terrestre'}
                          </Button>
                        </div> */}
                          </div>
                        )}
                      </div>
                    </section>
                  </>
                )}

                {/* Sección Ingresos y Costos */}
                {activeTab === 'income_costs' && (
                  <section className='grid grid-cols-1 gap-0 md:grid-cols-3'>
                    {/* Tabla de Ingresos */}
                    <div className='col-span-1 md:col-span-3'>
                      <div className='flex flex-wrap items-center gap-x-3 gap-y-4 pb-5'>
                        <div className='contents'>
                          <h2 className='order-[1] text-sm font-semibold uppercase tracking-[0.12em] text-default-500'>
                            Ingresos
                          </h2>
                          <Button
                            size='sm'
                            variant='flat'
                            className='order-[7] h-10 px-4'
                            startContent={<Icon icon='tabler:plus' />}
                            isDisabled={settlement?.is_blocked}
                            onPress={onRevenueModalOpen}
                          >
                            Otros Ingresos
                          </Button>
                          {/* {otherRevenues.length > 0 && (
                            <div className='order-[2] rounded-lg bg-success-500/10 px-3 py-1.5 text-sm font-semibold text-success-600 dark:text-success-400'>
                              Otros Ingresos{' '}
                              {totalOtherRevenue.toLocaleString()}{' '}
                              {formData.local_currency}
                            </div>
                          )} */}
                          <span className='order-[3] min-w-4 flex-1' />
                        </div>
                        <div className='contents'>
                          <Tooltip
                            content={
                              allowedLabels.length > 0
                                ? `Etiquetas oficiales: ${allowedLabels
                                    .slice(0, 6)
                                    .join(', ')}${
                                    allowedLabels.length > 6
                                      ? ` y ${allowedLabels.length - 6} más`
                                      : ''
                                  }`
                                : 'Etiquetas oficiales: sin datos'
                            }
                          >
                            <span
                              className={`order-[10] rounded-lg px-3 py-1.5 text-xs font-medium ${labelStatusInfo.className}`}
                            >
                              {labelStatusInfo.text}
                            </span>
                          </Tooltip>
                          <Tooltip
                            content={
                              allowedVarieties.length > 0
                                ? `Variedades oficiales: ${allowedVarieties
                                    .slice(0, 6)
                                    .join(', ')}${
                                    allowedVarieties.length > 6
                                      ? ` y ${allowedVarieties.length - 6} más`
                                      : ''
                                  }`
                                : 'Variedades oficiales: sin datos'
                            }
                          >
                            <span
                              className={`order-[11] rounded-lg px-3 py-1.5 text-xs font-medium ${varietyStatusInfo.className}`}
                            >
                              {varietyStatusInfo.text}
                            </span>
                          </Tooltip>
                          {detailValuesStatusInfo && (
                            <span
                              className={`order-[12] rounded-lg px-3 py-1.5 text-xs font-medium ${detailValuesStatusInfo.className}`}
                            >
                              {detailValuesStatusInfo.text}
                            </span>
                          )}
                          <Button
                            size='sm'
                            variant='flat'
                            className='order-[5] h-10 px-4'
                            onPress={() =>
                              setShowMarketComments((prev) => !prev)
                            }
                            isDisabled={
                              isLoadingMarketComments ||
                              marketComments.length === 0
                            }
                            title={
                              marketComments.length === 0
                                ? 'No hay comentarios para mostrar'
                                : showMarketComments
                                  ? 'Ocultar bitácora'
                                  : 'Mostrar bitácora'
                            }
                          >
                            Bitácora de Mercado
                            {marketComments.length > 0 && (
                              <span className='ml-2 inline-flex items-center justify-center rounded-full bg-background/60 px-2 py-0.5 text-xs font-semibold text-default-600'>
                                {marketComments.length}
                              </span>
                            )}
                          </Button>
                          <Button
                            size='sm'
                            color='primary'
                            className='order-[8] h-10 px-4'
                            onPress={() => handleAddNewDetail()}
                            isDisabled={
                              isAddingNewDetail ||
                              settlement?.is_blocked ||
                              copyMode !== null
                            }
                            startContent={<Icon icon='tabler:plus' />}
                          >
                            Línea de Detalle
                          </Button>
                          <Switch
                            size='sm'
                            isSelected={showCostColumns}
                            onValueChange={setShowCostColumns}
                            className='order-[14]'
                          >
                            <span className='text-sm'>Costos por detalle</span>
                          </Switch>
                          <div className='order-[4] flex min-w-0 items-center'>
                            {originalDocumentLoading ? (
                              <Tooltip content='Cargando archivo original'>
                                <span className='flex h-8 w-8 items-center justify-center'>
                                  <Spinner size='sm' />
                                </span>
                              </Tooltip>
                            ) : originalDocumentError ? (
                              <Tooltip content={originalDocumentError}>
                                <span className='px-2 text-xs text-danger'>
                                  Archivo no disponible
                                </span>
                              </Tooltip>
                            ) : originalDocument ? (
                              <Button
                                size='sm'
                                variant='flat'
                                className='h-10 px-4 text-default-500'
                                startContent={<Icon icon='tabler:file' />}
                                onPress={() =>
                                  setIsOriginalPreviewOpen(
                                    (currentValue) => !currentValue
                                  )
                                }
                              >
                                {isOriginalPreviewOpen
                                  ? 'Ocultar archivo'
                                  : 'Archivo original'}
                              </Button>
                            ) : (
                              <Tooltip content='Esta liquidación no conserva un archivo original'>
                                <span className='px-2 text-xs text-default-400'>
                                  Sin archivo original
                                </span>
                              </Tooltip>
                            )}
                          </div>
                          <span className='order-[6] hidden h-8 w-px bg-divider lg:block' />
                          <div className='order-[9] h-px basis-full bg-divider' />
                          <span className='order-[13] min-w-4 flex-1' />
                        </div>
                      </div>
                      <div
                        className={`grid gap-6 ${
                          showMarketComments
                            ? 'grid-cols-1 xl:grid-cols-[minmax(0,1fr)_24rem]'
                            : 'grid-cols-1'
                        }`}
                      >
                        <div>
                          <div className='h-[clamp(18rem,46vh,32rem)] overflow-hidden border-t border-divider'>
                            <Table
                              key={settlement?.id}
                              removeWrapper
                              aria-label='Detalles de liquidación'
                              className='h-full'
                              classNames={{
                                base: 'h-full overflow-auto',
                                table: 'h-auto',
                                thead:
                                  'sticky top-0 z-30 bg-default-100 shadow-sm',
                                tbody: '',
                                tr: 'border-b border-divider',
                                th: 'sticky top-0 z-30 bg-default-100 font-semibold',
                              }}
                            >
                              <TableHeader>
                                <TableColumn>ID</TableColumn>
                                <TableColumn>ACCIONES</TableColumn>
                                <TableColumn
                                  className={copyMode ? '' : 'hidden'}
                                >
                                  SELECCIONAR
                                </TableColumn>
                                <TableColumn>PALLET</TableColumn>
                                <TableColumn>VARIEDAD</TableColumn>
                                <TableColumn
                                  width={104}
                                  className='w-[104px] min-w-[104px]'
                                >
                                  FORMATO
                                </TableColumn>
                                <TableColumn>CALIBRE</TableColumn>
                                <TableColumn>COLOR</TableColumn>
                                <TableColumn>ETIQUETA</TableColumn>
                                <TableColumn>CATEGORIA</TableColumn>
                                <TableColumn className='text-right'>
                                  PESO BULTO
                                </TableColumn>
                                <TableColumn className='text-right'>
                                  BULTOS
                                </TableColumn>
                                <TableColumn className='text-right'>
                                  KILOS
                                </TableColumn>
                                <TableColumn className='text-right'>
                                  PRECIO BULTO
                                </TableColumn>
                                <TableColumn className='text-right'>
                                  INGRESOS MONEDA LOCAL
                                </TableColumn>
                                <TableColumn
                                  className={`${showCostColumns ? '' : 'hidden'} text-right`}
                                >
                                  COMISIÓN/VTA
                                </TableColumn>
                                <TableColumn
                                  className={`${showCostColumns ? '' : 'hidden'} text-right`}
                                >
                                  TAX/VTA
                                </TableColumn>
                                <TableColumn
                                  className={`${showCostColumns ? '' : 'hidden'} text-right`}
                                >
                                  REPACKING/KG
                                </TableColumn>
                                <TableColumn
                                  className={`${showCostColumns ? '' : 'hidden'} text-right`}
                                >
                                  OTROS COSTOS/KG
                                </TableColumn>
                                <TableColumn
                                  className={`${showCostColumns ? '' : 'hidden'} text-right`}
                                >
                                  FLETE/KG
                                </TableColumn>
                              </TableHeader>
                              <TableBody
                                emptyContent={'No hay detalles disponibles'}
                              >
                                {/* New Detail Row (at top when adding new, not duplicating) */}
                                {isAddingNewDetail &&
                                  !duplicateSourceDetailId &&
                                  renderNewDetailRow()}

                                {/* Existing Details */}
                                {(formData.settlement_details || []).flatMap(
                                  (detail) => {
                                    const row = (
                                      <TableRow
                                        key={detail.id}
                                        className={`${
                                          copyMode
                                            ? `cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-950 ${selectedRowsForCopy.has(detail.id) ? 'bg-blue-100 dark:bg-blue-900 border-l-4 border-blue-500' : ''}${
                                                isRowAlreadyHasValue(
                                                  detail,
                                                  copyMode.field,
                                                  copyMode.value
                                                )
                                                  ? ' opacity-50 cursor-not-allowed'
                                                  : ''
                                              }`
                                            : ''
                                        }`}
                                        onClick={
                                          copyMode
                                            ? (e) => {
                                                // Only handle row clicks if not clicking on interactive elements
                                                if (
                                                  e.target instanceof
                                                    HTMLElement &&
                                                  !e.target.closest('button') &&
                                                  !e.target.closest('input') &&
                                                  !e.target.closest('select') &&
                                                  !e.target.closest(
                                                    '[role="checkbox"]'
                                                  )
                                                ) {
                                                  const isCurrentlySelected =
                                                    selectedRowsForCopy.has(
                                                      detail.id
                                                    );
                                                  const isDisabled =
                                                    isRowAlreadyHasValue(
                                                      detail,
                                                      copyMode.field,
                                                      copyMode.value
                                                    );

                                                  if (!isDisabled) {
                                                    handleRowSelectionForCopy(
                                                      detail.id,
                                                      !isCurrentlySelected
                                                    );
                                                  } else {
                                                    toast.info(
                                                      'Esta fila ya tiene el mismo valor'
                                                    );
                                                  }
                                                }
                                              }
                                            : undefined
                                        }
                                      >
                                        <TableCell>{detail.id}</TableCell>

                                        <TableCell>
                                          <div className='flex gap-1'>
                                            <Button
                                              isIconOnly
                                              size='sm'
                                              variant='light'
                                              color='primary'
                                              onClick={() =>
                                                handleDeleteDetail(detail.id)
                                              }
                                              isDisabled={
                                                loading ||
                                                (formData.settlement_details
                                                  ?.length || 0) <= 1 ||
                                                settlement?.is_blocked ||
                                                copyMode !== null
                                              }
                                              title={
                                                copyMode
                                                  ? 'No disponible en modo copia'
                                                  : settlement?.is_blocked
                                                    ? 'Liquidación bloqueada'
                                                    : (formData
                                                          .settlement_details
                                                          ?.length || 0) <= 1
                                                      ? 'No se puede eliminar el único detalle'
                                                      : 'Eliminar detalle'
                                              }
                                            >
                                              <Icon icon='tabler:x' />
                                            </Button>
                                            <Button
                                              isIconOnly
                                              size='sm'
                                              variant='light'
                                              color='primary'
                                              onClick={() =>
                                                handleDuplicateDetail(detail.id)
                                              }
                                              isDisabled={
                                                loading ||
                                                isAddingNewDetail ||
                                                settlement?.is_blocked ||
                                                copyMode !== null
                                              }
                                              title={
                                                copyMode
                                                  ? 'No disponible en modo copia'
                                                  : settlement?.is_blocked
                                                    ? 'Liquidación bloqueada'
                                                    : isAddingNewDetail
                                                      ? 'Complete el detalle actual antes de duplicar'
                                                      : 'Duplicar detalle'
                                              }
                                            >
                                              <Icon icon='tabler:copy' />
                                            </Button>
                                          </div>
                                        </TableCell>
                                        <TableCell
                                          className={copyMode ? '' : 'hidden'}
                                        >
                                          {copyMode && (
                                            <Checkbox
                                              size='sm'
                                              isSelected={selectedRowsForCopy.has(
                                                detail.id
                                              )}
                                              onChange={(isSelected) =>
                                                handleRowSelectionForCopy(
                                                  detail.id,
                                                  isSelected
                                                )
                                              }
                                              isDisabled={
                                                settlement?.is_blocked ||
                                                (copyMode
                                                  ? isRowAlreadyHasValue(
                                                      detail,
                                                      copyMode.field,
                                                      copyMode.value
                                                    )
                                                  : false)
                                              }
                                            />
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {renderEditableCell(
                                            detail.id,
                                            'pallet',
                                            detail.pallet || ''
                                          )}
                                        </TableCell>
                                        <TableCell
                                          className={
                                            isVarietyUnknown(detail.variety)
                                              ? 'bg-red-50 dark:bg-red-950'
                                              : undefined
                                          }
                                        >
                                          {renderEditableCell(
                                            detail.id,
                                            'variety',
                                            detail.variety
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {renderEditableCell(
                                            detail.id,
                                            'format',
                                            detail.format
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {renderEditableCell(
                                            detail.id,
                                            'caliber',
                                            detail.caliber
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {renderEditableCell(
                                            detail.id,
                                            'color',
                                            detail.color
                                          )}
                                        </TableCell>
                                        <TableCell
                                          className={
                                            isLabelUnknown(detail.label)
                                              ? 'bg-red-50 dark:bg-red-950'
                                              : undefined
                                          }
                                        >
                                          {renderEditableCell(
                                            detail.id,
                                            'label',
                                            detail.label
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          {renderEditableCell(
                                            detail.id,
                                            'category',
                                            detail.category || ''
                                          )}
                                        </TableCell>
                                        <TableCell
                                          className={`text-right tabular-nums ${!detail.unit_weight ? 'text-default-300' : ''}`}
                                        >
                                          {renderEditableCell(
                                            detail.id,
                                            'unit_weight',
                                            detail.unit_weight?.toString() || ''
                                          )}
                                        </TableCell>
                                        <TableCell
                                          className={`text-right tabular-nums ${!detail.quantity ? 'text-default-300' : ''}`}
                                        >
                                          {renderEditableCell(
                                            detail.id,
                                            'quantity',
                                            detail.quantity?.toString() || ''
                                          )}
                                        </TableCell>
                                        <TableCell
                                          className={`text-right tabular-nums ${hasSettlementKilosMismatch(detail) ? 'bg-red-50 text-danger dark:bg-red-950' : !detail.kilos ? 'text-default-300' : ''}`}
                                          title={
                                            hasSettlementKilosMismatch(detail)
                                              ? `Kilos esperados: ${Number(detail.unit_weight) * Number(detail.quantity)} (peso bulto × bultos)`
                                              : undefined
                                          }
                                        >
                                          {(detail.kilos || 0).toLocaleString()}
                                        </TableCell>
                                        <TableCell
                                          className={`text-right tabular-nums ${!detail.local_total_price ? 'text-default-300' : ''}`}
                                        >
                                          {renderEditableCell(
                                            detail.id,
                                            'price_per_bulto',
                                            detail.quantity > 0
                                              ? (
                                                  detail.local_total_price /
                                                  detail.quantity
                                                ).toString()
                                              : '0'
                                          )}
                                        </TableCell>
                                        <TableCell
                                          className={`text-right tabular-nums ${
                                            !(
                                              detail.local_total_price +
                                              (showWithRevenues
                                                ? revenueAdjustmentByDetail.get(
                                                    detail.id
                                                  ) || 0
                                                : 0)
                                            )
                                              ? 'text-default-300'
                                              : ''
                                          }`}
                                        >
                                          {showWithRevenues &&
                                          revenueAdjustmentByDetail.get(
                                            detail.id
                                          ) ? (
                                            <div>
                                              <div className='line-through text-default-300 text-sm'>
                                                {detail.local_total_price.toLocaleString()}{' '}
                                                {formData.local_currency}
                                              </div>
                                              <div className='font-medium text-green-600 dark:text-green-400'>
                                                {(
                                                  detail.local_total_price +
                                                  revenueAdjustmentByDetail.get(
                                                    detail.id
                                                  )!
                                                ).toLocaleString()}{' '}
                                                {formData.local_currency}
                                              </div>
                                            </div>
                                          ) : (
                                            <span>
                                              {detail.local_total_price.toLocaleString()}
                                            </span>
                                          )}
                                        </TableCell>
                                        <TableCell
                                          className={`${showCostColumns ? '' : 'hidden'} text-right tabular-nums ${!detail.local_total_commision ? 'text-default-300' : ''}`}
                                        >
                                          <span className='text-xs tabular-nums'>
                                            {detail.local_total_price > 0
                                              ? (
                                                  ((detail.local_total_commision ||
                                                    0) /
                                                    detail.local_total_price) *
                                                  100
                                                ).toFixed(2)
                                              : '0.00'}
                                            %
                                          </span>
                                        </TableCell>
                                        <TableCell
                                          className={`${showCostColumns ? '' : 'hidden'} text-right tabular-nums ${!detail.local_total_tax ? 'text-default-300' : ''}`}
                                        >
                                          <span className='text-xs tabular-nums'>
                                            {detail.local_total_price > 0
                                              ? (
                                                  ((detail.local_total_tax ||
                                                    0) /
                                                    detail.local_total_price) *
                                                  100
                                                ).toFixed(2)
                                              : '0.00'}
                                            %
                                          </span>
                                        </TableCell>
                                        <TableCell
                                          className={`${showCostColumns ? '' : 'hidden'} text-right tabular-nums ${!detail.local_repacking_costs ? 'text-default-300' : ''}`}
                                        >
                                          <span className='text-xs tabular-nums'>
                                            {detail.kilos > 0
                                              ? (
                                                  (detail.local_repacking_costs ||
                                                    0) / detail.kilos
                                                ).toFixed(2)
                                              : '0.00'}
                                          </span>
                                        </TableCell>
                                        <TableCell
                                          className={`${showCostColumns ? '' : 'hidden'} text-right tabular-nums ${!detail.local_other_costs ? 'text-default-300' : ''}`}
                                        >
                                          <span className='text-xs tabular-nums'>
                                            {detail.kilos > 0
                                              ? (
                                                  (detail.local_other_costs ||
                                                    0) / detail.kilos
                                                ).toFixed(2)
                                              : '0.00'}
                                          </span>
                                        </TableCell>
                                        <TableCell
                                          className={`${showCostColumns ? '' : 'hidden'} text-right tabular-nums ${!detail.usd_freight ? 'text-default-300' : ''}`}
                                        >
                                          <span className='text-xs tabular-nums'>
                                            {detail.kilos > 0
                                              ? (
                                                  (detail.usd_freight || 0) /
                                                  detail.kilos
                                                ).toFixed(2)
                                              : '0.00'}
                                          </span>
                                        </TableCell>
                                      </TableRow>
                                    );
                                    if (
                                      isAddingNewDetail &&
                                      duplicateSourceDetailId === detail.id
                                    ) {
                                      return [row, renderNewDetailRow()];
                                    }
                                    return [row];
                                  }
                                )}
                                {(formData.settlement_details || []).length >
                                  0 && renderDetailTotalsRow()}
                              </TableBody>
                            </Table>
                          </div>
                        </div>

                        {showMarketComments && (
                          <div className='rounded-lg border border-divider bg-default-100/50 p-4'>
                            <div className='mb-3'>
                              <div className='text-sm font-semibold text-default-600'>
                                Bitácora de Mercado
                              </div>
                              <p className='text-xs text-default-400'>
                                Coincidencias por referencia o contenedor.
                              </p>
                            </div>
                            <div className='grid gap-3'>
                              {isLoadingMarketComments && (
                                <LoadingState label='Cargando...' />
                              )}
                              {marketCommentsError && (
                                <Alert variant='danger'>
                                  {marketCommentsError}
                                </Alert>
                              )}
                              {!isLoadingMarketComments &&
                                !marketCommentsError &&
                                marketComments.map((comment) => {
                                  const referenceMatch = Boolean(
                                    normalizeMatchValue(comment.exporter_ref) &&
                                      normalizeMatchValue(
                                        comment.exporter_ref
                                      ) ===
                                        normalizeMatchValue(
                                          formData.reference_number as string
                                        )
                                  );
                                  const containerMatch = Boolean(
                                    normalizeMatchValue(
                                      comment.container_number
                                    ) &&
                                      normalizeMatchValue(
                                        comment.container_number
                                      ) ===
                                        normalizeMatchValue(
                                          formData.container_number as string
                                        )
                                  );
                                  const dateLabel =
                                    comment.date_to &&
                                    comment.date_to !== comment.date_from
                                      ? `${formatDate(comment.date_from, 'dd MMM yyyy')} → ${formatDate(comment.date_to, 'dd MMM yyyy')}`
                                      : formatDate(
                                          comment.date_from,
                                          'dd MMM yyyy'
                                        );
                                  const createdAt =
                                    comment.created_at?.split('T')[0] || '';
                                  const priceLabel =
                                    typeof comment.unit_price === 'number'
                                      ? `${comment.unit_price.toFixed(2)} ${comment.local_currency || ''}`.trim()
                                      : '—';
                                  return (
                                    <Card key={comment.id} shadow='sm'>
                                      <CardBody>
                                        <div className='flex w-full items-center justify-between text-xs text-default-500'>
                                          <div className='font-medium truncate'>
                                            {dateLabel}
                                          </div>
                                          <div className='text-xs text-default-400'>
                                            Mercado:{' '}
                                            {formatMarketValue(
                                              comment.market_id
                                            )}
                                          </div>
                                        </div>
                                        <div className='mt-1 text-xs text-default-400'>
                                          Coincidencia:{' '}
                                          {referenceMatch && containerMatch
                                            ? 'Referencia y Contenedor'
                                            : referenceMatch
                                              ? 'Referencia'
                                              : containerMatch
                                                ? 'Contenedor'
                                                : '—'}
                                        </div>
                                        {comment.content ? (
                                          <div className='mt-2 text-sm whitespace-pre-wrap'>
                                            {comment.content}
                                          </div>
                                        ) : (
                                          <div className='mt-2 text-sm italic text-default-500'>
                                            (Sin contenido)
                                          </div>
                                        )}
                                        <div className='mt-2 rounded-md border border-default-200 bg-default-50 p-2'>
                                          <div className='grid grid-cols-2 gap-x-4 gap-y-1 text-xs'>
                                            <div>
                                              <span className='text-default-500'>
                                                Especie ID:{' '}
                                              </span>
                                              <span className='font-medium'>
                                                {formatMarketValue(
                                                  comment.species_id ??
                                                    formData.species_id
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className='text-default-500'>
                                                Temporada:{' '}
                                              </span>
                                              <span className='font-medium'>
                                                {formatMarketValue(
                                                  comment.season ??
                                                    formData.season
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className='text-default-500'>
                                                Variedad:{' '}
                                              </span>
                                              <span className='font-medium'>
                                                {formatMarketValue(
                                                  comment.variety
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className='text-default-500'>
                                                Calibre:{' '}
                                              </span>
                                              <span className='font-medium'>
                                                {formatMarketValue(
                                                  comment.caliber
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className='text-default-500'>
                                                Cliente:{' '}
                                              </span>
                                              <span className='font-medium'>
                                                {formatMarketValue(
                                                  comment.seller
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className='text-default-500'>
                                                Etiqueta:{' '}
                                              </span>
                                              <span className='font-medium'>
                                                {formatMarketValue(
                                                  comment.label
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className='text-default-500'>
                                                Referencia:{' '}
                                              </span>
                                              <span className='font-medium'>
                                                {formatMarketValue(
                                                  comment.exporter_ref
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className='text-default-500'>
                                                Contenedor:{' '}
                                              </span>
                                              <span className='font-medium'>
                                                {formatMarketValue(
                                                  comment.container_number
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className='text-default-500'>
                                                Cantidad de bultos:{' '}
                                              </span>
                                              <span className='font-medium'>
                                                {formatMarketValue(
                                                  comment.unit_count
                                                )}
                                              </span>
                                            </div>
                                            <div>
                                              <span className='text-default-500'>
                                                Peso bulto:{' '}
                                              </span>
                                              <span className='font-medium'>
                                                {typeof comment.unit_weight ===
                                                'number'
                                                  ? `${comment.unit_weight} kg`
                                                  : '—'}
                                              </span>
                                            </div>
                                            <div>
                                              <span className='text-default-500'>
                                                Precio bulto:{' '}
                                              </span>
                                              <span className='font-medium'>
                                                {priceLabel}
                                              </span>
                                            </div>
                                            <div>
                                              <span className='text-default-500'>
                                                Moneda:{' '}
                                              </span>
                                              <span className='font-medium'>
                                                {formatMarketValue(
                                                  comment.local_currency
                                                )}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                        {comment.attachments &&
                                          comment.attachments.filter(
                                            (attachment) =>
                                              !attachment.deleted_at
                                          ).length > 0 && (
                                            <div className='mt-2 grid grid-cols-3 gap-2'>
                                              {comment.attachments
                                                .filter(
                                                  (attachment) =>
                                                    !attachment.deleted_at
                                                )
                                                .map((attachment) => (
                                                  <button
                                                    key={attachment.id}
                                                    type='button'
                                                    className='relative group h-20 w-20 rounded border overflow-hidden'
                                                    title='Ver adjunto'
                                                    onClick={async () => {
                                                      try {
                                                        if (
                                                          attachment.is_image
                                                        ) {
                                                          const url =
                                                            marketCommentImageUrls[
                                                              attachment.id
                                                            ] ||
                                                            (await getMarketCommentAttachmentSignedUrl(
                                                              attachment.bucket,
                                                              attachment.path,
                                                              300
                                                            ));
                                                          window.open(
                                                            url,
                                                            '_blank'
                                                          );
                                                        } else {
                                                          const url =
                                                            await getMarketCommentAttachmentSignedUrl(
                                                              attachment.bucket,
                                                              attachment.path,
                                                              120
                                                            );
                                                          window.open(
                                                            url,
                                                            '_blank'
                                                          );
                                                        }
                                                      } catch {}
                                                    }}
                                                  >
                                                    {attachment.is_image ? (
                                                      marketCommentImageUrls[
                                                        attachment.id
                                                      ] ? (
                                                        <img
                                                          src={
                                                            marketCommentImageUrls[
                                                              attachment.id
                                                            ]
                                                          }
                                                          alt={attachment.name}
                                                          className='h-full w-full object-cover'
                                                        />
                                                      ) : (
                                                        <div className='h-full w-full flex items-center justify-center text-[10px] bg-default-100'>
                                                          IMG
                                                        </div>
                                                      )
                                                    ) : (
                                                      <div className='h-full w-full flex items-center justify-center text-[10px] bg-default-100'>
                                                        {attachment.name
                                                          ?.split('.')
                                                          .pop()
                                                          ?.toUpperCase() ||
                                                          'FILE'}
                                                      </div>
                                                    )}
                                                    <div className='absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs flex items-center justify-center'>
                                                      Ver
                                                    </div>
                                                  </button>
                                                ))}
                                            </div>
                                          )}
                                        <Divider className='my-2' />
                                        <div className='text-xs text-default-400'>
                                          Creado el{' '}
                                          {createdAt
                                            ? formatDate(
                                                createdAt,
                                                'dd MMM yyyy'
                                              )
                                            : '—'}
                                        </div>
                                      </CardBody>
                                    </Card>
                                  );
                                })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Resumen de Costos */}
                    <div className='col-span-1 mt-8 md:col-span-3'>
                      <div className='mb-5 flex min-h-10 flex-wrap items-center gap-3'>
                        <h2 className='text-sm font-semibold uppercase tracking-[0.12em] text-default-500'>
                          Costos
                        </h2>
                        {exporterCostsAvailable && (
                          <CostStandardToggle
                            value={costStandard}
                            onChange={setCostStandard}
                          />
                        )}
                        <Divider className='min-w-8 flex-1' />
                        <div className='flex items-center gap-2'>
                          {sourceCostLinesLoading && <Spinner size='sm' />}
                          {(canEditCurrentSourceCosts ||
                            sourceCostLines.length > 0) && (
                            <Button
                              size='sm'
                              color='primary'
                              variant='light'
                              onPress={() => {
                                setSourceCostEditorCategory('all');
                                setIsSourceCostEditorOpen(true);
                              }}
                            >
                              {canEditCurrentSourceCosts ? 'Editar' : 'Ver'}{' '}
                              costos originales · {sourceCostLines.length}
                            </Button>
                          )}
                        </div>
                      </div>
                      {sourceCostLinesError && (
                        <Alert variant='warning' className='mb-3'>
                          {sourceCostLinesError}
                        </Alert>
                      )}
                      {showExporterCosts && (
                        <div className='space-y-4 p-4 border border-divider rounded-lg bg-default-100'>
                          <ExporterCostSummary
                            onCategoryPress={(categoryId) => {
                              setExporterCostEditorFilter(categoryId);
                              setSourceCostEditorCategory('all');
                              setIsSourceCostEditorOpen(true);
                            }}
                            exchangeRateField={renderEditableCostField(
                              'exchange_rate',
                              formData.exchange_rate ?? undefined,
                              ''
                            )}
                            lines={sourceCostLines}
                            categories={receiverExporterCosts.categories}
                            mappings={receiverExporterCosts.mappings}
                            categoryTotalsLocal={
                              sharedSourceCosts
                                ? savedExporterCosts.categoryTotals
                                : undefined
                            }
                            localCurrency={formData.local_currency || ''}
                            exchangeRate={Number(formData.exchange_rate) || 1}
                            revenueLocal={formData.total_revenue_local || 0}
                            fmCostsLocal={
                              (formData.total_commission_local || 0) +
                              (formData.total_tax_local || 0) +
                              (formData.total_other_costs_local || 0) +
                              (formData.total_repacking_costs_local || 0) +
                              (formData.total_freight_usd || 0) *
                                (formData.local_currency?.toUpperCase() ===
                                'USD'
                                  ? 1
                                  : Number(formData.exchange_rate) || 1)
                            }
                          />
                        </div>
                      )}
                      <div
                        className={`space-y-4 p-4 border border-divider rounded-lg bg-default-100 ${
                          showExporterCosts ? 'hidden' : ''
                        }`}
                      >
                        {/* Total Ingresos */}
                        <div className='flex justify-between items-center py-2 border-b border-divider'>
                          <span className='font-semibold text-green-600 dark:text-green-400'>
                            Total Ingresos:
                          </span>
                          {showWithRevenues && totalOtherRevenue !== 0 ? (
                            <div className='text-right'>
                              <div className='line-through text-default-300 text-sm'>
                                {(
                                  formData.total_revenue_local || 0
                                ).toLocaleString()}{' '}
                                {formData.local_currency}
                              </div>
                              <div className='font-bold text-green-600 dark:text-green-400'>
                                {(
                                  (formData.total_revenue_local || 0) +
                                  totalOtherRevenue
                                ).toLocaleString()}{' '}
                                {formData.local_currency}
                              </div>
                            </div>
                          ) : (
                            <span className='font-bold text-green-600 dark:text-green-400'>
                              {(
                                formData.total_revenue_local || 0
                              ).toLocaleString()}{' '}
                              {formData.local_currency}
                            </span>
                          )}
                        </div>

                        {/* Comisión */}
                        <div className='flex items-center gap-2'>
                          <label className='text-sm font-medium text-red-600 dark:text-red-400 w-20 flex-shrink-0'>
                            Comisión:
                          </label>
                          {renderEditableCostField(
                            'commission',
                            formData.total_commission_local,
                            formData.local_currency || ''
                          )}
                          <span className='text-xs text-default-400 ml-2'>
                            (
                            {(
                              ((formData.total_commission_local || 0) /
                                (formData.total_revenue_local || 1)) *
                              100
                            ).toFixed(2)}
                            %)
                          </span>
                        </div>

                        {/* Impuestos */}
                        <div className='flex items-center gap-2'>
                          <label className='text-sm font-medium text-red-600 dark:text-red-400 w-20 flex-shrink-0'>
                            Impuestos:
                          </label>
                          {renderEditableCostField(
                            'tax',
                            formData.total_tax_local,
                            formData.local_currency || ''
                          )}
                          <span className='text-xs text-default-400 ml-2'>
                            (
                            {(
                              ((formData.total_tax_local || 0) /
                                (formData.total_revenue_local || 1)) *
                              100
                            ).toFixed(2)}
                            %)
                          </span>
                        </div>

                        {/* Repacking */}
                        <div className='flex items-center gap-2'>
                          <label className='text-sm font-medium text-red-600 dark:text-red-400 w-20 flex-shrink-0'>
                            Repacking:
                          </label>
                          {renderEditableCostField(
                            'repacking',
                            formData.total_repacking_costs_local,
                            formData.local_currency || ''
                          )}
                        </div>

                        {/* Otros Costos */}
                        <div className='flex items-center gap-2'>
                          <label className='text-sm font-medium text-red-600 dark:text-red-400 w-20 flex-shrink-0'>
                            Otros:
                          </label>
                          {renderEditableCostField(
                            'otros',
                            formData.total_other_costs_local,
                            formData.local_currency || ''
                          )}
                        </div>

                        {/* Total Costos */}
                        <div className='flex justify-between items-center py-2 border-t border-divider'>
                          <span className='font-semibold text-red-600 dark:text-red-400'>
                            Total Costos:
                          </span>
                          <span className='font-bold text-red-600 dark:text-red-400'>
                            {(
                              (formData.total_commission_local || 0) +
                              (formData.total_tax_local || 0) +
                              (formData.total_other_costs_local || 0) +
                              (formData.total_repacking_costs_local || 0)
                            ).toLocaleString() +
                              ' ' +
                              formData.local_currency}
                          </span>
                        </div>

                        {/* Subtotal */}
                        <div className='flex justify-between items-center py-2 border-t border-divider'>
                          <span className='font-semibold'>
                            Subtotal Moneda Local:
                          </span>
                          {showWithRevenues && totalOtherRevenue !== 0 ? (
                            <div className='text-right'>
                              <div className='line-through text-default-300 text-sm'>
                                {(
                                  (formData.total_revenue_local || 0) -
                                  (formData.total_commission_local || 0) -
                                  (formData.total_tax_local || 0) -
                                  (formData.total_other_costs_local || 0) -
                                  (formData.total_repacking_costs_local || 0)
                                ).toLocaleString()}{' '}
                                {formData.local_currency}
                              </div>
                              <div className='font-bold text-green-600 dark:text-green-400'>
                                {(
                                  (formData.total_revenue_local || 0) +
                                  totalOtherRevenue -
                                  (formData.total_commission_local || 0) -
                                  (formData.total_tax_local || 0) -
                                  (formData.total_other_costs_local || 0) -
                                  (formData.total_repacking_costs_local || 0)
                                ).toLocaleString()}{' '}
                                {formData.local_currency}
                              </div>
                            </div>
                          ) : (
                            <span className='font-bold'>
                              {(
                                (formData.total_revenue_local || 0) -
                                (formData.total_commission_local || 0) -
                                (formData.total_tax_local || 0) -
                                (formData.total_other_costs_local || 0) -
                                (formData.total_repacking_costs_local || 0)
                              ).toLocaleString()}{' '}
                              {formData.local_currency}
                            </span>
                          )}
                        </div>

                        {/* Tipo de Cambio */}
                        <div className='flex items-center gap-2'>
                          <label className='text-sm font-medium w-20 flex-shrink-0'>
                            Tipo de Cambio:
                          </label>
                          {renderEditableCostField(
                            'exchange_rate',
                            formData.exchange_rate,
                            ''
                          )}
                          {/* <span className="text-xs text-default-400">
                        La edición del tipo de cambio tomará los precios locales para establecer los precios en USD y no viceversa.
                      </span> */}
                        </div>

                        {effectiveExchangeRate && (
                          <div className='flex justify-between items-center py-2 border-t border-divider'>
                            <div className='flex items-center gap-2'>
                              <span className='font-semibold text-amber-700 dark:text-amber-300'>
                                Tipo de Cambio Efectivo:
                              </span>
                              <Tooltip content='Tasa ponderada resultante al combinar el subtotal original con otros ingresos convertidos a un tipo de cambio distinto.'>
                                <span className='text-xs text-default-400 cursor-help'>
                                  ¿por qué?
                                </span>
                              </Tooltip>
                            </div>
                            <div className='text-right'>
                              <div className='font-bold text-amber-700 dark:text-amber-300'>
                                {effectiveExchangeRate.toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits: 4,
                                    maximumFractionDigits: 4,
                                  }
                                )}
                              </div>
                              <div className='text-xs text-default-400'>
                                Original:{' '}
                                {(formData.exchange_rate || 0).toLocaleString(
                                  undefined,
                                  {
                                    minimumFractionDigits: 4,
                                    maximumFractionDigits: 4,
                                  }
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Subtotal USD */}
                        <div className='flex justify-between items-center py-2 border-t border-divider'>
                          <span className='font-semibold'>Subtotal USD:</span>
                          {showWithRevenues && totalOtherRevenueUsd !== 0 ? (
                            <div className='text-right'>
                              <div className='line-through text-default-300 text-sm'>
                                {(
                                  ((formData.total_revenue_local || 0) -
                                    (formData.total_commission_local || 0) -
                                    (formData.total_tax_local || 0) -
                                    (formData.total_other_costs_local || 0) -
                                    (formData.total_repacking_costs_local ||
                                      0)) /
                                  (formData.exchange_rate || 1)
                                ).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{' '}
                                USD
                              </div>
                              <div className='font-bold text-green-600 dark:text-green-400'>
                                {(
                                  ((formData.total_revenue_local || 0) -
                                    (formData.total_commission_local || 0) -
                                    (formData.total_tax_local || 0) -
                                    (formData.total_other_costs_local || 0) -
                                    (formData.total_repacking_costs_local ||
                                      0)) /
                                    (formData.exchange_rate || 1) +
                                  totalOtherRevenueUsd
                                ).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{' '}
                                USD
                              </div>
                            </div>
                          ) : (
                            <span className='font-bold'>
                              {(
                                ((formData.total_revenue_local || 0) -
                                  (formData.total_commission_local || 0) -
                                  (formData.total_tax_local || 0) -
                                  (formData.total_other_costs_local || 0) -
                                  (formData.total_repacking_costs_local || 0)) /
                                (formData.exchange_rate || 1)
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{' '}
                              USD
                            </span>
                          )}
                        </div>

                        {/* Flete USD */}
                        <div className='flex items-center gap-2'>
                          <label className='text-sm font-medium text-blue-600 w-20 flex-shrink-0'>
                            Flete:
                          </label>
                          {renderEditableCostField(
                            'freight',
                            formData.total_freight_usd,
                            'USD'
                          )}
                        </div>

                        {/* FOB Final */}
                        <div className='flex justify-between items-center py-2 border-t-2 border-default-400 bg-blue-50 dark:bg-blue-950 px-2 rounded'>
                          <span className='font-bold text-blue-700 dark:text-blue-300'>
                            FOB Total:
                          </span>
                          {showWithRevenues && totalOtherRevenueUsd !== 0 ? (
                            <div className='text-right'>
                              <div className='line-through text-default-300 text-sm'>
                                {(
                                  ((formData.total_revenue_local || 0) -
                                    (formData.total_commission_local || 0) -
                                    (formData.total_tax_local || 0) -
                                    (formData.total_other_costs_local || 0) -
                                    (formData.total_repacking_costs_local ||
                                      0)) /
                                    (formData.exchange_rate || 1) -
                                  (formData.total_freight_usd || 0)
                                ).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{' '}
                                USD
                              </div>
                              <div className='font-bold text-green-600 dark:text-green-400 text-lg'>
                                {(
                                  ((formData.total_revenue_local || 0) -
                                    (formData.total_commission_local || 0) -
                                    (formData.total_tax_local || 0) -
                                    (formData.total_other_costs_local || 0) -
                                    (formData.total_repacking_costs_local ||
                                      0)) /
                                    (formData.exchange_rate || 1) +
                                  totalOtherRevenueUsd -
                                  (formData.total_freight_usd || 0)
                                ).toLocaleString(undefined, {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}{' '}
                                USD
                              </div>
                            </div>
                          ) : (
                            <span className='font-bold text-blue-700 dark:text-blue-300 text-lg'>
                              {(
                                ((formData.total_revenue_local || 0) -
                                  (formData.total_commission_local || 0) -
                                  (formData.total_tax_local || 0) -
                                  (formData.total_other_costs_local || 0) -
                                  (formData.total_repacking_costs_local || 0)) /
                                  (formData.exchange_rate || 1) -
                                (formData.total_freight_usd || 0)
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{' '}
                              USD
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>
                )}
                {activeTab === 'history' && settlement && (
                  <SettlementEditSupportPanel
                    settlement={settlement}
                    mode='history'
                    onChange={() => void refreshSupportTabCounts()}
                  />
                )}
                {activeTab === 'documents' && settlement && (
                  <SettlementEditSupportPanel
                    settlement={settlement}
                    mode='documents'
                    onChange={() => {
                      void refreshOriginalDocument();
                      void refreshSupportTabCounts();
                    }}
                  />
                )}
              </div>
              {activeTab === 'income_costs' &&
                originalDocument &&
                isOriginalPreviewOpen && (
                  <SettlementSourcePreview
                    remoteDocument={{
                      fileName: originalDocument.fileName,
                      url: originalDocument.url,
                    }}
                    onClose={() => setIsOriginalPreviewOpen(false)}
                  />
                )}
            </div>
          </ModalBody>

          {/* Floating Copy Confirmation Button */}
          {copyMode && (
            <div className='fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-background border border-divider rounded-lg shadow-lg p-4'>
              <div className='flex items-center gap-4'>
                <div className='text-sm'>
                  <span className='font-medium'>Copiando:</span> "
                  {copyMode.value}"
                  <span className='text-default-400 ml-2'>
                    en {COPY_FIELD_LABELS[copyMode.field] || ''}
                  </span>
                </div>
                <div className='text-sm font-medium text-blue-600'>
                  {selectedRowsForCopy.size} filas seleccionadas
                </div>
                <div className='flex gap-2'>
                  <Button
                    size='sm'
                    color='primary'
                    onClick={() => {
                      const eligibleIds = (formData.settlement_details || [])
                        .filter(
                          (detail) =>
                            !isRowAlreadyHasValue(
                              detail,
                              copyMode.field,
                              copyMode.value
                            )
                        )
                        .map((detail) => detail.id);
                      setSelectedRowsForCopy(new Set(eligibleIds));
                    }}
                    isDisabled={(formData.settlement_details || [])
                      .filter(
                        (detail) =>
                          !isRowAlreadyHasValue(
                            detail,
                            copyMode.field,
                            copyMode.value
                          )
                      )
                      .every((detail) => selectedRowsForCopy.has(detail.id))}
                  >
                    Seleccionar todas las filas
                  </Button>
                  <Button
                    size='sm'
                    color='success'
                    onClick={handleConfirmCopyToRows}
                    isDisabled={selectedRowsForCopy.size === 0}
                  >
                    Confirmar
                  </Button>
                  <Button
                    size='sm'
                    color='danger'
                    variant='light'
                    onClick={handleCancelCopyMode}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          )}

          <ModalFooter className='flex shrink-0 items-center justify-between border-t border-divider px-6 py-4'>
            <Button
              color='danger'
              variant='flat'
              onPress={handleDeleteSettlement}
              isLoading={deleting}
              isDisabled={settlement?.is_blocked}
            >
              Eliminar
            </Button>
            <div className='flex items-center gap-2'>
              <Button variant='light' onPress={onClose}>
                Cerrar
              </Button>
              <Button
                color='primary'
                onPress={handleSubmit}
                isLoading={loading}
                isDisabled={
                  settlement?.is_blocked || !canSave || savingCostField
                }
              >
                Guardar cambios
              </Button>
            </div>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isSourceCostEditorOpen}
        onClose={() => setIsSourceCostEditorOpen(false)}
        size='4xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader className='flex flex-col gap-1'>
            <span>
              {canEditCurrentSourceCosts ? 'Editar' : 'Ver'} costos originales
            </span>
            <span className='text-sm font-normal text-default-500'>
              {canEditCurrentSourceCosts
                ? 'Edita, agrega, elimina o reclasifica las líneas del cliente. Los costos oficiales de esta liquidación se recalculan desde aquí.'
                : 'Estos costos provienen de un documento multi-despacho compartido con otras liquidaciones y no se editan aquí: para corregirlos, elimina las liquidaciones del documento y vuelve a subirlo. Sí puedes cambiar su categoría y su categoría exportadora.'}
            </span>
          </ModalHeader>
          <ModalBody>
            {exporterCostEditorFilter !== undefined && (
              <Chip
                className='self-start'
                variant='flat'
                onClose={() => setExporterCostEditorFilter(undefined)}
              >
                {t('Categoría exportadora')}:{' '}
                {exporterCostEditorFilter === null
                  ? t('Sin clasificar')
                  : receiverExporterCosts.categories.find(
                      (category) => category.id === exporterCostEditorFilter
                    )?.name}
              </Chip>
            )}
            <ReceiverCostClassificationTable
              costLines={sourceCostLines}
              localCurrency={formData.local_currency}
              exporterId={userProfile?.exporter_id}
              exporterReceiverId={formData.exporter_receiver_id}
              onCostLinesChange={handleSourceCostLinesChange}
              onResolvedLinesChange={handleSourceCostResolution}
              onMappingsReadyChange={setSourceCostMappingsReady}
              categoryFilter={sourceCostEditorCategory}
              exporterCategoryFilter={exporterCostEditorFilter}
              mappingOnly={!canEditCurrentSourceCosts}
            />
            {canEditCurrentSourceCosts && sourceCostLinesDirty && (
              <p className='text-xs text-default-500'>
                Los cambios quedarán guardados al presionar Guardar en la
                liquidación.
              </p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color='primary'
              onPress={() => setIsSourceCostEditorOpen(false)}
            >
              Listo
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Otros Ingresos Modal */}
      <Modal
        isOpen={isRevenueModalOpen}
        onClose={() => {
          setNewRevenue(null);
          onRevenueModalClose();
        }}
        size='3xl'
      >
        <ModalContent>
          <ModalHeader>Ingresos Adicionales</ModalHeader>
          <ModalBody>
            <div className='space-y-3'>
              {otherRevenues.map((rev) => (
                <div
                  key={rev.group_id}
                  className='flex items-center gap-2 p-2 bg-background rounded border border-green-100'
                >
                  <Chip size='sm' variant='flat' color='success'>
                    {REVENUE_TYPE_LABELS[rev.revenue_type] || rev.revenue_type}
                  </Chip>
                  <span className='flex-1 text-sm text-default-600'>
                    {rev.description || '—'}
                  </span>
                  <span className='font-medium text-sm'>
                    {rev.amount_local.toLocaleString()}{' '}
                    {formData.local_currency}
                  </span>
                  <span className='font-medium text-sm'>
                    {rev.exchange_rate > 0
                      ? (rev.amount_local / rev.exchange_rate).toLocaleString(
                          undefined,
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }
                        )
                      : '0'}{' '}
                    USD
                  </span>
                  <span className='text-xs text-default-400'>
                    TC{' '}
                    {rev.exchange_rate.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  <Chip size='sm' variant='flat'>
                    {rev.distribution === 'byKg' ? 'Por Kg' : 'Por Venta'}
                  </Chip>
                  <Button
                    isIconOnly
                    size='sm'
                    color='danger'
                    variant='light'
                    isDisabled={settlement?.is_blocked || savingRevenue}
                    onPress={() =>
                      rev.group_id && handleDeleteRevenue(rev.group_id)
                    }
                  >
                    <Icon icon='tabler:x' />
                  </Button>
                </div>
              ))}
              {otherRevenues.length > 0 && (
                <div className='flex justify-between items-center py-2 border-t border-green-300'>
                  <span className='font-semibold text-green-700 dark:text-green-300'>
                    Total Ingresos Adicionales:
                  </span>
                  <div className='flex items-center gap-3'>
                    <span className='font-bold text-green-700 dark:text-green-300'>
                      {totalOtherRevenue.toLocaleString()}{' '}
                      {formData.local_currency}
                    </span>
                    <span className='font-bold text-green-700 dark:text-green-300'>
                      {totalOtherRevenueUsd.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      USD
                    </span>
                    <span className='text-xs text-default-400'>
                      TC{' '}
                      {totalOtherRevenueUsd > 0
                        ? (
                            totalOtherRevenue / totalOtherRevenueUsd
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : '—'}
                    </span>
                  </div>
                </div>
              )}

              {/* New revenue inline form */}
              {newRevenue && (
                <div className='p-3 bg-green-50 dark:bg-green-950 rounded border border-green-200 dark:border-green-800 space-y-3'>
                  <p className='text-sm font-semibold text-green-800 dark:text-green-200'>
                    Nuevo Ingreso
                  </p>
                  {/* Row 1: Tipo, Descripción, Distribución */}
                  <div className='flex items-center gap-2'>
                    <Select
                      size='sm'
                      label='Tipo'
                      className='w-56 flex-shrink-0'
                      selectedKeys={[newRevenue.revenue_type]}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) return;
                        setNewRevenue((prev) =>
                          prev ? { ...prev, revenue_type: val } : prev
                        );
                      }}
                    >
                      {REVENUE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </Select>
                    <Input
                      size='sm'
                      label='Descripción'
                      className='flex-1'
                      value={newRevenue.description}
                      onChange={(e) =>
                        setNewRevenue((prev) =>
                          prev ? { ...prev, description: e.target.value } : prev
                        )
                      }
                    />
                    <Select
                      size='sm'
                      label='Distribución'
                      className='w-40 flex-shrink-0'
                      selectedKeys={[newRevenue.distribution]}
                      onChange={(e) => {
                        const val = e.target.value as 'byKg' | 'bySales';
                        if (!val) return;
                        setNewRevenue((prev) =>
                          prev ? { ...prev, distribution: val } : prev
                        );
                      }}
                    >
                      <SelectItem key='byKg' value='byKg'>
                        Por Kg
                      </SelectItem>
                      <SelectItem key='bySales' value='bySales'>
                        Por Venta
                      </SelectItem>
                    </Select>
                  </div>
                  {/* Row 2: Monto Local, Tipo de Cambio, Monto USD */}
                  <div className='flex items-center gap-2'>
                    <Input
                      size='sm'
                      label={`Monto ${formData.local_currency || 'Local'}`}
                      type='number'
                      className='flex-1'
                      value={newRevenue.amount_local.toString()}
                      endContent={
                        <span className='text-xs text-default-400'>
                          {formData.local_currency}
                        </span>
                      }
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        setNewRevenue((prev) => {
                          if (!prev) return prev;
                          const usd =
                            prev.exchange_rate > 0
                              ? val / prev.exchange_rate
                              : 0;
                          return {
                            ...prev,
                            amount_local: val,
                            _amount_usd: usd,
                          };
                        });
                      }}
                    />
                    <Input
                      size='sm'
                      label='Tipo de Cambio'
                      type='number'
                      className='w-36 flex-shrink-0'
                      value={newRevenue.exchange_rate.toString()}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        setNewRevenue((prev) => {
                          if (!prev) return prev;
                          const usd = val > 0 ? prev.amount_local / val : 0;
                          return {
                            ...prev,
                            exchange_rate: val,
                            _amount_usd: usd,
                          };
                        });
                      }}
                    />
                    <Input
                      size='sm'
                      label='Monto USD'
                      type='number'
                      className='flex-1'
                      value={
                        newRevenue.exchange_rate > 0
                          ? (
                              newRevenue.amount_local / newRevenue.exchange_rate
                            ).toFixed(2)
                          : '0'
                      }
                      endContent={
                        <span className='text-xs text-default-400'>USD</span>
                      }
                      onChange={(e) => {
                        const usd = parseFloat(e.target.value) || 0;
                        setNewRevenue((prev) => {
                          if (!prev) return prev;
                          const newRate =
                            usd > 0
                              ? prev.amount_local / usd
                              : prev.exchange_rate;
                          return { ...prev, exchange_rate: newRate };
                        });
                      }}
                    />
                  </div>
                  <div className='flex gap-2 justify-end'>
                    <Button
                      size='sm'
                      variant='light'
                      onPress={() => setNewRevenue(null)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size='sm'
                      color='success'
                      isLoading={savingRevenue}
                      isDisabled={newRevenue.amount_local === 0}
                      onPress={handleConfirmNewRevenue}
                    >
                      Confirmar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => {
                setNewRevenue(null);
                onRevenueModalClose();
              }}
            >
              Cerrar
            </Button>
            {!newRevenue && (
              <Button
                size='sm'
                color='primary'
                startContent={<Icon icon='tabler:plus' />}
                isDisabled={settlement?.is_blocked || savingRevenue}
                onPress={() =>
                  setNewRevenue({
                    revenue_type: 'compensation',
                    description: '',
                    amount_local: 0,
                    exchange_rate: formData.exchange_rate || 1,
                    distribution: 'bySales',
                  })
                }
              >
                Agregar Ingreso
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <FormatMappingModal
        isOpen={isOpen && isFormatMappingModalOpen}
        onClose={handleCloseFormatMapping}
        loading={formatMappingLoading}
        error={formatMappingError}
        onRetry={() => void handleOpenFormatMapping()}
        speciesId={settlement?.species_id}
        season={settlement?.season}
        exporterId={settlement?.exporter_id}
        exporterRef={settlement?.reference_number || ''}
      />

      {/* Delete Settlement Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalContent>
          <ModalHeader>
            {deleteBlockingClaims.length > 0
              ? 'No se puede eliminar'
              : 'Confirmar eliminación'}
          </ModalHeader>
          <ModalBody>
            {isCheckingDeleteClaims ? (
              <div className='flex items-center gap-2'>
                <Spinner size='sm' />
                <span>Verificando dependencias...</span>
              </div>
            ) : deleteBlockingClaims.length > 0 ? (
              <div className='flex flex-col gap-2'>
                <p>
                  No puedes borrar esta liquidación porque las compensaciones{' '}
                  <strong>
                    {deleteBlockingClaims.map((c) => `#${c.id}`).join(', ')}
                  </strong>{' '}
                  la consideran.
                </p>
                <p>Debes borrar primero esas compensaciones para proceder.</p>
              </div>
            ) : (
              <>
                <p>
                  ¿Está seguro que desea eliminar la liquidación{' '}
                  <strong>{settlement?.reference_number}</strong>?
                </p>
                <p className='text-danger'>Esta acción no se puede deshacer.</p>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={onDeleteModalClose}>
              {deleteBlockingClaims.length > 0 ? 'Cerrar' : 'Cancelar'}
            </Button>
            {deleteBlockingClaims.length === 0 && !isCheckingDeleteClaims && (
              <Button
                color='danger'
                onPress={handleConfirmDelete}
                isLoading={deleting}
              >
                Eliminar
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditSettlementDialog;
